/* DermPro theme — interactions */
(function () {
  'use strict';

  /* ---------- Scroll lock ----------
     The page must not move at all while a drawer, the mobile nav or the search
     overlay is open — only that panel's own scroller may respond.

     Two things are needed, and both matter:
     - html.scroll-locked (see base.css) locks the ROOT. Because html carries
       overflow-x:clip, html is the scroll container, so locking body alone had no
       effect on any browser.
     - body is pinned with position:fixed at a negative top offset, which is the
       only thing iOS Safari honours. The offset is restored on close so the page
       doesn't jump back to the top.

     Locks are keyed, so overlapping overlays can't unlock each other and a
     repeated close call can't unlock early. Padding compensates for the scrollbar
     that disappears while locked, which would otherwise shift the layout. */
  var scrollLocks = {};
  var scrollLockY = 0;
  function lockScroll(key) {
    if (scrollLocks[key]) return;
    var wasEmpty = Object.keys(scrollLocks).length === 0;
    scrollLocks[key] = true;
    if (!wasEmpty) return;
    scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var barWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.classList.add('scroll-locked');
    document.body.style.top = -scrollLockY + 'px';
    /* Locking sets overflow:hidden on the root, which removes the vertical
       scrollbar and widens the viewport by its width. Padding the body keeps
       in-flow content still, but a stuck header is position:fixed against the
       VIEWPORT, so body padding does nothing for it — it widens instantly and its
       centred contents slide right by half the scrollbar width. Publishing the
       width lets base.css compensate the fixed header too (see .scroll-locked
       .site-header.is-stuck). */
    if (barWidth > 0) {
      document.body.style.paddingRight = barWidth + 'px';
      document.documentElement.style.setProperty('--scrollbar-width', barWidth + 'px');
    }
  }
  function unlockScroll(key) {
    if (!scrollLocks[key]) return;
    delete scrollLocks[key];
    if (Object.keys(scrollLocks).length > 0) return;
    var root = document.documentElement;
    /* Pin scroll-behavior for the duration of the restore.
       html carries scroll-behavior:smooth, and .scroll-locked's
       scroll-behavior:auto is what was meant to keep this restore instant. But
       the class has to come off before the scroll can happen at all (it is what
       pins the body), so by the time scrollTo runs, smooth is back in effect and
       the browser ANIMATES back to the saved offset. Closing the drawer part-way
       down a page therefore looked like the page jumping or gliding. At the top
       of the page there is nothing to animate, which is why it only showed up
       mid-page. Setting the property inline overrides the stylesheet regardless
       of class state; cleared immediately after, since scrollTo with auto
       completes synchronously. */
    root.style.scrollBehavior = 'auto';
    root.classList.remove('scroll-locked');
    document.body.style.top = '';
    document.body.style.paddingRight = '';
    root.style.removeProperty('--scrollbar-width');
    window.scrollTo(0, scrollLockY);
    root.style.scrollBehavior = '';
  }

  /* ---------- Menu aim (v267) ----------
     Raymond's case: Products open on Cultura (a 3-column mega panel centred under it), the
     pointer heading diagonally down-left for the Brand column. On the way it leaves the menu
     item sideways before it reaches the panel, and the panel closed 120ms later — or, from
     another item, the pointer crossed a neighbouring menu item and opened that one instead.
     The fix is the one Amazon's menus made well known ("menu aim"): while the pointer is
     moving towards the open panel — inside the triangle from where it was a moment ago to the
     panel's near edge, plus 20px either side — leaving the item does not close the panel and
     crossing another item does not open it. Each move that still heads there gives it another
     300ms; once the pointer turns away or rests outside, the panel closes and the item now
     under the pointer (if any) opens, exactly as before. Reaching the panel, or coming back
     to the item, keeps it open.
     Used by the mega / simple dropdowns and by "Panel with fly-outs" (top panels and their
     fly-outs alike). Mouse only: touch and keyboard never start it. Leaving the PANEL itself
     is never "aiming" (the pointer is not above / beside it), so the v-old rule still holds:
     darting out of the panel and back in does not keep a menu open. */
  var aimPts = [];
  /* Recent pointer positions, timed. mouseleave fires BEFORE the mousemove that caused it,
     so the leave handlers note their own position first (aimNote). */
  function aimNote(e) {
    var t = Date.now(), last = aimPts[aimPts.length - 1];
    if (last && last.x === e.clientX && last.y === e.clientY) return;
    aimPts.push({ x: e.clientX, y: e.clientY, t: t });
    if (aimPts.length > 6) aimPts.shift();
  }
  document.addEventListener('mousemove', aimNote, { passive: true });
  function aimSide(p, a, b) { return (p.x - b.x) * (a.y - b.y) - (a.x - b.x) * (p.y - b.y); }
  function aimInTri(p, a, b, c) {
    var d1 = aimSide(p, a, b), d2 = aimSide(p, b, c), d3 = aimSide(p, c, a);
    return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
  }
  /* Heading into `r`? The apex is the most recent earlier position at least 6px away and no
     older than 150ms: the direction the pointer is actually moving in, not a pixel of jitter
     and not somewhere it was long ago. From inside r (leaving it) is never heading in. */
  function aimToward(r, anchor) {
    if (aimPts.length < 2 || !r || !r.width) return false;
    var now = aimPts[aimPts.length - 1], from = anchor || null, s = 20, a, b;
    for (var k = aimPts.length - 2; !from && k >= 0; k--) {
      var q = aimPts[k];
      if (now.t - q.t > 150) break;
      if (Math.abs(q.x - now.x) + Math.abs(q.y - now.y) >= 6) from = q;
    }
    if (!from || (now.x === from.x && now.y === from.y)) return false;
    if (from.y <= r.top) { a = { x: r.left - s, y: r.top }; b = { x: r.right + s, y: r.top }; }
    else if (from.y >= r.bottom) { a = { x: r.left - s, y: r.bottom }; b = { x: r.right + s, y: r.bottom }; }
    else if (from.x <= r.left) { a = { x: r.left, y: r.top - s }; b = { x: r.left, y: r.bottom + s }; }
    else if (from.x >= r.right) { a = { x: r.right, y: r.top - s }; b = { x: r.right, y: r.bottom + s }; }
    else return false;
    return aimInTri(now, from, a, b) ? from : false;
  }
  var navAim = {
    item: null, wait: null, timer: 0, move: null, fail: null,
    /* Keep `item`'s panel open while the pointer heads for it; `fail` closes it. */
    hold: function (item, panel, fail, apex) {
      if (this.item && this.item !== item) this.release(false);
      var self = this;
      this.item = item; this.fail = fail; this.wait = null;
      var again = function () { clearTimeout(self.timer); self.timer = setTimeout(function () { self.release(false); }, 300); };
      /* While holding, every move is judged against the same funnel: from where the pointer
         was just before it left the item to the panel's near edge. A sideways stretch or a
         short pause inside that funnel is fine; wandering out of it closes the panel, and so
         does dawdling — the hold never lasts more than 1.2s in all. */
      var anchor = apex, until = Date.now() + 1200;
      this.move = function () {
        var r = panel.getBoundingClientRect(), p = aimPts[aimPts.length - 1];
        /* Got there (the browser normally says so first, by re-entering the item). */
        if (p && p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom) { self.release(true); return; }
        if (Date.now() < until && aimToward(r, anchor)) again(); else self.release(false);
      };
      document.addEventListener('mousemove', this.move, { passive: true });
      again();
    },
    /* reached: the pointer got to the panel (or back to the item) — keep it open. */
    release: function (reached) {
      if (!this.item) return;
      clearTimeout(this.timer);
      document.removeEventListener('mousemove', this.move);
      var fail = this.fail, wait = this.wait;
      this.item = null; this.fail = null; this.wait = null; this.move = null;
      if (reached) return;
      if (fail) fail();
      if (wait && wait.el.matches(':hover')) wait.open();
    },
    /* Another item asked to open while one is being aimed at: hold it until the aim ends. */
    defer: function (el, open) {
      if (!this.item || this.item === el || el.contains(this.item) || this.item.contains(el)) return false;
      this.wait = { el: el, open: open };
      return true;
    }
  };
  var aimMouse = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ---------- Desktop dropdown nav ---------- */
  document.querySelectorAll('.header-nav__item').forEach(function (item) {
    if (!item.querySelector('.header-dropdown')) return;
    var link = item.querySelector('a');
    var panel = item.querySelector('.header-dropdown');
    var closeTimer;
    /* Opening is bound to the TRIGGER LINK, not the whole item.
       The panel is a DOM child of the item but sits 10px below it visually, so
       crossing that gap fires mouseleave on the item. With the open handler on the
       item, re-entering the panel fired mouseenter and cancelled the pending close —
       so leaving the panel and darting back in kept it open indefinitely, even though
       the pointer had been outside. Binding open to the link means re-entering the
       panel alone can never reopen it; only pointing at the menu item can.

       The transit from link to panel is covered by an invisible bridge over that 10px
       gap (.header-dropdown::before in base.css), which keeps the pointer inside the
       item's subtree so no mouseleave fires on the way down. The timer below is only
       a tolerance for sub-pixel jitter at the boundary, and deliberately is NOT
       cancelled by entering the panel.
       v267 (menu aim, above): leaving the item while heading for its open panel holds it
       open instead, and the item re-entered (the panel is inside it) ends the hold. */
    var openNow = function () { clearTimeout(closeTimer); item.classList.add('is-open'); };
    link.addEventListener('mouseenter', function () {
      if (navAim.defer(link, openNow)) return;
      openNow();
    });
    item.addEventListener('mouseenter', function () { if (navAim.item === item) navAim.release(true); });
    item.addEventListener('mouseleave', function (e) {
      aimNote(e);
      clearTimeout(closeTimer);
      var apex = aimMouse.matches && window.innerWidth > 990 && item.classList.contains('is-open') && aimToward(panel.getBoundingClientRect());
      if (apex) {
        navAim.hold(item, panel, function () { item.classList.remove('is-open'); }, apex);
        return;
      }
      closeTimer = setTimeout(function () { item.classList.remove('is-open'); }, 120);
    });
    link.addEventListener('click', function (e) {
      if (window.innerWidth <= 990) return;
      if (link.getAttribute('href') === '#' || link.dataset.toggle) { e.preventDefault(); item.classList.toggle('is-open'); }
    });
  });

  /* ---------- Cascading dropdowns (Header → Dropdown style: Panel with fly-outs) ----------
     Markup: snippets/header-nav.liquid (cascade). Resting styles: sections/header.liquid.
     Timings measured on the reference store and reproduced here, not guessed:
       hover in  -> opens after 100ms;  hover out -> closes after 150ms
       panel becomes clickable 100ms after it starts opening
       open : panel opacity 0 -> 1 over 300ms ease-in-out; its contents slide from
              -30px (top panel) / +20px (fly-out) to 0 over 600ms cubic-bezier(.3,1,.3,1)
       close: opacity to 0 over 200ms ease-in-out (hidden at the end); contents slide
              back over 600ms, from wherever they are
     Reduced motion: every duration is 0. Desktop only: phones use the drawer.
     Fly-outs open to the left when the panel's left edge plus twice its width would run
     past the window. Run with the Web Animations API so one function drives both levels.

     Header → "Panel with fly-outs: animation" = Rise (v233; class hdc-rise on the top
     panel) replaces the open/close animation above. Hover timings stay as above. Measured
     on the reference store, frame by frame:
       open : the panel appears at once; its background grows from the top over .25s ease
              (CSS, class hdc-shown); the list rises 16px -> 0 and fades 0 -> 1 over .25s ease.
              First menu opened since the pointer came into the menu bar: the list shows in
              place for 100ms, then drops to 16px down, invisible, and rises - the "jump".
              Moving across from another open menu, and every fly-out: the list stays hidden
              for 250ms, then rises.
       close: instant.
       Opening a menu closes any other open one at once; a fly-out closes its open siblings. */
  (function () {
    var tops = document.querySelectorAll('.header-nav__item--cascade');
    if (!tops.length) return;
    var EASE_SLIDE = 'cubic-bezier(.3,1,.3,1)';
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    var desktop = function () { return window.innerWidth > 990; };
    var header = document.querySelector('.site-header');

    function run(el, frames, ms, easing) {
      if (el._hdcAnim) el._hdcAnim.cancel();
      el._hdcAnim = el.animate(frames, { duration: reduced.matches ? 0 : ms, easing: easing, fill: 'forwards' });
      return el._hdcAnim;
    }
    function isRise(panel) { return !!panel.closest('.hdc-rise'); }
    function listOf(panel) { return panel.firstElementChild.querySelector(':scope > .hdc-nav'); }
    function riseIn(panel, fresh) {
      var list = listOf(panel);
      panel.classList.add('hdc-shown');
      if (list._hdcAnim) list._hdcAnim.cancel();
      if (reduced.matches) return;
      list._hdcAnim = list.animate(
        [{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }],
        // fresh: 'forwards' leaves the list showing normally during the 100ms delay (the jump);
        // otherwise 'both' holds the first keyframe (hidden, 16px down) through the delay.
        { duration: 250, easing: 'ease', delay: fresh ? 100 : 250, fill: fresh ? 'forwards' : 'both' });
    }
    function riseOut(panel) {
      var list = listOf(panel);
      panel.classList.remove('hdc-shown');
      if (list._hdcAnim) { list._hdcAnim.cancel(); list._hdcAnim = null; }
    }
    function show(panel, fresh) {
      clearTimeout(panel._hdcReady);
      panel._hdcReady = setTimeout(function () { panel.classList.add('is-open'); }, 100);
      if (isRise(panel)) { riseIn(panel, fresh); return; }
      var box = panel.firstElementChild;
      var from = panel.classList.contains('hdc-panel--top') ? '-30px' : '20px';
      run(panel, [{ opacity: 0, visibility: 'visible' }, { opacity: 1, visibility: 'visible' }], 300, 'ease-in-out');
      run(box, [{ transform: 'translateY(' + from + ')' }, { transform: 'translateY(0)' }], 600, EASE_SLIDE);
    }
    function hide(panel) {
      clearTimeout(panel._hdcReady);
      panel.classList.remove('is-open');
      if (isRise(panel)) {
        riseOut(panel);
      } else {
        var box = panel.firstElementChild;
        var to = panel.classList.contains('hdc-panel--top') ? '-30px' : '20px';
        // Close from the current position, so a half-open panel reverses smoothly.
        var op = getComputedStyle(panel).opacity, tf = getComputedStyle(box).transform;
        run(panel, [{ opacity: op, visibility: 'visible' }, { opacity: 0, visibility: 'hidden' }], 200, 'ease-in-out');
        run(box, [{ transform: tf === 'none' ? 'translateY(0)' : tf }, { transform: 'translateY(' + to + ')' }], 600, EASE_SLIDE);
      }
      panel.querySelectorAll('.hdc-panel--fly').forEach(function (f) { if (f._hdcOpen) setOpen(f.parentElement, f, false); });
    }
    function setOpen(trigger, panel, open) {
      if (!!panel._hdcOpen === open) return;
      var fresh = false;
      if (open && isRise(panel)) {
        if (panel.classList.contains('hdc-panel--top')) {
          var nav = trigger.closest('.header-nav');
          fresh = !(nav && nav._hdcWarm);
          tops.forEach(function (item) {
            var other = item.querySelector(':scope > .hdc-panel--top');
            if (other && other !== panel && other._hdcOpen) { fresh = false; setOpen(item, other, false); }
          });
          if (nav) nav._hdcWarm = true;
        } else {
          Array.prototype.forEach.call(trigger.parentElement.children, function (li) {
            var f = li !== trigger && li.querySelector(':scope > .hdc-panel--fly');
            if (f && f._hdcOpen) setOpen(li, f, false);
          });
        }
      }
      panel._hdcOpen = open;
      var link = trigger.querySelector(':scope > a');
      if (link && link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel.classList.contains('hdc-panel--top')) {
        trigger.classList.toggle('is-open', open);           // drives the page dim
        if (open) {
          if (header) trigger.style.setProperty('--hdc-gap', Math.max(0, header.getBoundingClientRect().bottom - trigger.getBoundingClientRect().bottom) + 'px');
          var r = panel.getBoundingClientRect();
          panel.classList.toggle('is-reversed', r.left + r.width * 2 > window.innerWidth);
        }
      }
      open ? show(panel, fresh) : hide(panel);
    }
    function wire(trigger, panel) {
      var openT, closeT;
      /* v267 menu aim (see "Menu aim" above): heading for this panel holds it open; while
         another panel is being aimed at, this one waits instead of opening. */
      var openSoon = function () {
        clearTimeout(closeT);
        if (!panel._hdcOpen) { clearTimeout(openT); openT = setTimeout(function () { setOpen(trigger, panel, true); }, 100); }
      };
      trigger.addEventListener('mouseenter', function () {
        if (!desktop() || !finePointer.matches) return;
        if (navAim.item === trigger) { navAim.release(true); clearTimeout(closeT); return; }
        if (navAim.defer(trigger, openSoon)) return;
        openSoon();
      });
      trigger.addEventListener('mouseleave', function (e) {
        if (!desktop() || !finePointer.matches) return;
        aimNote(e);
        clearTimeout(openT);
        if (!panel._hdcOpen) return;
        clearTimeout(closeT);
        var apex = aimToward(panel.getBoundingClientRect());
        if (apex) {
          navAim.hold(trigger, panel, function () { setOpen(trigger, panel, false); }, apex);
          return;
        }
        closeT = setTimeout(function () { setOpen(trigger, panel, false); }, 150);
      });
      // Keyboard: focus opens at once; focus leaving the item closes it.
      trigger.addEventListener('focusin', function () {
        if (!desktop()) return;
        // Escape hands focus back to the menu item; that must not reopen what it just closed.
        if (trigger._hdcNoReopen) { trigger._hdcNoReopen = false; return; }
        clearTimeout(closeT); setOpen(trigger, panel, true);
      });
      trigger.addEventListener('focusout', function (e) {
        if (!desktop() || trigger.contains(e.relatedTarget)) return;
        setOpen(trigger, panel, false);
      });
      // Touch screens wide enough for the desktop menu: first tap opens, second follows the link.
      var link = trigger.querySelector(':scope > a');
      if (link) link.addEventListener('click', function (e) {
        if (!desktop()) return;
        var href = link.getAttribute('href');
        if (href === '#' || href === '' || (!finePointer.matches && !panel._hdcOpen)) {
          e.preventDefault();
          setOpen(trigger, panel, !panel._hdcOpen);
        }
      });
    }
    tops.forEach(function (item) {
      var top = item.querySelector(':scope > .hdc-panel--top');
      if (!top) return;
      wire(item, top);
      top.querySelectorAll('.hdc-item--parent').forEach(function (li) {
        var fly = li.querySelector(':scope > .hdc-panel--fly');
        if (fly) wire(li, fly);
      });
    });
    function closeAll() {
      tops.forEach(function (item) {
        var top = item.querySelector(':scope > .hdc-panel--top');
        if (top && top._hdcOpen) setOpen(item, top, false);
      });
      navs.forEach(function (nav) { nav._hdcWarm = false; });
    }
    // Rise: the menu bar is "warm" from the first menu opened until the pointer or the
    // focus leaves the bar. Only a menu opened while it is cold gets the jump.
    var navs = [];
    tops.forEach(function (item) {
      var nav = item.closest('.header-nav');
      if (nav && navs.indexOf(nav) < 0) navs.push(nav);
    });
    navs.forEach(function (nav) {
      nav.addEventListener('mouseleave', function () { nav._hdcWarm = false; });
      nav.addEventListener('focusout', function (e) { if (!nav.contains(e.relatedTarget)) nav._hdcWarm = false; });
    });
    document.addEventListener('click', function (e) { if (!e.target.closest('.header-nav__item--cascade')) closeAll(); });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.activeElement && document.activeElement.closest('.header-nav__item--cascade');
      closeAll();
      if (open) { var a = open.querySelector(':scope > a'); if (a && document.activeElement !== a) { open._hdcNoReopen = true; a.focus(); } }
    });
    window.addEventListener('resize', function () { if (!desktop()) closeAll(); });
    var navDimEl = document.querySelector('[data-nav-dim]');
    if (navDimEl) navDimEl.addEventListener('click', closeAll);
  })();

  /* ---------- Page dim behind an open dropdown ----------
     Theme settings → Navigation → "Dim the page while a menu is open". The overlay
     (.nav-dim, sections/header.liquid) only exists when the setting is on. It follows the
     dropdowns' own is-open class rather than hooking each open/close path above, so any
     way a menu opens or closes — hover, click, the close timer — keeps the dim in step.
     Moving straight from one menu item to the next keeps it on: the next one opens
     before the previous one's 120ms close timer runs, so it never flickers. */
  var navDim = document.querySelector('[data-nav-dim]');
  if (navDim && window.MutationObserver) {
    var dimItems = Array.prototype.filter.call(document.querySelectorAll('.header-nav__item'), function (item) {
      return !!item.querySelector('.header-dropdown') || item.classList.contains('header-nav__item--cascade');
    });
    var syncNavDim = function () {
      var open = dimItems.some(function (item) { return item.classList.contains('is-open'); });
      document.documentElement.classList.toggle('nav-dimmed', open && window.innerWidth > 990);
    };
    var dimObserver = new MutationObserver(syncNavDim);
    dimItems.forEach(function (item) { dimObserver.observe(item, { attributes: true, attributeFilter: ['class'] }); });
    /* Clicking the dimmed page closes the menu (matters for a menu opened by click). */
    navDim.addEventListener('click', function () {
      dimItems.forEach(function (item) { item.classList.remove('is-open'); });
    });
    window.addEventListener('resize', syncNavDim);
  }

  /* ---------- Mobile nav ---------- */
  var mobileNav = document.getElementById('MobileNav');
  /* body.mobile-nav-open drives the 40px page shift, mirroring the cart drawer */
  function openMobile() {
    if (mobileNav) mobileNav.classList.add('is-open');
    document.body.classList.add('mobile-nav-open');
    lockScroll('nav');
  }
  function closeMobile() {
    if (mobileNav) mobileNav.classList.remove('is-open');
    document.body.classList.remove('mobile-nav-open');
    unlockScroll('nav');
  }
  document.querySelectorAll('[data-mobile-open]').forEach(function (b) { b.addEventListener('click', openMobile); });
  document.querySelectorAll('[data-mobile-close]').forEach(function (b) { b.addEventListener('click', closeMobile); });

  /* ---------- Cart drawer ---------- */
  var drawer = document.getElementById('CartDrawer');
  var cartOpenedAt = 0;
  function openCart() {
    if (drawer) drawer.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    cartOpenedAt = Date.now();   /* used to keep the recommendations' stagger */
    lockScroll('cart');
  }
  function closeCart() {
    if (drawer) drawer.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    unlockScroll('cart');
  }
  document.querySelectorAll('[data-cart-open]').forEach(function (b) {
    b.addEventListener('click', function (e) { e.preventDefault(); openCart(); refreshCart(false); });
  });
  document.querySelectorAll('[data-cart-close]').forEach(function (b) { b.addEventListener('click', closeCart); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') { closeCart(); closeMobile(); hideCartToast(); closeSearch(); } });

  /* ---------- Glass cart drawer: "You may also like" card (v268) ----------
     With a Liquid glass header the cart panel is frosted with its own backdrop filter
     (sections/header.liquid). The desktop "You may also like" card hangs off the left of
     the panel but lives inside it, and in Chrome nothing inside the sliding (transformed)
     panel can blur the page behind it — measured on Cultura: a filter on the card or on the
     panel's ::before showed the page sharp. So on computers (over 1180px, where the card is
     a separate card) it is moved next to the panel, into .cart-drawer__wrap, which does not
     move; its CSS places it the same way from there (right:100% of the same box). On phones
     the strip belongs inside the scrolling list, so it goes back there. Only with a glass
     header; every other store keeps the markup exactly as it was. */
  (function () {
    if (!document.querySelector('.site-header--glass') && !document.documentElement.classList.contains('ui-glass')) return;
    var reco = document.querySelector('.cart-drawer [data-drawer-reco]');
    var wrap = document.querySelector('.cart-drawer__wrap');
    if (!reco || !wrap || !window.matchMedia) return;
    var home = reco.parentNode, after = reco.nextSibling;
    var wide = window.matchMedia('(min-width: 1181px)');
    function place() {
      if (wide.matches) { if (reco.parentNode !== wrap) wrap.appendChild(reco); }
      else if (reco.parentNode !== home) home.insertBefore(reco, after && after.parentNode === home ? after : null);
      document.documentElement.classList.toggle('reco-outside', wide.matches);
    }
    place();
    if (wide.addEventListener) wide.addEventListener('change', place); else if (wide.addListener) wide.addListener(place);
  })();

  /* ---------- Search overlay ---------- */
  var searchModal = document.getElementById('SearchModal');
  var searchInput = searchModal ? searchModal.querySelector('[data-search-input]') : null;
  function openSearch() {
    if (!searchModal) return;
    searchModal.classList.add('is-open');
    lockScroll('search');
    setTimeout(function () { if (searchInput) searchInput.focus(); }, 250);
  }
  function closeSearch() { if (searchModal) { searchModal.classList.remove('is-open'); unlockScroll('search'); } }
  document.querySelectorAll('[data-search-open]').forEach(function (b) {
    b.addEventListener('click', function (e) { e.preventDefault(); openSearch(); });
  });
  if (searchModal) {
    searchModal.querySelectorAll('[data-search-close]').forEach(function (b) { b.addEventListener('click', closeSearch); });
  }

  /* ---------- Search panel (v268) ----------
     snippets/search-panel (Header → Search style: Spotlight / Drop-down / Full screen). The
     open / close / Escape above stay as they are; this adds, while the panel is open:
       - the field's hint typing out the store's suggestions (Header → Search suggestions)
         one by one, until the shopper types (not with reduced motion);
       - "Try" chips, and the shopper's last five searches (kept in this browser only;
         "Clear" forgets them);
       - live results from Shopify's predictive search, 160ms after the last key: suggested
         searches, products (picture, maker, price), collections, pages; the typed letters
         marked; a "View all results" button to the search page. A newer answer always
         wins over an older one still arriving;
       - ↓ / ↑ move through the results and chips, any other key goes back to the field.
     Drop-down opens just under the header wherever it is on the screen (--sp-top). */
  (function () {
    var panel = document.querySelector('[data-search-panel]');
    if (!panel) return;
    var input = panel.querySelector('[data-search-input]');
    var form = panel.querySelector('[data-search-form]');
    var results = panel.querySelector('[data-search-results]');
    var start = panel.querySelector('[data-search-start]');
    var recentBox = panel.querySelector('[data-search-recent]');
    var recentList = panel.querySelector('[data-search-recent-list]');
    if (!input || !results || !start) return;
    var base = panel.getAttribute('data-predictive-url') || '/search/suggest';
    var searchUrl = panel.getAttribute('data-search-url') || '/search';
    var still = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var terms = (panel.getAttribute('data-search-suggest') || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
    var HINT = input.getAttribute('placeholder') || 'Search products…';
    var KEY = 'ix-recent-searches';
    var ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
    var CLOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/></svg>';
    var esc = escapeHtml;

    function recent() { try { var r = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(r) ? r.slice(0, 5) : []; } catch (e) { return []; } }
    function remember(q) {
      q = String(q || '').trim();
      if (!q) return;
      try {
        var r = recent().filter(function (x) { return String(x).toLowerCase() !== q.toLowerCase(); });
        r.unshift(q);
        localStorage.setItem(KEY, JSON.stringify(r.slice(0, 5)));
      } catch (e) {}
    }
    function drawRecent() {
      if (!recentBox || !recentList) return;
      var r = recent();
      recentBox.hidden = !r.length;
      recentList.innerHTML = r.map(function (q, i) {
        return '<button type="button" class="sp-chip" data-search-term="' + esc(q) + '" style="--i:' + i + '">' + CLOCK + esc(q) + '</button>';
      }).join('');
    }

    /* the typing hint */
    var tw = { t: 0, i: 0, j: 0, dir: 1 };
    function typeStop() { clearTimeout(tw.t); input.setAttribute('placeholder', HINT); }
    function typeTick() {
      if (!panel.classList.contains('is-open') || input.value) { input.setAttribute('placeholder', HINT); return; }
      var word = terms[tw.i % terms.length];
      tw.j += tw.dir;
      input.setAttribute('placeholder', 'Search for ' + word.slice(0, tw.j) + (tw.dir > 0 ? '|' : ''));
      var wait = tw.dir > 0 ? 75 : 35;
      if (tw.dir > 0 && tw.j >= word.length) { tw.dir = -1; wait = 1500; input.setAttribute('placeholder', 'Search for ' + word); }
      else if (tw.dir < 0 && tw.j <= 0) { tw.dir = 1; tw.i++; wait = 350; }
      tw.t = setTimeout(typeTick, wait);
    }
    function typeStart() {
      typeStop();
      if (still || !terms.length) return;
      tw.j = 0; tw.dir = 1;
      tw.t = setTimeout(typeTick, 600);
    }

    /* results */
    function mark(text, q) {
      text = String(text || '');
      var k = text.toLowerCase().indexOf(String(q).toLowerCase());
      if (!q || k < 0) return esc(text);
      return esc(text.slice(0, k)) + '<mark>' + esc(text.slice(k, k + q.length)) + '</mark>' + esc(text.slice(k + q.length));
    }
    function price(p) { var n = parseFloat(p); return isFinite(n) ? formatMoney(Math.round(n * 100)) : ''; }
    function img(u) { if (!u) return ''; u = String(u); return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=200'; }
    function render(q, R) {
      var html = '', n = 0;
      var qs = (R.queries || []).filter(function (x) { return x && x.text && x.text.toLowerCase() !== q.toLowerCase(); }).slice(0, 4);
      var ps = R.products || [], cs = R.collections || [], pg = R.pages || [];
      if (qs.length) {
        html += '<div class="sr-sec"><p class="sp-label">Suggestions</p><div class="sp-chips">' + qs.map(function (x, i) {
          return '<button type="button" class="sp-chip" data-search-term="' + esc(x.text) + '" style="--i:' + i + '">' + mark(x.text, q) + '</button>';
        }).join('') + '</div></div>';
      }
      if (ps.length) {
        html += '<div class="sr-sec sr-sec--products"><p class="sp-label">Products</p><ul class="sr-list">' + ps.map(function (p) {
          return '<li><a class="sr-item" href="' + esc(p.url) + '" style="--i:' + (n++) + '"><span class="sr-item__img">' +
            (p.image ? '<img src="' + esc(img(p.image)) + '" alt="" loading="lazy">' : '') + '</span><span class="sr-item__txt"><span class="sr-item__title">' +
            mark(p.title, q) + '</span>' + (p.vendor ? '<span class="sr-item__meta">' + esc(p.vendor) + '</span>' : '') + '</span>' +
            '<span class="sr-item__price">' + price(p.price) + '</span><span class="sr-item__go">' + ARROW + '</span></a></li>';
        }).join('') + '</ul></div>';
      }
      [['Collections', cs], ['Pages', pg]].forEach(function (g) {
        if (!g[1].length) return;
        html += '<div class="sr-sec"><p class="sp-label">' + g[0] + '</p><ul class="sr-list">' + g[1].map(function (c) {
          return '<li><a class="sr-item" href="' + esc(c.url) + '" style="--i:' + (n++) + '"><span class="sr-item__txt"><span class="sr-item__title">' +
            mark(c.title, q) + '</span></span><span class="sr-item__go">' + ARROW + '</span></a></li>';
        }).join('') + '</ul></div>';
      });
      if (!ps.length && !cs.length && !pg.length && !qs.length) html += '<p class="sr-empty">No matches for “' + esc(q) + '” yet. Try another word.</p>';
      html += '<a class="sr-all" href="' + esc(searchUrl) + '?q=' + encodeURIComponent(q) + '" data-sr-all><span>View all results for “' + esc(q) + '”</span>' + ARROW + '</a>';
      results.innerHTML = html;
      results.hidden = false;
      start.hidden = true;
    }
    var timer = 0, seq = 0, ctl = null;
    function query(q) {
      clearTimeout(timer);
      q = String(q || '').trim();
      if (!q) { seq++; if (ctl) ctl.abort(); panel.classList.remove('is-loading'); results.hidden = true; results.innerHTML = ''; start.hidden = false; drawRecent(); return; }
      timer = setTimeout(function () {
        var my = ++seq;
        if (ctl) ctl.abort();
        ctl = window.AbortController ? new AbortController() : null;
        panel.classList.add('is-loading');
        fetch(base + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product,collection,page,query&resources[limit]=6&resources[options][unavailable_products]=last',
          ctl ? { signal: ctl.signal } : {})
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
          .then(function (j) { if (my !== seq) return; panel.classList.remove('is-loading'); render(q, (j.resources && j.resources.results) || {}); })
          .catch(function (e) { if (my !== seq || (e && e.name === 'AbortError')) return; panel.classList.remove('is-loading'); render(q, {}); });
      }, 160);
    }

    input.addEventListener('input', function () {
      if (input.value) typeStop(); else typeStart();
      query(input.value);
    });
    panel.addEventListener('click', function (e) {
      var t = e.target.closest('[data-search-term]');
      if (t) { e.preventDefault(); input.value = t.getAttribute('data-search-term'); typeStop(); query(input.value); input.focus(); return; }
      if (e.target.closest('[data-search-clear]')) { try { localStorage.removeItem(KEY); } catch (x) {} drawRecent(); input.focus(); return; }
      if (e.target.closest('.sr-item, [data-sr-all]')) remember(input.value);
    });
    if (form) form.addEventListener('submit', function () { remember(input.value); });
    /* ↓ / ↑ through the results (or the chips before typing); any other key back to the field */
    panel.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
        if (e.key.length === 1 && document.activeElement !== input && !e.metaKey && !e.ctrlKey) input.focus();
        return;
      }
      var scope = results.hidden ? start : results;
      var items = Array.prototype.slice.call(scope.querySelectorAll('.sr-item, [data-sr-all], .sp-chip'));
      if (!items.length) return;
      e.preventDefault();
      var k = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') k = k < 0 ? 0 : Math.min(items.length - 1, k + 1);
      else { if (k <= 0) { input.focus(); return; } k--; }
      items[k].focus();
    });
    new MutationObserver(function () {
      if (panel.classList.contains('is-open')) {
        var hdr = document.querySelector('.site-header .header-main') || document.querySelector('.site-header');
        if (hdr) panel.style.setProperty('--sp-top', Math.max(0, Math.round(hdr.getBoundingClientRect().bottom)) + 'px');
        drawRecent();
        if (!input.value) typeStart();
      } else typeStop();
    }).observe(panel, { attributes: true, attributeFilter: ['class'] });
  })();

  /* ---------- "Added to cart" toast (shown on add instead of opening the drawer) ---------- */
  var cartToast = document.getElementById('CartToast');
  var cartToastTimer = null;
  function showCartToast(item) {
    if (!cartToast || !item) return;
    var img = cartToast.querySelector('.cart-toast__img');
    if (img) img.src = item.image ? item.image.replace(/(\.[^.]+)$/, '_140x$1') : '';
    var t = cartToast.querySelector('[data-toast-title]');
    if (t) t.textContent = item.product_title || item.title || '';
    var meta = [];
    if (item.variant_title && item.variant_title !== 'Default Title') meta.push(item.variant_title);
    meta.push(formatMoney(item.final_price));
    meta.push('Qty ' + item.quantity);
    var m = cartToast.querySelector('[data-toast-meta]');
    if (m) m.textContent = meta.join(' · ');
    cartToast.classList.add('is-open');
    clearTimeout(cartToastTimer);
    cartToastTimer = setTimeout(hideCartToast, 5000);
  }
  function hideCartToast() { if (cartToast) cartToast.classList.remove('is-open'); clearTimeout(cartToastTimer); }
  if (cartToast) {
    cartToast.querySelectorAll('[data-toast-close]').forEach(function (b) { b.addEventListener('click', hideCartToast); });
    /* "Open cart" opens the drawer instead of navigating to the cart page */
    cartToast.querySelectorAll('[data-toast-open-cart]').forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        hideCartToast();
        openCart();
        refreshCart(false);
      });
    });
  }

  /* ---------- After adding to cart (v293) ----------
     Theme settings → Cart drawer → "After adding to cart" (data-add-effect on the drawer):
       toast     the pop-up card over a dimmed page (as before; the default)
       fly       the product's picture arcs from where it was clicked into the header's cart
                 icon, which catches it; then the count ticks up (no pop-up, no dim)
       fly_note  the same, then a slim "Added to cart · View cart" bar under the cart icon
                 for a few seconds (no dim)
       drawer    the cart drawer opens with the new line briefly highlighted
       quiet     nothing pops up: the button's check mark, the cart icon's catch and the count
     Every choice keeps the button's "Added" state (flashAdded, and "✓ Added" on the product
     page). Screen readers hear "Added to cart: <product>" (the toast announces itself).
     Reduced motion: no flight; the count still updates. Only the Add to cart forms come here
     (product page, product cards); Undo and "You may also like" are inside the drawer. */
  var ADD_EFFECTS = ['toast', 'fly', 'fly_note', 'drawer', 'quiet'];
  function addEffect() {
    var v = drawer && drawer.getAttribute('data-add-effect');
    return ADD_EFFECTS.indexOf(v) > -1 ? v : 'toast';
  }
  var cartLive = document.querySelector('[data-cart-live]');
  function announceAdded(item) {
    if (!cartLive || !item) return;
    cartLive.textContent = '';
    setTimeout(function () { cartLive.textContent = 'Added to cart: ' + (item.product_title || item.title || ''); }, 40);
  }
  /* The header's cart button that is actually on screen (phones and computers each have one
     in some layouts). */
  function cartIconEl() {
    var list = document.querySelectorAll('.site-header [data-cart-open]');
    for (var i = 0; i < list.length; i++) { if (list[i].getClientRects().length && list[i].offsetWidth) return list[i]; }
    return list[0] || null;
  }
  function bumpCartIcon() {
    var b = cartIconEl();
    if (!b) return;
    b.classList.remove('is-catch');
    void b.offsetWidth;
    b.classList.add('is-catch');
    clearTimeout(b._catchT);
    b._catchT = setTimeout(function () { b.classList.remove('is-catch'); }, 1000);
  }
  function refreshCartCounted(after) {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      renderCart(c, { animate: true });
      if (after) after(c);
    }).catch(function () {});
  }
  /* The picture the shopper was looking at: the card's own image, or the product page's
     main image. */
  function addSourceImage(form) {
    var card = form.closest('.card');
    var img = card ? card.querySelector('.card__media img') : null;
    if (!img) {
      var pdp = form.closest('.pdp') || document.querySelector('.pdp');
      img = pdp ? pdp.querySelector('.pdp__main-image img, .pdp__media img, .pdp__gallery img') : null;
    }
    return img && img.getBoundingClientRect().width ? img : null;
  }
  function onScreen(r) {
    return !!r && r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
  }
  /* v298 (Raymond: from the sticky bar it flew out of the main picture, scrolled far out of view):
     the flight starts from what the shopper can see —
       1. the product page's sticky Add to Cart bar, while it is showing for this product: its
          thumbnail (or its button when the product has no picture);
       2. the card's picture / the product page's main picture, when on screen;
       3. the button that was pressed, when on screen.
     Nothing of it on screen: no flight (the icon still catches and the count still ticks). */
  function flySource(form, item) {
    var fallbackUrl = item && item.image ? item.image.replace(/(\.[^.]+)$/, '_300x$1') : '';
    /* Only the product page's own form (it holds .pdp__atc): a product card elsewhere in the same
       section, e.g. related products, flies from its own picture. */
    var sec = form.querySelector('.pdp__atc') ? form.closest('.shopify-section') : null;
    var bar = sec ? sec.querySelector('[data-pdp-sticky].is-shown') : null;
    if (bar) {
      var th = bar.querySelector('.pdp-sticky__img'), tr0 = th && th.getBoundingClientRect();
      if (onScreen(tr0)) return { rect: tr0, url: th.currentSrc || th.src || fallbackUrl, small: true };
      var sb = bar.querySelector('[data-pdp-sticky-btn]'), sbr = sb && sb.getBoundingClientRect();
      if (onScreen(sbr)) return { rect: { left: sbr.left + sbr.width / 2 - 24, top: sbr.top + sbr.height / 2 - 24, width: 48, height: 48 }, url: fallbackUrl, small: true };
    }
    var img = addSourceImage(form), ir = img && img.getBoundingClientRect();
    if (onScreen(ir)) return { rect: ir, url: img.currentSrc || img.src || fallbackUrl, small: false };
    var b = form.querySelector('[type="submit"]'), br = b && b.getBoundingClientRect();
    if (onScreen(br)) return { rect: { left: br.left + br.width / 2 - 45, top: br.top + br.height / 2 - 45, width: 90, height: 90 }, url: fallbackUrl, small: false };
    return null;
  }
  function flyToCart(form, item) {
    var target = cartIconEl();
    if (!target || cartMotion().still) return Promise.resolve();
    var from = flySource(form, item);
    if (!from || !from.url) return Promise.resolve();
    var url = from.url, sr = from.rect;
    var vh = window.innerHeight, vw = window.innerWidth;
    /* A small start (the sticky bar's 40-48px thumbnail) keeps its own size; a big picture is
       taken down to at most 200px. */
    var size = from.small ? Math.round(Math.max(32, Math.min(sr.width, sr.height))) : Math.round(Math.max(56, Math.min(sr.width, sr.height, 200)));
    var sx = sr.left + sr.width / 2, sy = sr.top + sr.height / 2;
    var tr = target.getBoundingClientRect();
    var tx = Math.min(vw - 16, Math.max(16, tr.left + tr.width / 2)), ty = Math.max(16, tr.top + tr.height / 2);
    var dx = tx - sx, dy = ty - sy;
    var wrap = document.createElement('div');
    wrap.className = 'cart-fly';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.cssText = 'left:' + (sx - size / 2) + 'px;top:' + (sy - size / 2) + 'px;width:' + size + 'px;height:' + size + 'px';
    var im = document.createElement('img');
    im.src = url; im.alt = '';
    wrap.appendChild(im);
    document.body.appendChild(wrap);
    /* A curve that lifts first and drops into the icon: a quadratic Bézier from the picture to
       the icon, its control point above the higher of the two, sampled into keyframes. The
       picture shrinks to 22px and turns round on the way. */
    var cx = dx * 0.3, cy = Math.min(0, dy) - Math.min(160, 60 + Math.abs(dx) * 0.12);
    var end = 22 / size, frames = [], N = 16;
    for (var i = 0; i <= N; i++) {
      var t = i / N, u = 1 - t;
      var x = 2 * u * t * cx + t * t * dx, y = 2 * u * t * cy + t * t * dy;
      var sc = i === 0 ? 1 : (i <= 2 ? 1.06 : 1 + (end - 1) * Math.pow((i - 2) / (N - 2), 1.15));
      frames.push({ offset: t, transform: 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px) scale(' + sc.toFixed(3) + ')',
        borderRadius: (t < 0.5 ? 14 : 50) + '%', opacity: t > 0.92 ? 0.35 : 1 });
    }
    var anim = wrap.animate(frames, { duration: 780, easing: 'cubic-bezier(.45,0,.25,1)', fill: 'forwards' });
    var done = function () { if (wrap.parentNode) wrap.parentNode.removeChild(wrap); };
    var guard = setTimeout(done, 1600);
    return anim.finished.then(function () { clearTimeout(guard); done(); }, function () { clearTimeout(guard); done(); });
  }
  var noteBar = document.querySelector('[data-cart-added]');
  var noteTimer = null;
  function placeUnderCart(el) {
    var b = cartIconEl();
    var r = b ? b.getBoundingClientRect() : { bottom: 70, right: window.innerWidth - 16 };
    el.style.top = Math.max(12, Math.round(r.bottom + 12)) + 'px';
    el.style.right = Math.max(12, Math.round(window.innerWidth - r.right - 12)) + 'px';
  }
  function hideCartNote() { if (noteBar) { noteBar.classList.remove('is-open'); noteBar.setAttribute('aria-hidden', 'true'); } clearTimeout(noteTimer); }
  function showCartNote(item) {
    if (!noteBar || !item) return;
    var img = noteBar.querySelector('[data-cart-added-img]');
    if (img) img.src = item.image ? item.image.replace(/(\.[^.]+)$/, '_120x$1') : '';
    placeUnderCart(noteBar);
    noteBar.classList.add('is-open');
    noteBar.setAttribute('aria-hidden', 'false');
    clearTimeout(noteTimer);
    noteTimer = setTimeout(hideCartNote, 3500);
  }
  if (noteBar) {
    noteBar.addEventListener('mouseenter', function () { clearTimeout(noteTimer); });
    noteBar.addEventListener('mouseleave', function () { noteTimer = setTimeout(hideCartNote, 1500); });
  }
  /* "View cart" in the slim bar and the hover preview opens the drawer (href = no-JS fallback). */
  document.addEventListener('click', function (e) {
    var a = e.target.closest('[data-cart-added] [data-toast-open-cart], [data-cart-peek] [data-toast-open-cart]');
    if (!a) return;
    e.preventDefault();
    hideCartNote(); hidePeek();
    openCart(); refreshCart(false);
  });
  function highlightLine(key) {
    if (!key) return;
    var q = document.querySelector('.cart-drawer .cart-qty[data-line-key="' + String(key).replace(/"/g, '\\"') + '"]');
    var line = q && q.closest('.cart-line');
    if (!line) return;
    line.classList.remove('is-just-added');
    void line.offsetWidth;
    line.classList.add('is-just-added');
    setTimeout(function () { line.classList.remove('is-just-added'); }, 1800);
  }
  function afterAdd(item, form) {
    var fx = addEffect();
    if (fx === 'toast') { refreshCart(false); showCartToast(item); return; }
    announceAdded(item);
    if (fx === 'drawer') {
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
        renderCart(c); openCart();
        setTimeout(function () { highlightLine(item && item.key); }, 120);
      }).catch(function () { openCart(); refreshCart(false); });
      return;
    }
    if (fx === 'quiet') { bumpCartIcon(); refreshCartCounted(); return; }
    /* fly, fly_note: the count changes as the picture lands */
    flyToCart(form, item).then(function () {
      bumpCartIcon();
      refreshCartCounted();
      if (fx === 'fly_note') showCartNote(item);
    });
  }

  /* ---------- Cart icon preview on hover (v293) ----------
     Theme settings → Cart drawer → "Preview the cart on hover" renders [data-cart-peek]. With a
     mouse, resting on the header's cart icon shows up to three lines, the subtotal, View cart
     and (survey off) Checkout. Not while the cart is empty or the drawer is open. */
  var peek = document.querySelector('[data-cart-peek]');
  var peekT = null;
  function hidePeek() { if (peek) { peek.classList.remove('is-open'); peek.setAttribute('aria-hidden', 'true'); } clearTimeout(peekT); }
  function fillPeek(c) {
    if (!peek || !c) return;
    var list = peek.querySelector('[data-cart-peek-list]'), tot = peek.querySelector('[data-cart-peek-total]');
    var items = c.items || [];
    if (list) list.innerHTML = items.slice(0, 3).map(function (it) {
      return '<div class="cart-peek__item"><img src="' + (it.image ? it.image.replace(/(\.[^.]+)$/, '_120x$1') : '') + '" alt="" width="44" height="44">' +
        '<span class="cart-peek__name">' + escapeHtml(it.product_title) + '</span><span class="cart-peek__qty">×' + it.quantity + '</span></div>';
    }).join('') + (items.length > 3 ? '<div class="cart-peek__more">+ ' + (items.length - 3) + ' more</div>' : '');
    if (tot) tot.textContent = formatMoney(c.total_price);
  }
  if (peek && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    var showPeek = function (btn) {
      if (!document.documentElement.classList.contains('cart-has-items') || (drawer && drawer.classList.contains('is-open'))) return;
      clearTimeout(peekT);
      placeUnderCart(peek);
      if (cartSnapshot) fillPeek(cartSnapshot);
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) { cartSnapshot = cartSnapshot || c; fillPeek(c); }).catch(function () {});
      peek.classList.add('is-open');
      peek.setAttribute('aria-hidden', 'false');
    };
    var peekSoon = function () { clearTimeout(peekT); peekT = setTimeout(hidePeek, 240); };
    document.querySelectorAll('.site-header [data-cart-open]').forEach(function (b) {
      b.addEventListener('mouseenter', function () { showPeek(b); });
      b.addEventListener('mouseleave', peekSoon);
      b.addEventListener('click', hidePeek);
    });
    peek.addEventListener('mouseenter', function () { clearTimeout(peekT); });
    peek.addEventListener('mouseleave', peekSoon);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') hidePeek(); });
    window.addEventListener('scroll', hidePeek, { passive: true });
  }

  /* Signature of the cart the drawer rows were last built for — see the comment
     at its use inside renderCart. */
  var renderedLinesKey = null;

  /* ---------- Discount fields: Liquid and the Ajax cart disagree ----------
     The drawer is rendered twice by two different things. On first paint Liquid builds
     it from `cart.cart_level_discount_applications`, where a discount carries `title`
     and `total_allocated_amount`. After any change the JS rebuilds it from /cart.js —
     and those field names are Liquid's, not the Ajax API's. `total_allocated_amount`
     does not appear anywhere in the Ajax cart reference.

     The symptom was a row reading "undefined  −$NaN": the array had an entry, so a row
     was written, but both fields read undefined and `undefined - 0` formats as NaN. It
     only showed after a re-render, which is why it took a second product to appear.

     Rather than bet on one spelling, read the ones that exist across both shapes — and,
     more importantly, REFUSE TO RENDER A ROW THAT IS NOT FULLY UNDERSTOOD. That last
     rule is the actual fix: whatever Shopify returns now or later, a row is only written
     when there is both a label and a real number, so "undefined" and "$NaN" cannot reach
     a shopper again. The "You save" line is computed from the totals and is unaffected,
     which is why it stayed correct while the row above it broke. */
  function discountTitle(d) {
    if (!d) return '';
    var da = d.discount_application || {};
    return d.title || d.description || da.title || da.description || '';
  }
  function discountAmount(d) {
    if (!d) return null;
    var da = d.discount_application || {};
    var candidates = [d.total_allocated_amount, d.amount, d.discounted_amount,
                      da.total_allocated_amount, da.amount];
    for (var i = 0; i < candidates.length; i++) {
      var n = Number(candidates[i]);
      if (candidates[i] !== null && candidates[i] !== undefined && isFinite(n)) return n;
    }
    return null;
  }
  function discountRow(d, cls) {
    var t = discountTitle(d), a = discountAmount(d);
    if (!t || a === null) return '';
    return '<div class="' + cls + '"><span>' + t + '</span><span>\u2212' + formatMoney(a) + '</span></div>';
  }

  /* Customer-typed text (gift card messages, names) goes into innerHTML below, so it
     is escaped. Product titles come from the admin but are escaped too while here. */
  function escapeHtml(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* A line's visible properties — the same three rules as
     snippets/line-item-properties.liquid, which renders them on page load: names
     starting "_" are hidden (Shopify's "__shopify_…" fields and the theme's
     "_design_…"), blanks are skipped, "Recipient name" reads "Recipient" and a
     "Send on" date reads "Oct 5, 2026". Change one, change the other. */
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function lineProps(it) {
    var props = it.properties || {};
    var rows = Object.keys(props).map(function (k) {
      var v = props[k];
      if (k.charAt(0) === '_' || v == null || String(v).trim() === '') return '';
      var label = k === 'Recipient name' ? 'Recipient' : k;
      var shown = String(v).trim();
      if (k === 'Send on') {
        var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(shown);
        if (m && +m[2] >= 1 && +m[2] <= 12) shown = MONTHS[+m[2] - 1] + ' ' + (+m[3]) + ', ' + m[1];
      }
      return '<li><span class="line-props__k">' + escapeHtml(label) + ':</span> ' + escapeHtml(shown) + '</li>';
    }).join('');
    return rows ? '<ul class="line-props">' + rows + '</ul>' : '';
  }

  var lastCartTotal = null;
  /* The items the drawer's rows were last drawn or patched for (v248). */
  var renderedItems = null;
  function linePriceHtml(it) {
    return (it.original_line_price > it.final_line_price)
      ? '<s class="cart-was">' + formatMoney(it.original_line_price) + '</s> <span class="cart-now">' + formatMoney(it.final_line_price) + '</span>'
      : formatMoney(it.final_line_price);
  }
  function lineDiscHtml(it) {
    return (it.line_level_discount_allocations || []).map(function (d) {
      var t = discountTitle(d), a = discountAmount(d);
      if (!t || a === null) return '';
      return '<div class="cart-line__discount">' + t + ' (\u2212' + formatMoney(a) + ')</div>';
    }).join('');
  }
  /* What a row's markup depends on apart from its quantity, prices and discounts. */
  function lineShape(it) {
    return [it.key, it.image || '', it.product_title, it.variant_title || '', it.url, JSON.stringify(it.properties || {})].join('~');
  }
  /* One cart line's markup (the drawer builds and inserts lines from it). */
  var MINUS_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M5 12h14"/></svg>';
  var PLUS_SVG  = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 5v14"/><path d="M5 12h14"/></svg>';
  var TRASH_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M4 7h16"/><path d="M10 4h4a1 1 0 0 1 1 1v2H9V5a1 1 0 0 1 1-1z"/><path d="M6 7l1 12.2a1.8 1.8 0 0 0 1.8 1.8h6.4a1.8 1.8 0 0 0 1.8-1.8L18 7"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>';
  function lineHtml(it) {
    return '<div class="cart-line">' +
      '<a class="cart-line__media" href="' + it.url + '"><img src="' + (it.image ? it.image.replace(/(\.[^.]+)$/, '_200x$1') : '') + '" alt="" width="70" height="70"></a>' +
      '<div class="cart-line__info">' +
        '<a class="cart-line__title" href="' + it.url + '">' + escapeHtml(it.product_title) + '</a>' +
        (it.variant_title && it.variant_title !== 'Default Title' ? '<div class="cart-line__meta">' + escapeHtml(it.variant_title) + '</div>' : '') +
        lineProps(it) +
        lineDiscHtml(it) +
        '<div class="cart-qty" data-line-key="' + it.key + '">' +
          '<button type="button" class="cart-qty__btn" data-drawer-qty-down aria-label="Decrease quantity">' + MINUS_SVG + '</button>' +
          '<input class="cart-qty__input" type="number" value="' + it.quantity + '" min="0" aria-label="Quantity" data-drawer-qty>' +
          '<button type="button" class="cart-qty__btn" data-drawer-qty-up aria-label="Increase quantity">' + PLUS_SVG + '</button>' +
        '</div>' +
      '</div>' +
      '<div class="cart-line__right">' +
        '<div class="cart-line__price">' + linePriceHtml(it) + '</div>' +
        '<button class="cart-line__remove" data-line-remove="' + it.key + '" aria-label="Remove item">' + TRASH_SVG + '</button>' +
      '</div>' +
    '</div>';
  }
  /* Lines are shown oldest first, so a new one joins at the BOTTOM of the list (v249).
     /cart.js lists the newest first; only the display order is turned round — the
     suggestions still read cart.items newest first. The cart page does the same in
     Liquid (`cart.items reversed`). */
  function displayLines(cart) { return (cart.items || []).slice().reverse(); }
  /* Patch the drawer's rows in place (v248) instead of rebuilding them: quantities, prices
     and discounts are updated, lines gone from the cart close, and (v249) lines new to the
     cart are inserted where they belong (at the bottom, for an add). No existing picture is
     re-created, so nothing flickers and the other lines never jump. Lines with a change of
     their own still on the way keep the shopper's number. Returns false when a rebuild is
     needed (lines reordered, a changed picture or title, or rows that are not ours). */
  function patchLines(itemsEl, items, animate) {
    if (!renderedItems) return false;
    var prev = {}, order = [], added = [];
    renderedItems.forEach(function (it, i) { prev[it.key] = { it: it, i: i }; });
    for (var n = 0; n < items.length; n++) {
      var p = prev[items[n].key];
      if (!p) { added.push(items[n].key); continue; }
      if (lineShape(p.it) !== lineShape(items[n])) return false;
      if (order.length && p.i < order[order.length - 1]) return false;
      order.push(p.i);
      if (!cartViews.drawer.row(items[n].key)) return false;
    }
    var keep = {};
    items.forEach(function (it) { keep[it.key] = true; });
    Array.prototype.forEach.call(itemsEl.querySelectorAll(':scope > .cart-line'), function (row) {
      var q = row.querySelector('.cart-qty[data-line-key]'), k = q && q.getAttribute('data-line-key');
      if (k && (keep[k] || (lineState[k] && lineState[k].removing))) return;   /* a removal in progress ends itself */
      closeSpacer(rowSpacer(row, ''));   /* gone from the cart by other means */
    });
    /* New lines go straight after the line before them in the cart's order (or first). */
    items.forEach(function (it, i) {
      if (added.indexOf(it.key) < 0) return;
      var tmp = document.createElement('div');
      tmp.innerHTML = lineHtml(it);
      var row = tmp.firstChild, before = i > 0 ? cartViews.drawer.row(items[i - 1].key) : null;
      if (before && before.parentNode === itemsEl) {
        var ref = before.nextSibling;
        /* an Undo bar or a closing gap that followed the line before stays above the new line */
        while (ref && ref.classList && ref.classList.contains('cart-motion-row')) ref = ref.nextSibling;
        itemsEl.insertBefore(row, ref);
      } else if (i === 0) itemsEl.insertBefore(row, itemsEl.firstChild);
      else itemsEl.appendChild(row);
      lineSt(it.key).confirmed = it.quantity;
    });
    items.forEach(function (it) {
      if (added.indexOf(it.key) > -1) return;
      var row = cartViews.drawer.row(it.key), p = prev[it.key].it;
      var input = row.querySelector('[data-drawer-qty]');
      if (input) input.setAttribute('value', it.quantity);
      lineSt(it.key).confirmed = it.quantity;
      if (linePending(it.key)) return;
      if (input && String(input.value) !== String(it.quantity)) rollInput(input, it.quantity, it.quantity > (parseInt(input.value, 10) || 0), animate);
      var price = row.querySelector('.cart-line__price');
      if (price) {
        rollSwap(price, linePriceHtml(it), it.final_line_price >= p.final_line_price, animate);
        price.style.opacity = '';
      }
      var info = row.querySelector('.cart-line__info'), qty = row.querySelector('.cart-qty');
      if (info) {
        Array.prototype.forEach.call(info.querySelectorAll('.cart-line__discount'), function (d) { d.remove(); });
        var tmp = document.createElement('div');
        tmp.innerHTML = lineDiscHtml(it);
        while (tmp.firstChild) info.insertBefore(tmp.firstChild, qty && qty.parentNode === info ? qty : null);
      }
    });
    return true;
  }
  function renderCart(cart, opts) {
    opts = opts || {};
    var count = cart.item_count;
    var prevCount = null;
    var prevTotal = lastCartTotal;
    cartSnapshot = cart;
    document.querySelectorAll('.cart-count').forEach(function (el) {
      if (prevCount === null) prevCount = parseInt(el.textContent, 10);
      el.textContent = count; el.style.display = count > 0 ? 'flex' : 'none';
    });
    /* The header's "Cart (n)" words (Header → Search, account and cart → Words). Always
       shown, zero included — it reads as a label, not a badge. */
    document.querySelectorAll('[data-cart-count-text]').forEach(function (el) { el.textContent = count; });
    /* v293: the cart icon's look with items (html.cart-has-items) and the Pill's count and total. */
    document.documentElement.classList.toggle('cart-has-items', count > 0);
    document.querySelectorAll('[data-cart-pill-count]').forEach(function (el) { el.textContent = count; });
    document.querySelectorAll('[data-cart-pill-total]').forEach(function (el) { el.textContent = formatMoney(cart.total_price); });
    /* On the cart page, a change made anywhere else (the drawer, its suggestions) is
       brought into the page's lines too (v248). */
    if (!opts.fromPage && cartPageSid() && pageShownSig !== null && cartSig(cart) !== pageShownSig) {
      clearTimeout(pageRefreshT);
      pageRefreshT = setTimeout(pageRefresh, 60);
    }
    if (opts.animate && !cartMotion().still && prevCount !== null && prevCount !== count) {
      document.querySelectorAll('.cart-count').forEach(function (el) {
        if (el.offsetWidth) el.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.4)' }, { transform: 'scale(1)' }], { duration: 380, easing: 'cubic-bezier(.3,1.6,.5,1)' });
      });
    }
    var itemsEl = document.querySelector('.cart-drawer__items');
    var footEl = document.querySelector('.cart-drawer__foot');
    if (!itemsEl) return;
    /* Undo bars and closing gaps (v248) are kept across a rebuild of the list. */
    var motionRows = Array.prototype.slice.call(itemsEl.querySelectorAll(':scope > .cart-motion-row--undo'));
    if (count === 0) {
      itemsEl.innerHTML = '<div class="cart-drawer__empty"><p>Your cart is empty.</p><a class="btn" href="/collections/all">Shop products</a></div>';
      keepMotionRows(itemsEl, motionRows, function () { return null; });
      renderedLinesKey = null;   /* force a rebuild when the cart next has items */
      renderedItems = null;
      lastCartTotal = cart.total_price;
      if (footEl) footEl.style.display = 'none';
      loadDrawerReco();   /* nothing to recommend against — this hides the panel */
      return;
    }
    if (footEl) footEl.style.display = 'block';

    /* Skip the rebuild when nothing the row markup depends on has changed.
       Rewriting innerHTML discards every <img> and builds a replacement that has
       no decoded image for a frame or two, which is the thumbnail flicker on
       drawer open. The signature therefore has to cover everything the markup
       renders — not just which products are present, but quantities, both line
       prices and every line-level discount — or a stale row would survive a
       change it should have shown. Same approach as the recommendations panel;
       it just needs a wider key here. When it has changed, patchLines (v248) still
       avoids the rebuild if only quantities, prices or discounts moved. */
    var lines = displayLines(cart);
    var linesKey = lines.map(function (it) {
      return [
        it.key,
        it.quantity,
        it.final_line_price,
        it.original_line_price,
        it.image || '',
        it.product_title,
        it.variant_title || '',
        it.url,
        JSON.stringify(it.properties || {}),
        /* Same defensive read as the row builders, and this one matters more: an
           allocation without a discount_application would THROW here, and this runs
           before anything is drawn — so the whole drawer would fail to re-render
           rather than merely showing one wrong line. */
        (it.line_level_discount_allocations || []).map(function (d) {
          return discountTitle(d) + '=' + discountAmount(d);
        }).join('|')
      ].join('~');
    }).join('§');
    var hasLines = !!itemsEl.querySelector('.cart-line');
    if (linesKey !== renderedLinesKey || !hasLines) {
      if (!(hasLines && patchLines(itemsEl, lines, !!opts.animate))) {
      itemsEl.innerHTML = lines.map(lineHtml).join('');
      keepMotionRows(itemsEl, motionRows, function (k) { return cartViews.drawer.row(k); });
      cart.items.forEach(function (it) { var s = lineState[it.key]; if (s && !s.removing) s.confirmed = it.quantity; });
      }
      renderedLinesKey = linesKey;
      renderedItems = lines;
    }

    /* The summary has no images, so rebuilding it is free and it is always
       rewritten — cart-level discounts and the subtotal move independently of the
       row markup. */
    var summary = document.querySelector('[data-cart-summary]');
    if (summary) {
      var html = '';
      (cart.cart_level_discount_applications || []).forEach(function (d) {
        html += discountRow(d, 'cart-drawer__disc-row');
      });
      var saved = cart.original_total_price - cart.total_price;
      if (saved > 0) html += '<div class="cart-drawer__disc-row cart-drawer__saved"><span>You save</span><span>−' + formatMoney(saved) + '</span></div>';
      html += '<div class="cart-drawer__subtotal"><span>Subtotal</span><span>' + formatMoney(cart.total_price) + '</span></div>';
      summary.innerHTML = html;
      /* v248: the subtotal counts to its new amount after a quantity change or removal. */
      if (opts.animate && !cartMotion().still) countUp(summary.querySelector('.cart-drawer__subtotal span:last-child'), prevTotal, cart.total_price);
    }
    lastCartTotal = cart.total_price;
    /* Recommendations are seeded from the cart, so they have to be re-read
       whenever it changes. Every mutation — add, quantity change, remove — ends
       up here, so this is the one place that catches all of them. Previously it
       only ran when the drawer was opened, which is why changing quantities or
       removing a line left a stale list. The signature check inside means an
       unchanged cart still costs nothing. */
    loadDrawerReco();
    /* Request access: re-ask Liquid whether checkout must be blocked (no-op when off). */
    raCartCheck();
  }

  /* Drawer quantity steppers and remove buttons: one listener each on the drawer, in
     "Changing quantity and removing (v248)" below. (Until v247 each row was bound
     here after every render, and each press re-read the number still on screen.) */


  /* Drawer "You may also like" — Shopify recommendations seeded by the newest cart item */
  /* The recommendations need two round trips (/cart.js, then /recommendations),
     so on the first open of a freshly loaded page the panel is still [hidden]
     while the drawer slides in. Un-hiding it at that point drops it straight at
     its final position with nothing to transition from — which is the pop-in.
     Snap it to the start state, flush layout so the browser records that as the
     "from" value, then release on the next frame so the usual 40px ease + fade
     plays. The remaining part of the 0.6s stagger is preserved, so a fast fetch
     still waits its turn behind the cart panel instead of racing ahead. */
  var RECO_STAGGER = 600;
  function showDrawerReco(panel) {
    var wasHidden = panel.hidden;
    panel.hidden = false;
    if (!wasHidden || !drawer || !drawer.classList.contains('is-open')) return;
    var remaining = Math.max(0, RECO_STAGGER - (Date.now() - cartOpenedAt));
    panel.classList.add('reco--enter');
    void panel.offsetWidth;
    panel.style.transitionDelay = (remaining / 1000) + 's';
    /* Release on the next frame. The timeout is a fallback: requestAnimationFrame
       is paused in a backgrounded tab, and without it the panel could sit stuck at
       opacity 0. Removing the class twice is harmless. */
    var release = function () { panel.classList.remove('reco--enter'); };
    requestAnimationFrame(release);
    setTimeout(release, 60);
    setTimeout(function () { panel.style.transitionDelay = ''; }, remaining + 700);
  }
  function hideDrawerReco(panel) {
    panel.hidden = true;
    panel.classList.remove('reco--enter');
    panel.style.transitionDelay = '';
  }

  /* Signature of the cart the current recommendation list was built for. This runs
     on every drawer open, and rewriting list.innerHTML throws away the <img>
     elements and builds new ones — which have no decoded image for a frame or two,
     hence the intermittent flicker. The recommendations only change when the cart's
     products change, so when the signature matches, the existing markup is left
     untouched and nothing repaints. */
  var recoCartKey = null;
  /* Per-product recommendation cache. Shopify's recommendations for a given
     product do not depend on the cart, so a response stays valid for the life of
     the page. That is what makes the whole-cart merge below affordable: a cart of
     five distinct products costs five requests once, and nothing at all on every
     subsequent drawer open. */
  var recoCache = {};
  /* Ceiling on parallel requests. /recommendations/products.json accepts exactly
     one product_id, so a whole-cart merge costs one request per distinct product.
     /cart.js returns the NEWEST line FIRST, so when a cart exceeds this ceiling it
     is the most recently added products that get used. */
  var RECO_MAX_SEEDS = 10;
  /* Product the shopper added most recently. Recorded explicitly rather than read
     off the end of cart.items, because /cart.js is newest-first — taking the last
     entry meant taking the OLDEST item in the cart, which is why adding something
     new used to produce the same suggestions over and over. Now only breaks ties. */
  var lastAddedProductId = null;
  function noteAdded(res) {
    if (!res) return;
    if (res.product_id) lastAddedProductId = res.product_id;
    else if (res.items && res.items.length && res.items[0].product_id) lastAddedProductId = res.items[0].product_id;
  }

  /* ---------- Adding from "You may also like" (v247) ----------
     Theme settings → Cart drawer → "Adding from You may also like" (data-add-motion on
     the drawer): how a suggestion travels into the cart once Shopify has added it.
       arc    the picture lifts out of the suggestion, arcs into its place in the cart and
              settles (default)
       glide  the whole suggestion row slides across into the cart and becomes the line
       drop   the new cart line drops in from above with a small bounce and a highlight
       bag    the picture arcs up and shrinks into the drawer's "Cart" title, which gives a
              small nudge; then the line slides in
       none   no animation (the line simply appears, as before v247)
     Every style: the button reads "✓ Added", a new line opens its own space in the list,
     the subtotal counts up to the new total, the header's bag count pops, and the
     suggestion fades out before the list refreshes. The animation runs only after the
     add has succeeded, on the cart Shopify returns, so it can never show something that
     did not happen. Reduced motion: no animation. The flying picture is a fixed-position
     copy above everything, so it works the same on computers (suggestions in a panel
     beside the cart) and on phones (a strip under it). */
  var recoHold = false, recoPending = false;
  var ADD_MOTIONS = ['arc', 'glide', 'drop', 'bag', 'none'];
  function addMotion() {
    var m = drawer && drawer.getAttribute('data-add-motion');
    return ADD_MOTIONS.indexOf(m) > -1 ? m : 'arc';
  }
  function markRecoAdded(row) {
    var l = row && row.querySelector('.reco-cta__label');
    if (l) l.textContent = '\u2713 Added';
  }
  function flyCopy(el, r) {
    var c = el.cloneNode(true);
    c.removeAttribute('href');
    c.setAttribute('aria-hidden', 'true');
    c.style.cssText = 'position:fixed;margin:0;z-index:1000;pointer-events:none;box-sizing:border-box;' +
      'left:' + r.left + 'px;top:' + r.top + 'px;width:' + r.width + 'px;height:' + r.height + 'px;transform-origin:center';
    /* A bare picture copy is out of its row, so the row's image rules no longer reach it. */
    if (!c.classList.contains('reco-item')) {
      c.querySelectorAll('img').forEach(function (i) {
        i.style.cssText = 'display:block;width:100%;height:100%;object-fit:contain;mix-blend-mode:darken';
      });
    }
    /* Inside the drawer (which is fixed and untransformed), so the copy keeps the drawer's
       sizes and colours and still sits above both panels. */
    (drawer || document.body).appendChild(c);
    return c;
  }
  function countUp(el, from, to) {
    if (!el || from == null || from === to) return;
    var t0 = null;
    function step(t) {
      if (t0 === null) t0 = t;
      var k = Math.min(1, (t - t0) / 650), e = 1 - Math.pow(1 - k, 3);
      el.textContent = formatMoney(Math.round(from + (to - from) * e));
      if (k < 1) requestAnimationFrame(step); else el.textContent = formatMoney(to);
    }
    requestAnimationFrame(step);
    setTimeout(function () { el.textContent = formatMoney(to); }, 900);   /* a background tab pauses rAF */
  }
  /* Scroll the list that holds `el` (if it scrolls) just enough to show all of it, as an
     eased glide over `ms` instead of a jump. Returns how far the content will move up, so
     a flight can aim at the final position. */
  function glideIntoView(el, ms) {
    var sc = el.parentNode;
    while (sc && sc !== document.body) {
      var oy = getComputedStyle(sc).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && sc.scrollHeight > sc.clientHeight) break;
      sc = sc.parentNode;
    }
    if (!sc || sc === document.body) return 0;
    var er = el.getBoundingClientRect(), sr = sc.getBoundingClientRect(), pad = 8, d = 0;
    if (er.bottom > sr.bottom - pad) d = er.bottom - sr.bottom + pad;
    else if (er.top < sr.top + pad) d = er.top - sr.top - pad;
    var start = sc.scrollTop, end = Math.max(0, Math.min(sc.scrollHeight - sc.clientHeight, start + d));
    d = end - start;
    if (!d) return 0;
    var t0 = null;
    var step = function (t) {
      if (t0 === null) t0 = t;
      var k = Math.min(1, (t - t0) / ms), e = k < .5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
      sc.scrollTop = start + d * e;
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    setTimeout(function () { sc.scrollTop = end; }, ms + 120);   /* a background tab pauses rAF */
    return d;
  }
  function playRecoAdd(row, res, cart) {
    var motion = addMotion();
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var media = row && row.querySelector('.reco-item__media');
    var from = media && media.getBoundingClientRect();
    var item = res && res.items && res.items[0];
    var key = item && item.key;
    var sel = function (k) { return '.cart-drawer .cart-qty[data-line-key="' + String(k).replace(/"/g, '\\"') + '"]'; };
    var existed = !!(key && document.querySelector(sel(key)));
    var oldTotal = lastCartTotal;
    if (motion === 'none' || reduce || !media || !from || !from.width || !Element.prototype.animate || !key) {
      renderCart(cart);
      return Promise.resolve();
    }
    recoHold = true;
    renderCart(cart);
    var done = false;
    var finish = function () {   /* once: the guard below and the normal end can both call it */
      if (done) return;
      done = true;
      recoHold = false;
      if (recoPending) { recoPending = false; loadDrawerReco(); }
    };
    var q = document.querySelector(sel(key));
    var line = q && q.closest('.cart-line');
    if (!line) { finish(); return Promise.resolve(); }
    /* v249: the new line joins at the BOTTOM of the list, so nothing above it moves. If it
       is below the visible part of the list, the list glides down to it during the flight
       (no jump), and the landing point is where the line will be once it has. */
    var shift = glideIntoView(line, 640);
    var target = line.querySelector('.cart-line__media') || line;
    var tr = target.getBoundingClientRect();
    var to = { left: tr.left, top: tr.top - shift, width: tr.width, height: tr.height };
    var ease = 'cubic-bezier(.4,0,.2,1)';
    var opening = null;
    if (!existed) {
      var h = line.offsetHeight;
      line.style.overflow = 'hidden';
      opening = line.animate([{ height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }],
        { duration: 460, easing: 'cubic-bezier(.2,.7,.2,1)' });
      opening.finished.then(function () { line.style.overflow = ''; }, function () { line.style.overflow = ''; });
    }
    /* The flights move a copy with transform only (no layout work per frame), and land
       softly: the copy fades over the real picture, which settles with a small give. */
    var land = function () {
      target.style.visibility = '';
      if (target.animate) target.animate([{ transform: 'scale(.94)' }, { transform: 'scale(1.03)', offset: .6 }, { transform: 'none' }], { duration: 340, easing: 'ease-out' });
    };
    var flight;
    if (motion === 'arc') {
      var c = flyCopy(media, from);
      c.style.transformOrigin = '0 0';
      target.style.visibility = 'hidden';
      media.style.visibility = 'hidden';   /* lifted out of the suggestion */
      var sc = to.width / from.width;
      var tx = to.left - from.left, ty = to.top + (to.height - from.height * sc) / 2 - from.top;
      var mx = tx / 2, my = Math.min(0, ty) - 70, ms = (1 + sc) / 2 * 1.08;
      flight = settle(c.animate([
        { transform: 'translate(0px,0px) scale(1) rotate(0deg)' },
        { transform: 'translate(' + mx.toFixed(1) + 'px,' + my.toFixed(1) + 'px) scale(' + ms.toFixed(3) + ') rotate(-6deg)', offset: .45 },
        { transform: 'translate(' + tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px) scale(' + sc.toFixed(3) + ') rotate(0deg)' }
      ], { duration: 760, easing: ease, fill: 'forwards' }), 1200).then(function () {
        land();
        return settle(c.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: 'ease-out', fill: 'forwards' }), 500)
          .then(function () { c.remove(); });
      });
    } else if (motion === 'glide') {
      var rr = row.getBoundingClientRect(), lr = line.getBoundingClientRect();
      var g = flyCopy(row, rr);
      g.style.background = 'var(--color-bg)';
      g.style.height = 'auto';
      g.style.transformOrigin = '0 0';
      line.style.visibility = 'hidden';
      row.style.visibility = 'hidden';
      flight = settle(g.animate([{ transform: 'translate(0px,0px)', width: rr.width + 'px' },
                                 { transform: 'translate(' + (lr.left - rr.left).toFixed(1) + 'px,' + (lr.top - shift - rr.top).toFixed(1) + 'px)', width: lr.width + 'px' }],
        { duration: 680, easing: 'cubic-bezier(.65,0,.35,1)', fill: 'forwards' }), 1100).then(function () {
          line.style.visibility = '';
          return settle(g.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, fill: 'forwards' }), 500).then(function () { g.remove(); });
        });
    } else if (motion === 'drop') {
      /* Resolved here: a var() inside animation keyframes is not read by every browser. */
      var glow = getComputedStyle(line).getPropertyValue('--color-light').trim() || 'rgba(0,0,0,.05)';
      flight = Promise.all([
        line.animate([{ transform: 'translateY(-24px)', opacity: 0 }, { transform: 'translateY(4px)', opacity: 1, offset: .7 }, { transform: 'none' }],
          { duration: 560, easing: 'cubic-bezier(.3,1.3,.5,1)' }).finished,
        line.animate([{ backgroundColor: glow }, { backgroundColor: 'transparent' }], { duration: 1200, easing: 'ease-out' }).finished
      ]);
    } else {
      var head = document.querySelector('.cart-drawer__heading') || line;
      var hr = head.getBoundingClientRect();
      var b = flyCopy(media, from);
      line.style.visibility = 'hidden';
      media.style.visibility = 'hidden';
      var ex = hr.left + Math.min(hr.width, 60) / 2 - (from.left + from.width / 2), ey = hr.top + hr.height / 2 - (from.top + from.height / 2);
      flight = settle(b.animate([
        { transform: 'translate(0px,0px) scale(1)', opacity: 1 },
        { transform: 'translate(' + (ex / 2).toFixed(1) + 'px,' + (Math.min(0, ey) - 60).toFixed(1) + 'px) scale(.8)', opacity: 1, offset: .5 },
        { transform: 'translate(' + ex.toFixed(1) + 'px,' + ey.toFixed(1) + 'px) scale(.15)', opacity: .4 }
      ], { duration: 640, easing: ease, fill: 'forwards' }), 1100).then(function () {
        b.remove();
        head.animate([{ transform: 'none' }, { transform: 'scale(1.12) rotate(-3deg)' }, { transform: 'none' }], { duration: 400, easing: 'cubic-bezier(.3,1.6,.5,1)' });
        line.style.visibility = '';
        return line.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 360, easing: 'ease' }).finished;
      });
    }
    var sub = document.querySelector('[data-cart-summary] .cart-drawer__subtotal span:last-child');
    countUp(sub, oldTotal, cart.total_price);
    document.querySelectorAll('.cart-count').forEach(function (el) {
      el.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.5)' }, { transform: 'scale(1)' }], { duration: 400, easing: 'cubic-bezier(.3,1.6,.5,1)' });
    });
    /* Safety net: whatever happens to an animation, the cart is never left with a hidden
       line or a frozen list. */
    var guard = setTimeout(function () { line.style.visibility = ''; target.style.visibility = ''; finish(); }, 2500);
    return Promise.resolve(flight).catch(function () {}).then(function () {
      line.style.visibility = ''; target.style.visibility = '';
      return row.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(.96)' }], { duration: 250, easing: 'ease', fill: 'forwards' }).finished;
    }).catch(function () {}).then(function () { clearTimeout(guard); finish(); });
  }

  /* ---------- Changing quantity and removing (v248) ----------
     The cart drawer and the cart page share this. Theme settings → Cart drawer:
       Changing quantity (data-qty-motion)
         roll   the number rolls up or down, the line price rolls to its new amount, and
                the picture hops (default)
         float  a small "+1" / "−1" floats up from the stepper, and the picture hops
         hop    the picture hops only
         none   no animation
         The picture hop: on +, the picture lifts with a second copy stacked behind it;
         on −, it squashes a little.
       Removing a product (data-remove-motion)
         ash    the line greys and crumbles into dust, left to right (default)
         slide  the line slides away to the right and fades
         bin    the picture arcs into the trash icon, which wiggles; the line fades
         fold   the line folds away
         none   no animation
       Undo after removing (data-cart-undo, on by default): the removed line becomes a
         "Removed … Undo" bar for 5 seconds (paused while the pointer or focus is on it).
         Undo adds the same variant back with the same quantity, line properties and
         subscription plan. Shopify lists it as the newest line.

     How a change travels:
       - The number changes the moment + or − is pressed. Presses within 350ms are sent as
         ONE request with the final quantity (before v248 each press re-read the number
         still on screen, so two quick presses added one). A press while a request is in
         flight is sent once that request is back. Typing a number sends it on change;
         Enter no longer submits the cart page's form (which went to checkout).
       - Shopify's answer is the truth: if it refuses (out of stock) or adds fewer than
         asked, the number rolls back to what is really in the cart, the stepper shakes,
         and a small note says why.
       - Prices are never guessed: the line price waits (dimmed) and rolls to Shopify's
         amount, discounts included.
       - Lines are patched in place when only quantities, prices or discounts changed, so
         the pictures no longer flicker. Anything else rebuilds the list as before.
       - Removing: the animation starts at once and the request goes in parallel. The
         gap closes (or the Undo bar appears) only once Shopify confirms. If it fails, the
         line comes back and the cart is re-read.
       - The cart page no longer reloads: the same request returns the cart section
         (bundled section rendering), and its lines and totals are patched from it. The
         express checkout buttons are never re-rendered. A change made in the drawer while
         on the cart page refreshes the page's lines too, and the other way round.
     Reduced motion: no movement anywhere; the Undo bar still appears (it is not motion). */
  var QTY_MOTIONS = ['roll', 'float', 'hop', 'none'];
  var REMOVE_MOTIONS = ['ash', 'slide', 'bin', 'fold', 'none'];
  var cartSnapshot = null;   /* the newest /cart.js-shaped cart seen: titles and undo data */
  function cartMotion() {
    var src = drawer || document.querySelector('[data-qty-motion]');
    var pick = function (attr, list, d) {
      var v = src && src.getAttribute(attr);
      return list.indexOf(v) > -1 ? v : d;
    };
    var still = !Element.prototype.animate ||
      !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    return {
      qty: still ? 'none' : pick('data-qty-motion', QTY_MOTIONS, 'roll'),
      remove: still ? 'none' : pick('data-remove-motion', REMOVE_MOTIONS, 'ash'),
      undo: !(src && src.getAttribute('data-cart-undo') === 'false'),
      still: still
    };
  }
  /* No movement for the gap closing, the Undo bar opening or a restored line: reduced
     motion, or "No animation" chosen for removing. */
  function calmRemove() { var m = cartMotion(); return m.still || m.remove === 'none'; }
  function keySel(k) { return String(k).replace(/\\/g, '\\\\').replace(/"/g, '\\"'); }
  function findItem(cart, key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) if (items[i].key === key) return items[i];
    return null;
  }
  function relPos(el) {
    if (el && getComputedStyle(el).position === 'static') el.style.position = 'relative';
    return el;
  }
  function cssVar(el, name, fallback) {
    var v = el ? getComputedStyle(el).getPropertyValue(name).trim() : '';
    return v || fallback;
  }
  /* Responses can come back out of order; a cart older than one already shown is not
     drawn again (it could bring back a line that was just removed). */
  var reqSeq = 0, appliedSeq = 0;
  function freshest(res) {
    if (res.seq < appliedSeq) return false;
    appliedSeq = res.seq;
    return true;
  }
  function postCart(url, body) {
    var seq = ++reqSeq;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; })
        .then(function (j) { return { ok: r.ok, status: r.status, body: j || {}, seq: seq }; });
    });
  }
  /* Every animation here ends by itself even when the tab is in the background (where
     animations can stall): `settle` resolves on the animation or after `ms`, whichever
     comes first. */
  function settle(anim, ms) {
    return new Promise(function (res) {
      var done = false;
      var end = function () { if (!done) { done = true; res(); } };
      if (anim && anim.finished) anim.finished.then(end, end);
      setTimeout(end, ms);
    });
  }

  /* -- small motions -- */
  var ROLL = { duration: 320, easing: 'cubic-bezier(.5,0,.2,1)' };
  function rollPair(oldNode, newNode, up) {
    var d = up ? 1 : -1;
    oldNode.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(' + (-100 * d) + '%)', opacity: 0 }],
      { duration: ROLL.duration, easing: ROLL.easing, fill: 'forwards' });
    return newNode.animate([{ transform: 'translateY(' + (100 * d) + '%)', opacity: 0 }, { transform: 'none', opacity: 1 }], ROLL);
  }
  /* Swap an element's contents, rolling old out and new in ("roll" only; otherwise a
     plain swap). Compared after the browser's own normalising, so equal prices never roll. */
  function rollSwap(el, html, up, animate) {
    if (!el) return;
    if (el._rollEnd) el._rollEnd();
    var probe = document.createElement(el.tagName);
    probe.innerHTML = html;
    if (probe.innerHTML === el.innerHTML) return;
    if (!animate || cartMotion().qty !== 'roll' || !el.offsetWidth) { el.innerHTML = html; return; }
    var box = document.createElement('span'), a = document.createElement('span'), b = document.createElement('span');
    box.style.cssText = 'display:inline-grid;overflow:hidden;vertical-align:top;max-width:100%';
    a.style.cssText = b.style.cssText = 'grid-area:1/1';
    a.innerHTML = el.innerHTML;
    b.innerHTML = html;
    box.appendChild(a); box.appendChild(b);
    el.innerHTML = '';
    el.appendChild(box);
    var end = function () { if (el._rollEnd !== end) return; el._rollEnd = null; el.innerHTML = html; };
    el._rollEnd = end;
    settle(rollPair(a, b, up), 600).then(end);
  }
  /* An <input> cannot hold moving text, so the roll is drawn on a copy laid over it while
     the input's own digits are transparent. The value itself changes at once. */
  function rollInput(input, to, up, animate) {
    if (!input) return;
    if (input._rollEnd) input._rollEnd();
    var from = input.value;
    input.value = to;
    if (!animate || String(from) === String(to) || cartMotion().qty !== 'roll' || !input.offsetWidth) return;
    var box = relPos(input.parentNode);
    var r = input.getBoundingClientRect(), br = box.getBoundingClientRect(), cs = getComputedStyle(input);
    var ov = document.createElement('span'), a = document.createElement('span'), b = document.createElement('span');
    var align = /center/.test(cs.textAlign) ? 'center' : (/right|end/.test(cs.textAlign) ? 'end' : 'start');
    ov.className = 'cart-motion-tmp';
    ov.setAttribute('aria-hidden', 'true');
    ov.style.cssText = 'position:absolute;pointer-events:none;overflow:hidden;display:grid;align-items:center;justify-items:' + align + ';' +
      'box-sizing:border-box;padding:0 ' + cs.paddingRight + ' 0 ' + cs.paddingLeft + ';' +
      'left:' + (r.left - br.left - box.clientLeft) + 'px;top:' + (r.top - br.top - box.clientTop) + 'px;' +
      'width:' + r.width + 'px;height:' + r.height + 'px;font-family:' + cs.fontFamily + ';font-size:' + cs.fontSize + ';' +
      'font-weight:' + cs.fontWeight + ';letter-spacing:' + cs.letterSpacing + ';color:' + cs.color;
    a.style.cssText = b.style.cssText = 'grid-area:1/1';
    a.textContent = from;
    b.textContent = to;
    ov.appendChild(a); ov.appendChild(b);
    box.appendChild(ov);
    input.style.color = 'transparent';
    var end = function () { if (input._rollEnd !== end) return; input._rollEnd = null; ov.remove(); input.style.color = ''; };
    input._rollEnd = end;
    settle(rollPair(a, b, up), 600).then(end);
  }
  /* The picture hop. On + a copy of the picture sits behind it and slides out a little,
     like a second one being stacked; on − the picture squashes slightly. */
  function pictureHop(media, up) {
    if (!media || !media.offsetWidth) return;
    if (media._hop) return;   /* one at a time; quick presses do not stack hops */
    var parts = [];
    if (up) {
      var host = relPos(media.parentNode);
      var mr = media.getBoundingClientRect(), hr = host.getBoundingClientRect();
      var g = media.cloneNode(true);
      g.classList.add('cart-motion-tmp');
      g.removeAttribute('href');
      g.setAttribute('aria-hidden', 'true');
      g.setAttribute('tabindex', '-1');
      g.style.cssText = 'position:absolute;margin:0;pointer-events:none;z-index:0;box-sizing:border-box;' +
        'left:' + (mr.left - hr.left - host.clientLeft) + 'px;top:' + (mr.top - hr.top - host.clientTop) + 'px;width:' + mr.width + 'px;height:' + mr.height + 'px';
      host.insertBefore(g, media);
      var pos = media.style.position, z = media.style.zIndex;
      if (getComputedStyle(media).position === 'static') media.style.position = 'relative';
      media.style.zIndex = '1';
      parts.push(settle(g.animate([{ transform: 'none', opacity: .9 }, { transform: 'translate(6px,-6px)', opacity: .5, offset: .45 }, { transform: 'none', opacity: 0 }],
        { duration: 600, easing: 'ease-out' }), 900).then(function () { g.remove(); media.style.position = pos; media.style.zIndex = z; }));
      parts.push(settle(media.animate([{ transform: 'none' }, { transform: 'translateY(-8px) scale(1.04)' }, { transform: 'none' }],
        { duration: 420, easing: 'cubic-bezier(.3,1.5,.5,1)' }), 700));
    } else {
      var origin = media.style.transformOrigin;
      media.style.transformOrigin = '50% 100%';
      parts.push(settle(media.animate([{ transform: 'none' }, { transform: 'scale(1.06,.9)' }, { transform: 'none' }],
        { duration: 380, easing: 'ease-out' }), 700).then(function () { media.style.transformOrigin = origin; }));
    }
    media._hop = true;
    Promise.all(parts).then(function () { media._hop = false; });
  }
  function floatChip(stepper, btn, up) {
    if (!stepper) return;
    relPos(stepper);
    var f = document.createElement('span');
    f.className = 'cart-motion-tmp';
    f.textContent = up ? '+1' : '\u22121';
    f.setAttribute('aria-hidden', 'true');
    f.style.cssText = 'position:absolute;left:50%;top:0;z-index:3;pointer-events:none;font-size:13px;font-weight:600;line-height:1;' +
      'color:' + cssVar(stepper, '--color-accent', 'currentColor');
    stepper.appendChild(f);
    settle(f.animate([{ transform: 'translate(-50%,0)', opacity: 0 }, { transform: 'translate(-50%,-14px)', opacity: 1, offset: .25 },
      { transform: 'translate(-50%,-34px)', opacity: 0 }], { duration: 800, easing: 'ease-out', fill: 'forwards' }), 1100)
      .then(function () { f.remove(); });
    if (btn) btn.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.18)' }, { transform: 'scale(1)' }], { duration: 260, easing: 'ease-out' });
  }
  function shake(el) {
    if (!el || cartMotion().still) return;
    el.animate([{ transform: 'none' }, { transform: 'translateX(-5px)' }, { transform: 'translateX(5px)' },
      { transform: 'translateX(-3px)' }, { transform: 'none' }], { duration: 360, easing: 'ease-in-out' });
  }
  /* A note over the stepper (absolutely placed, so the line's alignment never moves). */
  function qtyNote(stepper, text) {
    if (!stepper) return;
    relPos(stepper);
    var p = stepper.querySelector('.cart-qty-note');
    if (!p) {
      p = document.createElement('span');
      p.className = 'cart-qty-note';
      p.setAttribute('role', 'status');
      stepper.appendChild(p);
    }
    p.textContent = text;
    clearTimeout(p._t);
    p._t = setTimeout(function () { p.remove(); }, 4000);
  }

  /* -- the two places a cart can be changed -- */
  function cartPageSid() {
    var page = document.querySelector('.cart-page[data-cart-page]');
    var s = page && page.closest('[id^="shopify-section-"]');
    return s ? s.id.replace('shopify-section-', '') : null;
  }
  var cartViews = {
    drawer: {
      row: function (key) {
        var q = document.querySelector('.cart-drawer .cart-qty[data-line-key="' + keySel(key) + '"]');
        return q && q.closest('.cart-line');
      },
      input: function (row) { return row.querySelector('[data-drawer-qty]'); },
      stepper: function (row) { return row.querySelector('.cart-qty'); },
      media: function (row) { return row.querySelector('.cart-line__media'); },
      price: function (row) { return row.querySelector('.cart-line__price'); },
      trash: function (row) { return row.querySelector('.cart-line__remove'); },
      host: function (row) { return row.parentNode; },
      flyHost: function () { return drawer || document.body; },
      send: function (key, qty) { return postCart('/cart/change.js', { id: key, quantity: qty }); },
      apply: function (res, animate) { renderCart(res.body, { animate: animate }); },
      /* Re-read the cart and bring every line back to it: patched in place (which also
         resets a number the shopper typed), or rebuilt when `hard` (the lines changed shape). */
      reload: function (hard) { renderedLinesKey = null; if (hard) renderedItems = null; refreshCart(false); }
    },
    page: {
      row: function (key) { return document.querySelector('.cart-page tr[data-line-key="' + keySel(key) + '"]'); },
      input: function (row) { return row.querySelector('[data-cart-qty]'); },
      stepper: function (row) { return row.querySelector('.cart-qty'); },
      media: function (row) { return row.querySelector('.cart-item__media'); },
      price: function (row) { return row.querySelector('.cart-line-total'); },
      trash: function (row) { return row.querySelector('.cart-item__remove'); },
      host: function (row) { return row.closest('form') || row.closest('.cart-page'); },
      flyHost: function () { return document.body; },
      send: function (key, qty) {
        var sid = cartPageSid();
        return postCart('/cart/change.js', { id: key, quantity: qty, sections: [sid], sections_url: location.pathname });
      },
      apply: function (res, animate) {
        var sid = cartPageSid();
        renderCart(res.body, { fromPage: true });   /* the drawer and the header count */
        pageApply(res.body.sections && res.body.sections[sid], { animate: animate });
      },
      reload: function () {
        pageRefresh();
        fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) { renderCart(c, { fromPage: true }); }).catch(function () {});
      }
    }
  };

  /* -- quantity -- */
  var lineState = {};
  function lineSt(key) {
    return lineState[key] || (lineState[key] = { target: null, confirmed: null, timer: 0, busy: false, again: false, removing: false, inflight: null });
  }
  function linePending(key) {
    var s = lineState[key];
    return !!(s && (s.timer || s.busy || s.removing));
  }
  function qtyChange(view, key, next, opts) {
    opts = opts || {};
    var s = lineSt(key), row = view.row(key);
    if (!row || s.removing) return;
    var input = view.input(row);
    if (!input) return;
    if (s.confirmed === null) s.confirmed = parseInt(input.defaultValue, 10) || 0;
    var shown = s.target === null ? s.confirmed : s.target;
    if (isNaN(next) || next < 0) next = 0;
    if (next === 0) { removeLine(view, key); return; }
    if (next === shown) { input.value = next; return; }
    var up = next > shown, cfg = cartMotion();
    s.target = next;
    if (opts.typed) input.value = next; else rollInput(input, next, up, true);
    if (cfg.qty === 'float') floatChip(view.stepper(row), opts.btn, up);
    if (cfg.qty !== 'none') pictureHop(view.media(row), up);
    var price = view.price(row);
    if (price) { price.style.transition = 'opacity .2s'; price.style.opacity = '.45'; }
    clearTimeout(s.timer);
    s.timer = setTimeout(function () { s.timer = 0; qtyFlush(view, key); }, opts.typed ? 0 : 350);
  }
  function qtyFlush(view, key) {
    var s = lineSt(key);
    if (s.removing) return;
    if (s.busy) { s.again = true; return; }
    var q = s.target;
    if (q === null) return;
    s.busy = true;
    var p = view.send(key, q).then(function (res) {
      s.busy = false;
      s.inflight = null;
      if (s.removing) return;
      var row = view.row(key), input = row && view.input(row), stepper = row && view.stepper(row);
      var unDim = function () { var pr = row && view.price(row); if (pr) pr.style.opacity = ''; };
      if (!res.ok) {
        var msg = (res.body && (res.body.description || res.body.message)) || '';
        if (res.status === 422 || res.status === 400) {
          /* Refused or cut down (usually stock). Shopify may still have changed the line —
             asked for 9999 it answers 422 "Only 50 items were added…" and the cart holds
             50 — so the cart is re-read and the number goes to what it really holds. */
          s.target = null;
          unDim();
          shake(stepper);
          qtyNote(stepper, typeof msg === 'string' && msg ? msg : 'That quantity is not available.');
          view.reload();
          return;
        }
        throw new Error('change ' + res.status);
      }
      var cart = res.body, item = findItem(cart, key);
      var newer = s.timer || (s.again && s.target !== q);
      s.again = false;
      if (!item) { s.target = null; view.reload(true); return; }   /* the line changed shape (rare): re-read everything */
      s.confirmed = item.quantity;
      if (newer) {
        /* More presses came in while this was away: send them; this line keeps showing
           the shopper's number (the patch skips pending lines). */
        if (!s.timer) qtyFlush(view, key);
      } else {
        if (item.quantity < q) {
          /* Shopify added fewer than asked (stock). */
          rollInput(input, item.quantity, false, true);
          shake(stepper);
          qtyNote(stepper, 'Only ' + item.quantity + ' available.');
        }
        s.target = null;
      }
      if (freshest(res)) view.apply(res, cartMotion().qty !== 'none');
      if (!newer) unDim();
    }).catch(function () {
      s.busy = false;
      s.inflight = null;
      s.target = null;
      var row = view.row(key);
      if (row) {
        rollInput(view.input(row), s.confirmed, false, true);
        var pr = view.price(row); if (pr) pr.style.opacity = '';
        shake(view.stepper(row));
        qtyNote(view.stepper(row), 'Could not update. Please try again.');
      }
      view.reload();
    });
    s.inflight = p;
  }

  /* -- removing -- */
  function cellsOf(row) { return row.tagName === 'TR' ? Array.prototype.slice.call(row.cells) : [row]; }
  function fadeCells(row, kf, o) {
    return Promise.all(cellsOf(row).map(function (c) { return settle(c.animate(kf, o), o.duration + 400); }));
  }
  /* What a shard shows: a copy of the row at its exact size. A table row only lays out
     inside a table, so it is copied inside a one-row copy of its table with the column
     widths pinned. Inputs are copied with their current numbers. */
  function shardSource(row, r) {
    var c;
    if (row.tagName === 'TR') {
      var table = row.closest('table'), tr = table.getBoundingClientRect();
      var t = table.cloneNode(false), tb = document.createElement('tbody'), rc = row.cloneNode(true);
      t.removeAttribute('id');
      if (getComputedStyle(row).display === 'table-row') {
        t.style.tableLayout = 'fixed';
        Array.prototype.forEach.call(row.cells, function (cell, i) { rc.cells[i].style.width = cell.getBoundingClientRect().width + 'px'; });
      }
      tb.appendChild(rc); t.appendChild(tb);
      t.style.position = 'absolute'; t.style.margin = '0'; t.style.width = tr.width + 'px';
      t.style.left = (tr.left - r.left) + 'px'; t.style.top = '0';
      c = t;
    } else {
      c = row.cloneNode(true);
      c.style.position = 'absolute'; c.style.margin = '0'; c.style.left = '0'; c.style.top = '0';
      c.style.width = r.width + 'px'; c.style.height = r.height + 'px'; c.style.boxSizing = 'border-box';
    }
    /* A copy is the only row of its table (or list), so ":last-child" rules would drop its
       divider; the row's own borders are carried over. */
    var rcs = getComputedStyle(row), rowCopy = row.tagName === 'TR' ? c.querySelector('tr') : c;
    if (rowCopy) { rowCopy.style.borderTop = rcs.borderTopWidth + ' ' + rcs.borderTopStyle + ' ' + rcs.borderTopColor; rowCopy.style.borderBottom = rcs.borderBottomWidth + ' ' + rcs.borderBottomStyle + ' ' + rcs.borderBottomColor; }
    var ins = row.querySelectorAll('input'), cins = c.querySelectorAll('input');
    Array.prototype.forEach.call(cins, function (x, i) { if (ins[i]) x.setAttribute('value', ins[i].value); x.style.color = ''; });
    Array.prototype.forEach.call(c.querySelectorAll('.cart-motion-tmp, .cart-qty-note'), function (x) { x.remove(); });
    /* Copies must never be found as lines: no keys, no ids, nothing focusable. */
    [c].concat(Array.prototype.slice.call(c.querySelectorAll('[data-line-key], [data-line-remove], [id]'))).forEach(function (x) {
      x.removeAttribute('data-line-key'); x.removeAttribute('data-line-remove'); x.removeAttribute('id');
    });
    Array.prototype.forEach.call(c.querySelectorAll('a, button, input'), function (x) { x.setAttribute('tabindex', '-1'); });
    c.style.visibility = 'visible';
    return c;
  }
  /* Turn to ash: the row is cut into a grid of pieces (each a clipped copy of the row).
     They grey, then drift up and to the right, turning and shrinking, in a sweep from
     left to right, with fine dust between them. Pieces are larger on small rows, so a
     phone animates at most 18 × 6. */
  function ashRow(view, row) {
    var host = relPos(view.host(row));
    var r = row.getBoundingClientRect(), hr = host.getBoundingClientRect();
    if (!r.width || !r.height) { row.style.visibility = 'hidden'; return Promise.resolve(); }
    var src = shardSource(row, r);
    var ov = document.createElement('div');
    ov.className = 'cart-ash';
    ov.setAttribute('aria-hidden', 'true');
    ov.style.cssText = 'position:absolute;pointer-events:none;z-index:5;' +
      'left:' + Math.round(r.left - hr.left - host.clientLeft + host.scrollLeft) + 'px;top:' + Math.round(r.top - hr.top - host.clientTop + host.scrollTop) + 'px;' +
      'width:' + r.width + 'px;height:' + r.height + 'px';
    var cols = Math.max(8, Math.min(18, Math.round(r.width / 26))), rows = Math.max(3, Math.min(6, Math.round(r.height / 26)));
    var cw = r.width / cols, ch = r.height / rows, sweep = 520, life = 760, anims = [];
    for (var y = 0; y < rows; y++) {
      for (var x = 0; x < cols; x++) {
        /* Whole pixels, so the cuts between pieces never show as hairlines before they move. */
        var px = Math.round(x * cw), py = Math.round(y * ch);
        var pw = Math.round((x + 1) * cw) - px, ph = Math.round((y + 1) * ch) - py;
        var s = document.createElement('div');
        s.style.cssText = 'position:absolute;overflow:hidden;left:' + px + 'px;top:' + py + 'px;width:' + pw + 'px;height:' + ph + 'px';
        var piece = src.cloneNode(true);
        piece.style.left = (parseFloat(src.style.left) - px) + 'px';
        piece.style.top = (parseFloat(src.style.top) - py) + 'px';
        s.appendChild(piece);
        ov.appendChild(s);
        var ang = -(20 + Math.random() * 50) * Math.PI / 180, dist = 30 + Math.random() * 70;
        anims.push(s.animate([
          { transform: 'none', opacity: 1, filter: 'grayscale(0)' },
          { transform: 'translate(2px,-2px)', opacity: 1, filter: 'grayscale(1) sepia(.2) brightness(.92)', offset: .25 },
          { transform: 'translate(' + (Math.cos(ang) * dist).toFixed(1) + 'px,' + (Math.sin(ang) * dist).toFixed(1) + 'px) rotate(' + ((Math.random() - .5) * 90).toFixed(0) + 'deg) scale(.35)',
            opacity: 0, filter: 'grayscale(1) sepia(.2) brightness(.8)' }
        ], { duration: life, delay: x / cols * sweep + Math.random() * 160, easing: 'cubic-bezier(.3,.1,.6,1)', fill: 'both' }));
      }
    }
    var dust = cssVar(row, '--color-text', '#888');
    for (var i = 0; i < 28; i++) {
      var p = document.createElement('span'), sz = 1.5 + Math.random() * 2.5, x0 = Math.random() * r.width, y0 = Math.random() * r.height;
      p.style.cssText = 'position:absolute;border-radius:50%;opacity:0;left:' + x0 + 'px;top:' + y0 + 'px;width:' + sz + 'px;height:' + sz + 'px;background:' + dust;
      ov.appendChild(p);
      anims.push(p.animate([{ transform: 'none', opacity: 0 }, { opacity: .55, offset: .2 },
        { transform: 'translate(' + (20 + Math.random() * 80).toFixed(0) + 'px,' + (-30 - Math.random() * 70).toFixed(0) + 'px)', opacity: 0 }],
        { duration: 1000, delay: x0 / r.width * sweep, easing: 'ease-out', fill: 'both' }));
    }
    host.appendChild(ov);
    row.style.visibility = 'hidden';
    return settle({ finished: Promise.all(anims.map(function (a) { return a.finished; })) }, sweep + 160 + life + 600)
      .then(function () { ov.remove(); });
  }
  function binRow(view, row) {
    var media = view.media(row), trash = view.trash(row);
    if (!media || !trash || !media.offsetWidth || !trash.offsetWidth) return fadeCells(row, [{ opacity: 1 }, { opacity: 0 }], { duration: 220, fill: 'forwards' });
    var a = media.getBoundingClientRect(), b = trash.getBoundingClientRect();
    var c = media.cloneNode(true);
    c.removeAttribute('href');
    c.setAttribute('aria-hidden', 'true');
    c.style.cssText = 'position:fixed;margin:0;z-index:1000;pointer-events:none;box-sizing:border-box;transform-origin:center;' +
      'left:' + a.left + 'px;top:' + a.top + 'px;width:' + a.width + 'px;height:' + a.height + 'px';
    view.flyHost().appendChild(c);
    media.style.visibility = 'hidden';
    var dx = b.left + b.width / 2 - (a.left + a.width / 2), dy = b.top + b.height / 2 - (a.top + a.height / 2);
    return settle(c.animate([
      { transform: 'translate(0,0) scale(1) rotate(0)', opacity: 1 },
      { transform: 'translate(' + (dx / 2).toFixed(1) + 'px,' + (Math.min(0, dy) - 50).toFixed(1) + 'px) scale(.7) rotate(12deg)', opacity: 1, offset: .5 },
      { transform: 'translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px) scale(.12) rotate(30deg)', opacity: .6 }
    ], { duration: 620, easing: 'cubic-bezier(.45,0,.2,1)', fill: 'forwards' }), 1000).then(function () {
      c.remove();
      trash.animate([{ transform: 'rotate(0)' }, { transform: 'rotate(-14deg)' }, { transform: 'rotate(10deg)' }, { transform: 'rotate(0)' }], { duration: 380 });
      return fadeCells(row, [{ opacity: 1 }, { opacity: 0 }], { duration: 220, delay: 120, fill: 'forwards' });
    });
  }
  function playRemove(view, row, mode) {
    if (mode === 'ash') return ashRow(view, row);
    if (mode === 'slide') return fadeCells(row, [{ transform: 'none', opacity: 1 }, { transform: 'translateX(60%)', opacity: 0 }],
      { duration: 340, easing: 'cubic-bezier(.5,0,.75,0)', fill: 'forwards' });
    if (mode === 'bin') return binRow(view, row);
    if (mode === 'fold') return fadeCells(row, [{ transform: 'none', opacity: 1 }, { transform: 'scaleY(.6)', opacity: 0 }],
      { duration: 240, easing: 'ease-in', fill: 'forwards' });
    row.style.visibility = 'hidden';
    return Promise.resolve();
  }
  function unplayRemove(view, row) {
    cellsOf(row).forEach(function (c) { if (c.getAnimations) c.getAnimations().forEach(function (a) { a.cancel(); }); });
    row.style.visibility = '';
    row.style.pointerEvents = '';
    row.removeAttribute('aria-hidden');
    var m = view.media(row); if (m) m.style.visibility = '';
    if (!cartMotion().still) fadeCells(row, [{ opacity: 0 }, { opacity: 1 }], { duration: 250 });
  }
  /* The removed row's place: a plain spacer of the same height (a <tr> with one wide
     cell on the cart page), which then closes or holds the Undo bar. Spacers carry
     .cart-motion-row so the patching below never mistakes them for lines. */
  function rowSpacer(row, afterKey) {
    if (!row || !row.parentNode) return null;
    var h = row.offsetHeight, el, inner;   /* layout height: a transform (fold) does not count */
    if (row.tagName === 'TR') {
      el = document.createElement('tr');
      var td = document.createElement('td');
      td.colSpan = row.cells.length || 4;
      td.style.cssText = 'padding:0;border:0';
      if (getComputedStyle(row).display !== 'table-row') {
        el.style.cssText = 'display:block;padding:0;border:0;margin:0';
        td.style.cssText = 'display:block;padding:0;border:0;width:auto';
      }
      inner = document.createElement('div');
      td.appendChild(inner);
      el.appendChild(td);
    } else {
      el = inner = document.createElement('div');
    }
    el.className = 'cart-motion-row';
    el.setAttribute('data-after', afterKey || '');
    inner.style.height = h + 'px';
    inner.style.overflow = 'hidden';
    row.parentNode.replaceChild(el, row);
    return { el: el, inner: inner, h: h };
  }
  function closeSpacer(sp) {
    if (!sp) return;
    var h = sp.inner.getBoundingClientRect().height;
    if (calmRemove() || !h) { sp.el.remove(); return; }
    settle(sp.inner.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 300, easing: 'ease', fill: 'forwards' }), 700)
      .then(function () { sp.el.remove(); });
  }
  function rowKeyBefore(view, row) {
    var p = row.previousElementSibling;
    while (p && !(p.matches('.cart-line, tr[data-line-key]'))) p = p.previousElementSibling;
    if (!p) return '';
    if (p.tagName === 'TR') return p.getAttribute('data-line-key') || '';
    var q = p.querySelector('.cart-qty[data-line-key]');
    return q ? q.getAttribute('data-line-key') : '';
  }
  function removeLine(view, key, keyboard) {
    var s = lineSt(key);
    if (s.removing) return;
    var row = view.row(key);
    if (!row) return;
    s.removing = true;
    clearTimeout(s.timer); s.timer = 0;
    var cfg = cartMotion();
    var item = findItem(cartSnapshot, key);
    var titleEl = row.querySelector('.cart-line__title, .cart-item__title');
    var title = item ? item.product_title : (titleEl ? titleEl.textContent.trim() : '');
    var after = rowKeyBefore(view, row);
    row.setAttribute('aria-hidden', 'true');
    row.style.pointerEvents = 'none';
    var gone = playRemove(view, row, cfg.remove);
    /* A quantity change still on its way goes first, so Shopify sees them in order. */
    var req = (s.inflight || Promise.resolve()).catch(function () {}).then(function () { return view.send(key, 0); });
    Promise.all([gone, req.catch(function () { return null; })]).then(function (out) {
      var res = out[1];
      if (!res || !res.ok) {
        s.removing = false;
        unplayRemove(view, row);
        var st = view.stepper(row);
        var why = res && res.status === 422 && res.body && res.body.description;
        qtyNote(st, typeof why === 'string' && why ? why : 'Could not remove it. Please try again.');
        view.reload();
        return;
      }
      delete lineState[key];
      var sp = rowSpacer(row, after);
      if (!sp) { if (freshest(res)) view.apply(res, cfg.remove !== 'none'); return; }   /* already gone (a rebuild) */
      if (cfg.undo && item) undoBar(view, sp, item, title, keyboard);
      else closeSpacer(sp);
      if (freshest(res)) view.apply(res, cfg.remove !== 'none');
    });
  }

  /* -- undo -- */
  function undoBar(view, sp, item, title, keyboard) {
    var bar = document.createElement('div');
    bar.className = 'cart-undo';
    bar.setAttribute('role', 'status');
    bar.innerHTML = '<span class="cart-undo__text">Removed <span class="cart-undo__name">' + escapeHtml(title) + '</span></span>' +
      '<button type="button" class="cart-undo__btn">Undo</button><span class="cart-undo__time" aria-hidden="true"></span>';
    sp.inner.innerHTML = '';
    sp.inner.appendChild(bar);
    sp.el.classList.add('cart-motion-row--undo');
    bar._sp = sp;
    var bcs = getComputedStyle(bar);
    var to = bar.offsetHeight + (parseFloat(bcs.marginTop) || 0) + (parseFloat(bcs.marginBottom) || 0);
    if (calmRemove()) { sp.inner.style.height = ''; }
    else {
      settle(sp.inner.animate([{ height: sp.h + 'px' }, { height: to + 'px' }], { duration: 300, easing: 'ease' }), 700)
        .then(function () { sp.inner.style.height = ''; });
      bar.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 250, delay: 120, fill: 'backwards' });
    }
    /* 5 seconds, paused while the pointer or keyboard focus is on the bar. The line under
       it is the visible timer (still under reduced motion: it only shrinks). */
    var tl = bar.querySelector('.cart-undo__time');
    var timer = cartMotion().still ? null : tl.animate([{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }], { duration: 5000, easing: 'linear', fill: 'forwards' });
    var left = 5000, started = Date.now(), t = null, over = false, focus = false;
    var expire = function () { if (sp.done) return; sp.done = true; closeSpacer(sp); };
    var run = function () { started = Date.now(); clearTimeout(t); t = setTimeout(expire, left); if (timer) timer.play(); };
    var hold = function () { left -= Date.now() - started; clearTimeout(t); if (timer) timer.pause(); };
    var sync = function () { if (over || focus) hold(); else run(); };
    bar.addEventListener('mouseenter', function () { over = true; sync(); });
    bar.addEventListener('mouseleave', function () { over = false; sync(); });
    bar.addEventListener('focusin', function () { focus = true; sync(); });
    bar.addEventListener('focusout', function () { focus = false; sync(); });
    run();
    var btn = bar.querySelector('.cart-undo__btn');
    /* Removed from the keyboard: focus moves to Undo (the removed button is gone). The
       bar then waits while it has focus, so it is never pulled away mid-decision. */
    if (keyboard) try { btn.focus({ preventScroll: true }); } catch (e) { btn.focus(); }
    btn.addEventListener('click', function () {
      if (sp.done) return;
      clearTimeout(t);
      if (timer) timer.pause();
      btn.disabled = true;
      btn.textContent = 'Restoring\u2026';
      var line = { id: item.variant_id, quantity: item.quantity };
      var props = {}, any = false;
      Object.keys(item.properties || {}).forEach(function (k) {
        var v = item.properties[k];
        if (v !== null && v !== undefined && v !== '') { props[k] = v; any = true; }
      });
      if (any) line.properties = props;
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) line.selling_plan = item.selling_plan_allocation.selling_plan.id;
      var body = { items: [line] }, sid = cartPageSid();
      if (view === cartViews.page && sid) { body.sections = [sid]; body.sections_url = location.pathname; }
      postCart('/cart/add.js', body).then(function (res) {
        if (!res.ok) throw res;
        sp.done = true;
        sp.el.remove();
        var newKey = res.body.items && res.body.items[0] && res.body.items[0].key;
        if (view === cartViews.page) {
          var html = res.body.sections && res.body.sections[sid];
          if (!document.querySelector('.cart-page form') || !html) { location.reload(); return; }
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
            renderCart(c, { fromPage: true });
            pageApply(html, { intro: newKey });
          }).catch(function () { pageApply(html, { intro: newKey }); });
        } else {
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
            renderCart(c, { animate: true });
            introLine(cartViews.drawer.row(newKey));
          });
        }
      }).catch(function (res) {
        var d = res && res.body && res.body.description;
        bar.querySelector('.cart-undo__text').textContent = typeof d === 'string' && d ? d : 'Could not put it back.';
        btn.remove();
        left = 3000; run();
        view.reload();   /* Shopify may have put back part of it */
      });
    });
  }
  function introLine(row) {
    if (!row || calmRemove()) return;
    var h = row.getBoundingClientRect().height;
    if (row.tagName === 'TR') { fadeCells(row, [{ opacity: 0, transform: 'translateY(-8px)' }, { opacity: 1, transform: 'none' }], { duration: 350, easing: 'ease' }); return; }
    row.style.overflow = 'hidden';
    settle(row.animate([{ height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }], { duration: 380, easing: 'ease' }), 800)
      .then(function () { row.style.overflow = ''; });
  }

  /* -- the drawer: one listener for every line, whenever it was drawn -- */
  if (drawer) {
    drawer.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-drawer-qty-up], [data-drawer-qty-down], [data-line-remove]') : null;
      if (!b || !drawer.contains(b)) return;
      if (b.hasAttribute('data-line-remove')) { removeLine(cartViews.drawer, b.getAttribute('data-line-remove'), e.detail === 0); return; }
      var q = b.closest('.cart-qty[data-line-key]');
      if (!q) return;
      var key = q.getAttribute('data-line-key'), s = lineSt(key), input = q.querySelector('[data-drawer-qty]');
      var cur = s.target !== null ? s.target : (parseInt(input && input.value, 10) || 0);
      qtyChange(cartViews.drawer, key, cur + (b.hasAttribute('data-drawer-qty-up') ? 1 : -1), { btn: b });
    });
    drawer.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.matches || !input.matches('[data-drawer-qty]')) return;
      var q = input.closest('.cart-qty[data-line-key]');
      if (q) qtyChange(cartViews.drawer, q.getAttribute('data-line-key'), parseInt(input.value, 10), { typed: true });
    });
  }

  /* -- the cart page -- */
  var pageShownSig = null, pageRefreshT = 0;
  function cartSig(c) {
    return (c.items || []).map(function (it) { return it.key + 'x' + it.quantity + '=' + it.final_line_price; }).join(',') + '|' + c.total_price;
  }
  function pageRefresh() {
    var sid = cartPageSid();
    if (!sid) return;
    fetch(location.pathname + '?section_id=' + encodeURIComponent(sid), { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('cart section ' + r.status); return r.text(); })
      .then(function (html) { pageApply(html, { animate: true }); })
      .catch(function () {});
  }
  function pageKeys(scope) {
    return Array.prototype.map.call(scope.querySelectorAll('tr[data-line-key]'), function (tr) { return tr.getAttribute('data-line-key'); });
  }
  /* Bring the cart page in line with freshly rendered section HTML. Lines are patched in
     place when the set of lines is the same or only lost some; otherwise the rows are
     replaced (Undo bars kept where they were). The summary's rows are patched; the
     survey, the messages and the express checkout buttons are never touched. */
  function pageApply(html, opts) {
    opts = opts || {};
    var page = document.querySelector('.cart-page[data-cart-page]');
    if (!page || !html) return;
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    var np = tpl.content.querySelector('.cart-page');
    if (!np) return;
    var form = page.querySelector('form'), nform = np.querySelector('form');
    if (cartSnapshot) pageShownSig = cartSig(cartSnapshot);
    if (!nform) {
      /* Now empty: show the empty page; any Undo bars stay on top of it. */
      var bars = Array.prototype.slice.call(page.querySelectorAll('.cart-undo'));
      page.innerHTML = np.innerHTML;
      var empty = page.querySelector('.cart-page__empty');
      bars.forEach(function (bar) {
        var holder = document.createElement('div');
        holder.className = 'cart-motion-row cart-motion-row--undo';
        holder.appendChild(bar);
        if (empty) empty.parentNode.insertBefore(holder, empty);
        if (bar._sp) { bar._sp.el = holder; bar._sp.inner = holder; }
      });
      return;
    }
    if (!form) { location.reload(); return; }
    var tbody = form.querySelector('.cart-table tbody'), ntbody = nform.querySelector('.cart-table tbody');
    if (!tbody || !ntbody) return;
    var cur = pageKeys(tbody), next = pageKeys(ntbody);
    /* Patched in place when the lines kept their order; lines new to the cart (an add, an
       Undo) are inserted from the fresh HTML where they belong — at the bottom for an add —
       so the other rows never jump or reload their pictures (v249). */
    var kept = next.filter(function (k) { return cur.indexOf(k) > -1; });
    var inOrder = kept.every(function (k, i) { return i === 0 || cur.indexOf(k) > cur.indexOf(kept[i - 1]); });
    if (inOrder) {
      next.forEach(function (k, i) {
        if (cur.indexOf(k) > -1) return;
        var nrow = ntbody.querySelector('tr[data-line-key="' + keySel(k) + '"]');
        if (!nrow) return;
        var row = nrow.cloneNode(true), before = i > 0 ? cartViews.page.row(next[i - 1]) : null;
        if (before && before.parentNode === tbody) {
          var ref = before.nextSibling;
          while (ref && ref.classList && ref.classList.contains('cart-motion-row')) ref = ref.nextSibling;
          tbody.insertBefore(row, ref);
        } else if (i === 0) tbody.insertBefore(row, tbody.firstChild);
        else tbody.appendChild(row);
        lineSt(k).confirmed = parseInt((row.querySelector('[data-cart-qty]') || {}).value, 10) || 0;
        if (opts.animate || opts.intro) introLine(row);
      });
      cur.forEach(function (k) {
        if (next.indexOf(k) > -1 || (lineState[k] && lineState[k].removing)) return;
        var gone = cartViews.page.row(k);
        if (gone) closeSpacer(rowSpacer(gone, ''));
      });
      next.forEach(function (k) {
        var row = cartViews.page.row(k), nrow = ntbody.querySelector('tr[data-line-key="' + keySel(k) + '"]');
        if (!row || !nrow) return;
        var pending = linePending(k);
        var input = row.querySelector('[data-cart-qty]'), ninput = nrow.querySelector('[data-cart-qty]');
        if (input && ninput) {
          var nq = ninput.getAttribute('value');
          input.setAttribute('value', nq);
          lineSt(k).confirmed = parseInt(nq, 10);
          if (!pending && String(input.value) !== String(nq)) rollInput(input, nq, parseInt(nq, 10) > parseInt(input.value, 10), opts.animate);
        }
        if (pending) return;
        var cells = [['td[data-label="Price"]', true], ['.cart-line-total', false]];
        cells.forEach(function (c) {
          var a = row.querySelector(c[0]), b = nrow.querySelector(c[0]);
          if (a && b) {
            var up = moneyOf(b.textContent) >= moneyOf(a.textContent);
            rollSwap(a, b.innerHTML, up, opts.animate);
            a.style.opacity = '';
          }
        });
        var info = row.querySelector('.cart-item__info'), ninfo = nrow.querySelector('.cart-item__info');
        if (info && ninfo) {
          Array.prototype.forEach.call(info.querySelectorAll('.cart-item__discount'), function (d) { d.remove(); });
          Array.prototype.forEach.call(ninfo.querySelectorAll('.cart-item__discount'), function (d) { info.appendChild(d.cloneNode(true)); });
        }
      });
    } else {
      var keep = Array.prototype.slice.call(tbody.querySelectorAll(':scope > .cart-motion-row--undo'));
      tbody.innerHTML = ntbody.innerHTML;
      keepMotionRows(tbody, keep, function (k) { return cartViews.page.row(k); });
      if (opts.intro) introLine(cartViews.page.row(opts.intro));
    }
    /* Summary: discount and "You save" rows replaced, the subtotal rolled. */
    var sum = form.querySelector('.cart-summary'), nsum = nform.querySelector('.cart-summary');
    if (sum && nsum) {
      var total = sum.querySelector('.cart-summary__total'), ntotal = nsum.querySelector('.cart-summary__total');
      Array.prototype.forEach.call(sum.querySelectorAll('.cart-summary__row:not(.cart-summary__total)'), function (r) { r.remove(); });
      Array.prototype.forEach.call(nsum.querySelectorAll('.cart-summary__row:not(.cart-summary__total)'), function (r) {
        if (total) total.parentNode.insertBefore(r.cloneNode(true), total);
      });
      if (total && ntotal) {
        var a = total.lastElementChild, b = ntotal.lastElementChild;
        if (a && b) rollSwap(a, b.innerHTML, moneyOf(b.textContent) >= moneyOf(a.textContent), opts.animate);
      }
    }
  }
  function moneyOf(t) { var n = parseFloat(String(t).replace(/[^0-9.]/g, '')); return isNaN(n) ? 0 : n; }
  /* Undo bars survive a rebuilt list: each goes back after the line it followed. */
  function keepMotionRows(container, rows, rowOf) {
    rows.forEach(function (el) {
      var after = el.getAttribute('data-after'), ref = after ? rowOf(after) : null;
      if (ref && ref.parentNode === container) container.insertBefore(el, ref.nextSibling);
      else container.insertBefore(el, container.firstChild);
    });
  }
  (function () {
    var page = document.querySelector('.cart-page[data-cart-page]');
    if (!page) return;
    var sid = cartPageSid();
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      if (!cartSnapshot) cartSnapshot = c;
      pageShownSig = cartSig(c);
    }).catch(function () {});
    /* Without a section id (not expected) the page keeps its old behaviour: every change
       submits the form and the page reloads. */
    var plain = function (input) { var f = input.form; if (f) { var u = f.querySelector('[name="update"]'); if (u) u.click(); } };
    document.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('.cart-page [data-qty-up], .cart-page [data-qty-down], .cart-page .cart-item__remove') : null;
      if (!b) return;
      var row = b.closest('tr[data-line-key]');
      if (!row) return;
      var key = row.getAttribute('data-line-key');
      if (b.classList.contains('cart-item__remove')) {
        if (!sid) { b.classList.add('is-removing'); return; }   /* the link reloads the page */
        e.preventDefault();
        removeLine(cartViews.page, key, e.detail === 0);
        return;
      }
      var input = row.querySelector('[data-cart-qty]'), s = lineSt(key);
      var cur = s.target !== null ? s.target : (parseInt(input && input.value, 10) || 0);
      var next = cur + (b.hasAttribute('data-qty-up') ? 1 : -1);
      if (!sid) { if (next >= 0) { input.value = next; plain(input); } return; }
      qtyChange(cartViews.page, key, next, { btn: b });
    });
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.matches || !input.matches('.cart-page [data-cart-qty]')) return;
      var row = input.closest('tr[data-line-key]');
      if (!sid || !row) { plain(input); return; }
      qtyChange(cartViews.page, row.getAttribute('data-line-key'), parseInt(input.value, 10), { typed: true });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || !e.target.matches || !e.target.matches('.cart-page [data-cart-qty]')) return;
      e.preventDefault();   /* Enter would submit the form through its first button: Check out */
      e.target.blur();
    });
  })();

  /* Fetched through the Section Rendering API (sections/drawer-reco-data.liquid) rather
     than /recommendations/products.json, because only Liquid can read a product's
     purchase mode and purchase requirements: each product arrives with direct (may it
     be added from here?) and show_price. If that section cannot be read, the plain JSON
     endpoint is the fallback and every product it returns is treated as link-only —
     a failure must never put an Add to cart button on a consult-only product or one
     whose certifications live on its product page. */
  function fetchRecoFor(productId) {
    if (recoCache[productId]) return Promise.resolve(recoCache[productId]);
    var q = '?product_id=' + productId + '&limit=6&intent=related';
    return fetch('/recommendations/products' + q + '&section_id=drawer-reco-data')
      .then(function (r) { if (!r.ok) throw new Error('reco section ' + r.status); return r.text(); })
      .then(function (html) {
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        var el = tpl.content.querySelector('[data-drawer-reco-json]');
        if (!el) throw new Error('reco section empty');
        return JSON.parse(el.textContent);
      })
      .catch(function () {
        return fetch('/recommendations/products.json' + q)
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            return ((data && data.products) || []).map(function (p) { p.direct = false; return p; });
          });
      })
      .then(function (products) {
        products = products || [];
        recoCache[productId] = products;
        return products;
      })
      /* A failed seed contributes nothing rather than rejecting the whole merge —
         one bad response should not empty the panel. */
      .catch(function () { return []; });
  }

  function loadDrawerReco() {
    /* An "add from You may also like" animation is playing (playRecoAdd): rebuilding the
       list now would pull the row out from under it. Run once the animation is done. */
    if (recoHold) { recoPending = true; return; }
    var panel = document.querySelector('[data-drawer-reco]');
    var list = document.querySelector('[data-drawer-reco-list]');
    if (!panel || !list) return;
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      if (!cart.items || !cart.items.length) { recoCartKey = null; hideDrawerReco(panel); return; }

      /* Distinct products, preserving /cart.js order (newest first). */
      var inCart = [];
      cart.items.forEach(function (i) {
        if (inCart.indexOf(i.product_id) === -1) inCart.push(i.product_id);
      });
      var seeds = inCart.slice(0, RECO_MAX_SEEDS);

      /* Keyed on the SET of products, sorted — so the list is rebuilt when the
         cart gains or loses a product, but not when a quantity changes or lines
         reorder. Neither affects what is recommended. */
      var cartKey = inCart.slice().sort().join(',');
      if (cartKey === recoCartKey && list.children.length) { showDrawerReco(panel); return; }

      return Promise.all(seeds.map(fetchRecoFor))
        .then(function (results) {
          /* Merge every seed's suggestions and rank by how many cart items
             suggested the same product, so the panel reflects the whole cart
             rather than a single line. Previously one product seeded the list and
             the rest of the cart only filtered it, which meant removing a middle
             item visibly changed nothing.

             Ordering: vote count first, then best position across the source
             lists, then whether the most recently added item suggested it. That
             last term keeps a nod to recency without letting it outrank a product
             that several cart items agree on. */
          var byId = {};
          results.forEach(function (products, seedIdx) {
            products.forEach(function (p, rank) {
              var e = byId[p.id];
              if (!e) e = byId[p.id] = { product: p, votes: 0, bestRank: rank, fromLastAdded: false };
              e.votes += 1;
              if (rank < e.bestRank) e.bestRank = rank;
              if (seeds[seedIdx] === lastAddedProductId) e.fromLastAdded = true;
            });
          });
          /* Collapse size variants that are sold as separate products. Merging
             pools from several seeds surfaces near-duplicates that a single seed
             never would: this catalogue lists "DermProtect Barrier Defense" and
             "DermProtect Barrier Defense - Travel Size 0.5oz" as distinct
             products, and both ranked in the top four — half the panel spent on
             one product line.

             The test is deliberately narrow: one normalised title being a PREFIX
             of another. That catches the size-suffix pattern without merging
             genuinely different products that merely share an opening, e.g.
             "X Serum - Brightening" vs "X Serum - Hydrating", where neither is a
             prefix of the other. Runs after the sort, so the better-ranked member
             of a family is the one kept.

             To turn this off, remove the .filter() that calls keepFirstOfFamily. */
          var keptKeys = [];
          var titleKey = function (p) {
            return String(p.title || '').toLowerCase()
              .replace(/[™®©]/g, '')   /* strip TM, (R), (C) */
              .replace(/\s+/g, ' ')
              .trim();
          };
          var keepFirstOfFamily = function (e) {
            var k = titleKey(e.product);
            if (!k) return true;   /* untitled: never treat as a family match */
            for (var i = 0; i < keptKeys.length; i++) {
              if (k.indexOf(keptKeys[i]) === 0 || keptKeys[i].indexOf(k) === 0) return false;
            }
            keptKeys.push(k);
            return true;
          };
          var items = Object.keys(byId)
            .map(function (k) { return byId[k]; })
            .filter(function (e) { return inCart.indexOf(e.product.id) === -1; })
            .sort(function (a, b) {
              return (b.votes - a.votes)
                || (a.bestRank - b.bestRank)
                || ((b.fromLastAdded ? 1 : 0) - (a.fromLastAdded ? 1 : 0));
            })
            .filter(keepFirstOfFamily)
            .slice(0, 4)
            .map(function (e) { return e.product; });
          if (!items.length) { recoCartKey = null; hideDrawerReco(panel); return; }
          recoCartKey = cartKey;
          list.innerHTML = items.map(function (p) {
            /* p.image: already sized by drawer-reco-data. featured_image: the JSON fallback. */
            var img = p.image || (p.featured_image ? p.featured_image.replace(/(\.[^.?]+)(\?|$)/, '_200x$1$2') : '');
            /* direct === false: consult only, or purchase requirements — link, never add. */
            var single = p.direct !== false && p.variants && p.variants.length === 1 && p.variants[0].available;
            var arrow = '<svg viewBox="0 0 14 10" aria-hidden="true" focusable="false"><path d="M1 5h11.4M9 1.4L12.6 5 9 8.6" stroke="currentColor" stroke-width="1.2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            /* Mobile icon control: the same bag icon used in the header + a small plus badge */
            var bagIcon = '<svg class="reco-add__bag" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M5 8h14l-1 12H6L5 8z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>'
              + '<span class="reco-add__plus"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><path d="M12 6v12M6 12h12"/></svg></span>';
            /* access_label: Request access (v228) — the same words as the product card. */
            var linkLabel = p.access_label ? escapeHtml(p.access_label) : (p.direct === false ? 'Learn more' : 'Choose options');
            /* p.login: Log in to buy (v243), a guest — the link goes to the login page
               (data-ltb; see "Log in to buy" below for where it returns). */
            var linkAttrs = p.login ? 'href="' + escapeHtml(document.documentElement.getAttribute('data-ltb-login') || '/account/login') + '" data-ltb' : 'href="' + p.url + '"';
            var action = single
              ? '<button type="button" class="reco-cta" data-reco-add="' + p.variants[0].id + '"><span class="reco-cta__label">Add to cart</span>' + arrow + '</button>'
                + '<button type="button" class="reco-add" data-reco-add="' + p.variants[0].id + '" aria-label="Add ' + p.title.replace(/"/g, '&quot;') + ' to cart">' + bagIcon + '</button>'
              : '<a class="reco-cta" ' + linkAttrs + '><span class="reco-cta__label">' + linkLabel + '</span>' + arrow + '</a>'
                + '<a class="reco-add" ' + linkAttrs + ' aria-label="' + linkLabel + ': ' + p.title.replace(/"/g, '&quot;') + '">' + bagIcon + '</a>';
            return '<div class="reco-item">' +
              /* Not lazy: these are inserted only when the panel is about to be
                 shown, and deferring the fetch just widens the blank gap. */
              '<a class="reco-item__media" href="' + p.url + '"><img src="' + img + '" alt="" width="70" height="70" decoding="async"></a>' +
              '<div class="reco-item__info">' +
                '<a class="reco-item__title" href="' + p.url + '">' + p.title + '</a>' +
                (p.show_price === false ? '' : '<div class="reco-item__price">' + formatMoney(p.price) + '</div>') +
                action +
              '</div></div>';
          }).join('');
          showDrawerReco(panel);
          list.querySelectorAll('[data-reco-add]').forEach(function (b) {
            b.addEventListener('click', function () {
              var id = b.getAttribute('data-reco-add');
              var row = b.closest('.reco-item');
              b.disabled = true;
              b.classList.add('is-adding');
              fetch('/cart/add.js', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
              }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
                .then(function (res) {
                  if (!res.ok) { refreshCart(false); return; }
                  noteAdded(res.body); flashAdded(b); markRecoAdded(row);
                  return fetch('/cart.js').then(function (r) { return r.json(); })
                    .then(function (cart) { return playRecoAdd(row, res.body, cart); });
                })
                .catch(function () { refreshCart(false); })
                .finally(function () { b.disabled = false; b.classList.remove('is-adding'); });
            });
          });
        });
    }).catch(function () {});
  }

  /* ---------- Mobile drawer: drill-down panels ----------
     Each level is a full-height panel slid in from the right over the one before it;
     the row at the top of a panel goes back. One screen shows one level, instead of a
     three-level menu unrolling into a list dozens of rows long.

     Why JS is needed at all when the markup is <details>: a closed <details> hides its
     contents immediately, so there is nothing on screen left to animate out. The
     `open` attribute is therefore held on for the duration of the slide-out and removed
     only when the transition finishes. Opening is the mirror — `open` first, then the
     class that triggers the transform on the next frame, because a transition cannot
     run on an element that was display:none in the same frame.

     With scripting unavailable none of this binds and the <details> behave normally,
     expanding in place. The menu is still fully reachable, just not animated. */
  document.querySelectorAll('[data-drawer-root]').forEach(function (root) {
    var nodes = Array.prototype.slice.call(root.querySelectorAll('[data-drawer-node]'));
    if (!nodes.length) return;

    function panelOf(node) { return node.querySelector(':scope > [data-drawer-panel]'); }

    function openNode(node) {
      var panel = panelOf(node);
      if (!panel) return;
      node.open = true;
      /* Force layout while the panel is still at translateX(100%), so the transition
         has a start value to animate from. A synchronous reflow rather than
         requestAnimationFrame on purpose: rAF is suspended in a hidden or throttled
         tab, and if the callback never ran the panel would be `open` but parked
         off-screen — the menu would look broken rather than merely unanimated.
         Reading offsetWidth cannot be skipped by the browser, so this always holds. */
      void panel.offsetWidth;
      panel.classList.add('is-shown');
      root.classList.add('is-nested');
    }

    function closeNode(node) {
      var panel = panelOf(node);
      if (!panel) { node.open = false; return; }
      panel.classList.remove('is-shown');
      var done = function (e) {
        if (e && e.propertyName && e.propertyName !== 'transform') return;
        panel.removeEventListener('transitionend', done);
        clearTimeout(fallback);
        node.open = false;
        /* Only drop the nested flag once nothing is left open, or closing an inner
           panel would also un-style the outer one. */
        if (!root.querySelector('[data-drawer-node][open]')) root.classList.remove('is-nested');
      };
      panel.addEventListener('transitionend', done);
      /* transitionend does not fire under reduced motion or in a background tab, and
         without this the panel would stay open forever. */
      var fallback = setTimeout(done, 420);
    }

    nodes.forEach(function (node) {
      var summary = node.querySelector(':scope > [data-drawer-open]');
      if (summary) {
        summary.addEventListener('click', function (e) {
          /* Take over from the native toggle so the animation can be sequenced. */
          e.preventDefault();
          if (node.open) closeNode(node); else openNode(node);
        });
      }
      var back = node.querySelector(':scope > [data-drawer-panel] > * > * > [data-drawer-back]')
              || node.querySelector(':scope > [data-drawer-panel] [data-drawer-back]');
      if (back) {
        back.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          closeNode(node);
        });
      }
    });

    /* Closing the drawer resets it to the top level, so reopening never lands the
       visitor three panels deep in a menu they left minutes ago. */
    var drawer = root.closest('#MobileNav');
    if (drawer) {
      document.querySelectorAll('[data-mobile-close]').forEach(function (b) {
        b.addEventListener('click', function () {
          setTimeout(function () {
            nodes.forEach(function (n) {
              var p = panelOf(n);
              if (p) p.classList.remove('is-shown');
              n.open = false;
            });
            root.classList.remove('is-nested');
          }, 320);
        });
      });
    }
  });

  /* ---------- Testimonials: draggable wrapping track ----------
     A fixed-width viewport with overflow:hidden and a flex track translated by one
     slide at a time, so stars, quote and author travel together. On settle the incoming
     slide replays `quote-appear-up` (fade in + rise 1rem).

     WRAPPING — the track carries a clone of the last quote before the first and a clone
     of the first after the last. Stepping past either end animates one slide onto a
     clone, then repositions to the matching real slide with the transition switched
     off. Motion is therefore always one slide in the direction of travel, never a
     rewind across the whole track.

     INTERRUPTIBLE — this is why the drag stays responsive.
     An earlier version held an `animating` flag that made both pointerdown and the
     step function bail until the 550ms transition finished. Dragging about twice a
     second landed inside that window every time, so gestures were silently dropped and
     it felt laggy. There is no such flag now: a new grab cancels whatever is in flight,
     freezes the track at its CURRENT RENDERED position, and drags on from there.

     That is also why the drag works in pixels rather than in slide indices. Mid-flight
     the track sits between slides, so an index-based baseline would snap before the
     finger had moved. `rebase()` converts a position inside a clone back to the
     equivalent real slide by shifting both index and offset by the same amount, which
     leaves the pixels on screen untouched. */
  document.querySelectorAll('[data-quotes]').forEach(function (viewport) {
    var track = viewport.querySelector('[data-quotes-track]');
    if (!track) return;
    var real = Array.prototype.slice.call(track.children);
    var n = real.length;
    if (n <= 1) return;

    function makeClone(node) {
      var c = node.cloneNode(true);
      c.removeAttribute('data-shopify-editor-block');
      c.removeAttribute('id');
      c.setAttribute('aria-hidden', 'true');
      c.classList.remove('is-current', 'is-entering');
      return c;
    }
    track.insertBefore(makeClone(real[n - 1]), real[0]);
    track.appendChild(makeClone(real[0]));

    var cells = Array.prototype.slice.call(track.children);   /* n + 2 */
    var index = 1;
    var timer = null, pendingEnd = null, pendingTimer = null;
    var dragging = false, startX = 0, dx = 0, baseX = 0, moved = false;
    var THRESHOLD = 0.18;

    function slideW() { return viewport.clientWidth || 1; }
    function xFor(idx) { return -idx * slideW(); }

    /* The rendered offset, read from the computed matrix so it is the position on
       screen right now, not the target the transition is heading towards. */
    function currentX() {
      var t = getComputedStyle(track).transform;
      if (!t || t === 'none') return 0;
      if (window.DOMMatrixReadOnly) { try { return new DOMMatrixReadOnly(t).m41; } catch (e) {} }
      var m = t.match(/matrix.*\((.+)\)/);
      if (!m) return 0;
      var parts = m[1].split(', ');
      return parseFloat(parts.length > 6 ? parts[12] : parts[4]) || 0;
    }

    function setX(x, animate) {
      track.style.transition = animate ? '' : 'none';
      track.style.transform = 'translate3d(' + x + 'px,0,0)';
      if (!animate) {
        void track.offsetWidth;   /* flush before anything re-enables the transition */
        track.style.transition = '';
      }
    }

    function clearPending() {
      if (pendingEnd) { track.removeEventListener('transitionend', pendingEnd); pendingEnd = null; }
      if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; }
    }

    /* Move index and offset by the same number of slides so a position sitting inside a
       clone becomes the equivalent real slide with nothing changing visually. */
    function rebase() {
      var w = slideW();
      var cx = currentX();
      var pos = -cx / w;
      var shift = 0;
      if (pos < 0.5) shift = n;
      else if (pos > n + 0.5) shift = -n;
      if (shift) {
        index += shift;
        cx -= shift * w;
        setX(cx, false);
      }
      return cx;
    }

    function normalize() {
      if (index === 0) { index = n; setX(xFor(index), false); enter(); }
      else if (index === n + 1) { index = 1; setX(xFor(index), false); enter(); }
    }

    function enter() {
      cells.forEach(function (o) { o.classList.remove('is-current', 'is-entering'); });
      var s = cells[index];
      if (!s) return;
      s.classList.add('is-current');
      s.classList.remove('is-entering');
      void s.offsetWidth;       /* without the reflow the replay is coalesced away */
      s.classList.add('is-entering');
    }

    function animateTo(target) {
      clearPending();
      index = target;
      setX(xFor(index), true);
      enter();
      pendingEnd = function (e) {
        if (e && e.propertyName && e.propertyName !== 'transform') return;
        clearPending();
        normalize();
      };
      track.addEventListener('transitionend', pendingEnd);
      /* transitionend never fires when the transition is suppressed — reduced motion,
         a background tab, or an interrupted animation — so the wrap must not depend on
         it alone or the carousel would latch. */
      pendingTimer = setTimeout(function () { clearPending(); normalize(); }, 700);
    }

    function sizeViewport() {
      viewport.style.height = '';
      var tallest = 0;
      cells.forEach(function (s) { if (s.offsetHeight > tallest) tallest = s.offsetHeight; });
      if (tallest) viewport.style.height = tallest + 'px';
    }

    function stopAuto() { if (timer) { clearInterval(timer); timer = null; } }
    function startAuto() {
      var ms = parseInt(viewport.dataset.quotesAutoplay, 10);
      if (!ms) return;
      stopAuto();
      timer = setInterval(function () { animateTo(index + 1); }, ms);
    }

    viewport.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      /* No `animating` guard: a grab during a transition takes over from wherever the
         track currently is. That is what keeps repeated fast drags responsive. */
      clearPending();
      dragging = true; moved = false;
      startX = e.clientX; dx = 0;
      baseX = rebase();
      setX(baseX, false);       /* freeze in place, cancelling the in-flight transition */
      viewport.classList.add('is-dragging');
      stopAuto();
      if (viewport.setPointerCapture) viewport.setPointerCapture(e.pointerId);
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      /* .is-dragging suppresses the transition in CSS, so this writes straight through. */
      track.style.transform = 'translate3d(' + (baseX + dx) + 'px,0,0)';
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      var w = slideW();
      var from = Math.round(-baseX / w);
      var target = from;
      if (Math.abs(dx) / w > THRESHOLD) target = from + (dx < 0 ? 1 : -1);
      animateTo(target);
      dx = 0;
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', endDrag);
    viewport.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    viewport.setAttribute('tabindex', '0');
    viewport.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { stopAuto(); rebase(); animateTo(index + 1); }
      else if (e.key === 'ArrowLeft') { stopAuto(); rebase(); animateTo(index - 1); }
    });

    sizeViewport();
    setX(xFor(index), false);
    enter();
    startAuto();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { sizeViewport(); setX(xFor(index), false); });
    }
    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () { sizeViewport(); setX(xFor(index), false); }, 150);
    });
  });

  /* ---------- Skin motion: Testimonials + footer feature icons (v240) ----------
     OFF unless the skin's stylesheet switches it on with `--skin-motion: 1` on :root.
     The manufacturer skins do; regular-1 does not, so on regular stores this returns at once
     and nothing on the page changes. Both sections are shared files, which is why the
     motion lives here rather than in the sections; the look is in the skin's base.css
     ("v14: Testimonials and feature icons").

     Testimonials: a large quotation mark over the quote, the shopper's initial over each
     name, previous / next buttons with a "01 / 03" count under the carousel (they press
     the carousel's own arrow keys, so they behave exactly like the keyboard, autoplay
     stopping included), the section rising in when scrolled to, and a soft light behind
     the quote that follows the mouse. The stars sweep in on every change of quote (CSS).
     Feature icons: the icons draw themselves and the items rise in, one after another.
     The buttons and the count are added even with reduced motion; nothing moves then. */
  (function () {
    var root = document.documentElement;
    if (getComputedStyle(root).getPropertyValue('--skin-motion').trim() !== '1') return;
    var mm = window.matchMedia;
    var still = !!(mm && mm('(prefers-reduced-motion: reduce)').matches);
    var mouse = !!(mm && mm('(hover: hover) and (pointer: fine)').matches);
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    /* The entrance classes come off once it has played (after `ms`), leaving the section
       exactly as it is without them — so a later animation (the quote mark's bump, the
       icons' hover) is not undone by the entrance's starting state. */
    function reveal(el, name, threshold, ms) {
      if (still || !('IntersectionObserver' in window)) return;
      el.classList.add(name + '--anim');
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          el.classList.add(name + '--play');
          io.disconnect();
          setTimeout(function () { el.classList.remove(name + '--anim', name + '--play'); }, ms);
        });
      }, { threshold: threshold });
      io.observe(el);
    }
    function follow(el) {
      if (still || !mouse) return;
      var frame = 0, last = null;
      el.addEventListener('pointermove', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        last = e;
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = 0;
          if (!last) return;
          var r = el.getBoundingClientRect();
          if (!r.width || !r.height) return;
          el.style.setProperty('--gx', ((last.clientX - r.left) / r.width * 100).toFixed(1) + '%');
          el.style.setProperty('--gy', ((last.clientY - r.top) / r.height * 100).toFixed(1) + '%');
        });
      }, { passive: true });
      el.addEventListener('pointerleave', function () {
        last = null;
        el.style.removeProperty('--gx'); el.style.removeProperty('--gy');
      }, { passive: true });
    }
    var ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

    function quotes(sec) {
      if (!sec || sec.hasAttribute('data-sm')) return;
      var vp = sec.querySelector('[data-quotes]');
      if (!vp) return;
      sec.setAttribute('data-sm', '');
      sec.classList.add('sm-quotes');
      var track = vp.querySelector('[data-quotes-track]');
      var mark = document.createElement('span');
      mark.className = 'sm-quotes__mark';
      mark.setAttribute('aria-hidden', 'true');
      mark.textContent = '“';
      vp.parentNode.insertBefore(mark, vp);
      Array.prototype.forEach.call(sec.querySelectorAll('.quote'), function (q) {
        var a = q.querySelector('.quote__author');
        if (!a || q.querySelector('.sm-quotes__avatar')) return;
        var t = (a.textContent || '').trim();
        if (!t) return;
        var av = document.createElement('span');
        av.className = 'sm-quotes__avatar';
        av.setAttribute('aria-hidden', 'true');
        av.textContent = t.charAt(0).toUpperCase();
        a.parentNode.insertBefore(av, a);
      });
      /* The carousel locked its height before the initials were added; its own resize
         handler measures again. */
      window.dispatchEvent(new Event('resize'));

      /* Buttons and count, only where the carousel was built (one clone at each end). */
      var cells = track ? Array.prototype.slice.call(track.children) : [];
      var n = cells.filter(function (c) { return c.getAttribute('aria-hidden') !== 'true'; }).length;
      if (n > 1 && cells.length === n + 2) {
        var nav = document.createElement('div');
        nav.className = 'sm-quotes__nav';
        nav.innerHTML = '<button type="button" class="sm-quotes__btn sm-quotes__btn--prev" data-sm-dir="ArrowLeft" aria-label="Previous review">' + ARROW + '</button>' +
          '<span class="sm-quotes__count" aria-hidden="true"><b>01</b> / ' + pad(n) + '</span>' +
          '<button type="button" class="sm-quotes__btn" data-sm-dir="ArrowRight" aria-label="Next review">' + ARROW + '</button>';
        vp.parentNode.insertBefore(nav, vp.nextSibling);
        var count = nav.querySelector('b');
        nav.addEventListener('click', function (e) {
          var b = e.target.closest ? e.target.closest('[data-sm-dir]') : null;
          if (!b) return;
          vp.dispatchEvent(new KeyboardEvent('keydown', { key: b.getAttribute('data-sm-dir'), bubbles: true }));
        });
        var shown = 0;
        var sync = function () {
          var cur = track.querySelector('.quote.is-current');
          var i = cur ? cells.indexOf(cur) : -1;
          if (i < 0) return;
          var r = i === 0 ? n : (i === cells.length - 1 ? 1 : i);
          if (r === shown) return;
          var first = shown === 0;
          shown = r;
          count.textContent = pad(r);
          if (first || still) return;
          mark.classList.remove('is-bump');
          void mark.offsetWidth;
          mark.classList.add('is-bump');
        };
        new MutationObserver(sync).observe(track, { subtree: true, attributes: true, attributeFilter: ['class'] });
        sync();
      }
      reveal(sec, 'sm-quotes', 0.35, 1600);
      follow(sec);
    }

    function strip(el) {
      if (!el || el.hasAttribute('data-sm')) return;
      el.setAttribute('data-sm', '');
      el.classList.add('sm-strip');
      Array.prototype.forEach.call(el.querySelectorAll('.icon-strip__icon svg *'), function (p) {
        if (/^(path|circle|rect|line|polyline|polygon|ellipse)$/i.test(p.tagName)) p.setAttribute('pathLength', '1');
      });
      reveal(el, 'sm-strip', 0.4, 2300);
    }

    function scan(scope) {
      Array.prototype.forEach.call(scope.querySelectorAll('.quotes-section'), quotes);
      Array.prototype.forEach.call(scope.querySelectorAll('.icon-strip'), strip);
    }
    scan(document);
    document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  })();

  /* ---------- Footer link groups ----------
     <details> collapses on phones and is forced open above 749px, where there is room
     and an accordion would only add a click. They ship with `open` in the markup, so a
     visitor with JS disabled gets everything expanded rather than everything hidden —
     the failure mode is a long footer, not an unreachable one. */
  (function () {
    var accs = document.querySelectorAll('.footer-acc');
    if (!accs.length || !window.matchMedia) return;
    var wide = window.matchMedia('(min-width:750px)');
    function sync(e) {
      accs.forEach(function (d) { d.open = e.matches; });
    }
    sync(wide);
    /* addEventListener on MediaQueryList is not in older Safari, which only has
       addListener. Try the modern one and fall back rather than doing nothing. */
    if (wide.addEventListener) wide.addEventListener('change', sync);
    else if (wide.addListener) wide.addListener(sync);
  })();

  /* Drawer order note — saves as you type (debounced) */
  (function () {
    var note = document.querySelector('[data-cart-note]');
    if (!note) return;
    var t = null;
    note.addEventListener('input', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.value })
        }).catch(function () {});
      }, 600);
    });
  })();

  /* ---------- Promotion self-check ----------
     The promotion text is typed into a theme setting and cannot be verified against the
     admin, because Liquid has no access to automatic discounts. But a cart DOES see the
     real ones — so once a shopper has items, the storefront can check its own claim and
     take the announcement down if the discount is not actually applying. That turns a
     promotion someone forgot to switch off from something that lies indefinitely into
     something that corrects itself the moment anyone shops.

     ONLY WHEN THE PROMOTION COVERS ALL PRODUCTS. With a narrower scope, "items in the
     cart but no discount" is not evidence of anything: the shopper may simply be
     holding products the promotion never covered. Hiding on that would be a false
     positive — pulling a live promotion off the site because of a normal cart. The
     scope is published in a data attribute so this stays honest about what it can
     actually prove. For the other scopes, the end date is the safeguard.

     Costs one /cart.js call, and only on pages that are making a claim. */
  (function () {
    var els = document.querySelectorAll('[data-promo-bar], [data-promo-note], .card__badge--promo');
    if (!els.length) return;
    var root = document.documentElement;
    if (root.getAttribute('data-promo-scope') !== 'all') return;

    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      if (!cart || cart.item_count === 0) return;          // nothing to learn yet
      if (cart.total_discount > 0) return;                 // claim holds up
      els.forEach(function (el) { el.hidden = true; el.style.display = 'none'; });
    }).catch(function () { /* leave the announcement alone if the check cannot run */ });
  })();

  /* ---------- Cart survey ----------
     One required question in the cart whose answer rides along on the order as a cart
     attribute. Two copies of the field can be on the page at once (cart page and
     drawer); they are kept in step so answering in one satisfies the other.

     The answer is saved on change rather than on checkout. The shopper can wander
     between the drawer, the cart page and a reload before paying, and an answer held
     only in the DOM would not survive any of those.

     Gating is by state, not by removal: [data-checkout-gate] elements are disabled
     until the answer is valid, so the control stays visible and explains itself
     instead of vanishing and leaving the shopper hunting for it. */
  (function () {
    var selects = Array.prototype.slice.call(document.querySelectorAll('[data-cart-survey]'));
    if (!selects.length) return;

    var root = selects[0].closest('[data-cart-survey-root]');
    var key = root ? root.getAttribute('data-cart-survey-key') : '';
    var required = selects[0].hasAttribute('required');

    function value() {
      for (var i = 0; i < selects.length; i++) if (selects[i].value) return selects[i].value;
      return '';
    }
    function valid() { return !required || value() !== ''; }

    function setError(show) {
      selects.forEach(function (sel) {
        var box = sel.closest('[data-cart-survey-root]');
        var err = box && box.querySelector('[data-cart-survey-error]');
        if (err) err.hidden = !show;
        /* Not colour alone: aria-invalid carries it to assistive tech, and the CSS
           adds a border weight change as well as a red. */
        sel.setAttribute('aria-invalid', show ? 'true' : 'false');
        sel.classList.toggle('is-invalid', !!show);
      });
    }

    function syncGates() {
      var ok = valid();
      document.querySelectorAll('[data-checkout-gate]').forEach(function (el) {
        if (el.tagName === 'BUTTON') {
          el.disabled = !ok;
        } else {
          /* Anchors: aria-disabled plus a click guard. tabindex is left alone so the
             control keeps its place in the tab order and can still be reached and
             read — it announces as disabled rather than disappearing. */
          el.setAttribute('aria-disabled', ok ? 'false' : 'true');
        }
        el.classList.toggle('is-disabled', !ok);
      });
      if (ok) setError(false);
    }

    var saving = null;
    function persist(val) {
      if (!key) return;
      clearTimeout(saving);
      saving = setTimeout(function () {
        var attrs = {};
        attrs[key] = val;
        fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        }).catch(function () {});
      }, 150);
    }

    selects.forEach(function (sel) {
      sel.addEventListener('change', function () {
        var v = sel.value;
        selects.forEach(function (other) { if (other !== sel) other.value = v; });
        persist(v);
        syncGates();
      });
    });

    /* Anchor gate: capture phase, so this runs before the drawer's own click handlers
       rather than after one of them has already started navigating. */
    document.addEventListener('click', function (e) {
      var gate = e.target.closest ? e.target.closest('[data-checkout-gate]') : null;
      if (!gate || valid()) return;
      e.preventDefault();
      e.stopPropagation();
      reject();
    }, true);

    function reject() {
      setError(true);
      /* Focus the copy the shopper can actually see. The drawer and the cart page can
         both hold a field, and focusing a hidden one moves focus nowhere visible. */
      for (var i = 0; i < selects.length; i++) {
        if (selects[i].offsetParent !== null) { selects[i].focus(); return; }
      }
      selects[0].focus();
    }

    /* The cart page posts a real form. `required` on the select is the no-JS backstop;
       with JS here, take validation over so the message is inline and announced rather
       than a browser bubble, which cannot be styled or placed. */
    var cartForm = document.querySelector('.cart-page form');
    if (cartForm) {
      cartForm.setAttribute('novalidate', 'novalidate');
      cartForm.addEventListener('submit', function (e) {
        /* Quantity updates submit this same form through the hidden Update button.
           Only the checkout submit is gated — blocking a quantity change would strand
           the shopper on a cart they cannot edit. */
        var via = e.submitter && e.submitter.getAttribute('name');
        if (via !== 'checkout') return;
        if (valid()) return;
        e.preventDefault();
        reject();
      });
    }

    /* On load, not only on change: the answer may already be saved from a previous
       visit, and the gate has to reflect that without the shopper touching anything. */
    syncGates();
  })();

  function formatMoney(cents) {
    /* A last line of defence. Nothing should reach here with a non-number now, but a
       blank is a far better failure than "$NaN" printed next to a shopper's total. */
    var n = Number(cents);
    if (!isFinite(n)) return '';
    return (window.Shopify && Shopify.currency ? '' : '') + '$' + (n / 100).toFixed(2);
  }

  function refreshCart(open) {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) { renderCart(c); if (open) openCart(); });
  }

  /* ---------- Order note sheet ----------
     The Note row opens a sheet pinned to the bottom of the viewport over a dimmed page,
     rather than expanding inside the drawer and pushing the totals around.

     It is toggled with [hidden] rather than a class so it is genuinely out of the
     accessibility tree while closed. Closing is immediate — the entrance animation is a
     one-shot keyframe, so there is nothing to wait for on the way out.

     The drawer already holds the page scroll lock while it is open, so this does not
     take one of its own; adding a second lock would mean the first close released it
     and the page scrolled behind a still-open sheet. */
  (function () {
    var sheet = document.querySelector('[data-note-sheet]');
    if (!sheet) return;
    var field = sheet.querySelector('[data-cart-note]');

    function openSheet() {
      sheet.hidden = false;
      /* Focus after the frame the sheet becomes visible in — focusing a hidden element
         is a no-op, and on iOS focusing too early opens the keyboard over a sheet that
         has not finished rising. */
      setTimeout(function () { if (field) field.focus({ preventScroll: true }); }, 60);
    }
    function closeSheet() {
      if (sheet.hidden) return;
      sheet.hidden = true;
      var trigger = document.querySelector('[data-note-open]');
      if (trigger) trigger.focus({ preventScroll: true });
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-note-open]')) { e.preventDefault(); openSheet(); return; }
      if (e.target.closest('[data-note-close]')) { e.preventDefault(); closeSheet(); }
    });
    /* Escape closes the sheet and must not reach the drawer's own Escape handler,
       or one key press would shut both. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || sheet.hidden) return;
      e.stopPropagation();
      closeSheet();
    }, true);
  })();

  /* ---------- Discount code sheet (v235) ----------
     The drawer's Discount button opens a sheet like the Note one (same markup classes,
     same open / close / Escape handling). Codes go on the CART through /cart/update.js
     and its `discount` parameter (Shopify, May 2025): the answer is the whole cart, with
     discount_codes = [{ code, applicable }], so the drawer re-renders with the saving at
     once and checkout keeps the code.

     The parameter REPLACES the cart's codes, it does not add one. Applying therefore
     sends the codes that already apply plus the new one. A code the cart will not take
     still comes back, marked applicable:false; it is reported and then dropped by
     sending the list again without it, so it does not linger on the cart. Removing a
     code sends the list without it; an empty string clears them all. */
  /* v267: the code logic is shared with the cart page's own code field (sections/main-cart,
     [data-cart-disc]): discountCodes(root) wires one set of input / Apply / message / applied
     codes. The drawer sheet keeps exactly its v235 behaviour. On the cart page the applied
     code changes the cart total, and renderCart's cart-page refresh (v248) then brings the
     page's discount rows, line discounts and subtotal in line, rolling the numbers. */
  function discountCodes(root) {
    var input = root.querySelector('[data-disc-input]');
    var applyBtn = root.querySelector('[data-disc-apply]');
    var msg = root.querySelector('[data-disc-msg]');
    var list = root.querySelector('[data-disc-codes]');
    var busy = false;
    var xSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

    function same(a, b) { return String(a).trim().toUpperCase() === String(b).trim().toUpperCase(); }
    function applied(cart) {
      return (cart.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
    }
    function say(text, kind) {
      if (!msg) return;
      msg.textContent = text || '';
      msg.className = msg.className.replace(/\s*is-(ok|error)\b/g, '') + (kind ? ' is-' + kind : '');
    }
    function drawCodes(cart) {
      if (!list) return;
      list.innerHTML = applied(cart).map(function (c) {
        return '<li><span>' + escapeHtml(c) + '</span><button type="button" data-disc-remove="' + escapeHtml(c) + '" aria-label="Remove code ' + escapeHtml(c) + '">' + xSvg + '</button></li>';
      }).join('');
    }
    function send(codes) {
      return fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') })
      }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
    }
    function done(cart) { renderCart(cart); drawCodes(cart); return cart; }
    function setBusy(on) {
      busy = on;
      if (applyBtn) { applyBtn.disabled = on; applyBtn.classList.toggle('is-busy', on); }
    }
    function refresh() { fetch('/cart.js').then(function (r) { return r.json(); }).then(drawCodes).catch(function () {}); }

    function apply(code) {
      code = String(code || '').trim();
      if (!code) { say('Enter a discount code.', 'error'); if (input) input.focus(); return; }
      if (busy) return;
      setBusy(true); say('');
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        var current = applied(cart);
        if (current.some(function (c) { return same(c, code); })) {
          setBusy(false); drawCodes(cart);
          say(code + ' is already applied.', 'ok');
          return;
        }
        return send(current.concat(code)).then(function (after) {
          var hit = (after.discount_codes || []).filter(function (d) { return same(d.code, code); })[0];
          if (hit && hit.applicable) {
            done(after); setBusy(false);
            if (input) input.value = '';
            say(hit.code + ' applied.', 'ok');
            return;
          }
          /* Not taken: drop it again so it does not stay on the cart, keeping the rest. */
          return send(applied(after).filter(function (c) { return !same(c, code); })).then(function (clean) {
            done(clean); setBusy(false);
            say(code + ' can\u2019t be used with this cart.', 'error');
          });
        });
      }).catch(function () {
        setBusy(false);
        say('Something went wrong. Please try again.', 'error');
      });
    }
    function remove(code) {
      if (busy) return;
      setBusy(true); say('');
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        return send(applied(cart).filter(function (c) { return !same(c, code); }));
      }).then(function (cart) {
        done(cart); setBusy(false);
        say(code + ' removed.', 'ok');
      }).catch(function () {
        setBusy(false);
        say('Something went wrong. Please try again.', 'error');
      });
    }
    document.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-disc-remove]');
      if (rm && root.contains(rm)) { e.preventDefault(); remove(rm.getAttribute('data-disc-remove')); }
    });
    return { apply: apply, refresh: refresh, say: say, input: input };
  }

  (function () {
    var sheet = document.querySelector('[data-disc-sheet]');
    if (!sheet) return;
    var form = sheet.querySelector('[data-disc-form]');
    var ui = discountCodes(sheet);

    function open() {
      sheet.hidden = false;
      ui.say('');
      ui.refresh();
      setTimeout(function () { if (ui.input) ui.input.focus({ preventScroll: true }); }, 60);
    }
    function close() {
      if (sheet.hidden) return;
      sheet.hidden = true;
      var trigger = document.querySelector('[data-disc-open]');
      if (trigger) trigger.focus({ preventScroll: true });
    }

    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); ui.apply(ui.input ? ui.input.value : ''); });
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-disc-open]')) { e.preventDefault(); open(); return; }
      if (e.target.closest('[data-disc-close]')) { e.preventDefault(); close(); }
    });
    /* Escape closes this sheet only, not the drawer behind it (same as the Note sheet). */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || sheet.hidden) return;
      e.stopPropagation();
      close();
    }, true);
  })();

  /* ---------- Cart page: discount code field (v267) ----------
     sections/main-cart. Not a <form>: it sits inside the cart form, and a nested form is not
     allowed — so Apply is a type="button" and Enter in the field is caught here (otherwise
     Enter would submit the cart form, i.e. go to checkout). The field has no name, so it is
     never posted with the cart. The markup arrives [hidden] and is shown here, so without
     JavaScript there is no field that cannot work. */
  (function () {
    var box = document.querySelector('[data-cart-disc]');
    if (!box) return;
    box.hidden = false;
    var ui = discountCodes(box);
    ui.refresh();
    var btn = box.querySelector('[data-disc-apply]');
    if (btn) btn.addEventListener('click', function (e) { e.preventDefault(); ui.apply(ui.input ? ui.input.value : ''); });
    if (ui.input) ui.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); ui.apply(ui.input.value); }
    });
  })();

  /* Removing a line, in the drawer and on the cart page: "Changing quantity and removing
     (v248)". The cart page's remove links still work without JavaScript. */

  /* ---------- Add-to-cart button feedback ----------
     Drives the three states the CSS draws: .is-adding while the request is in flight,
     then .is-added for a beat once the server has confirmed. Shared by the product
     card's icon button and the drawer's recommendation button.

     The class is removed and re-added around a forced reflow so a second tap replays
     the animation instead of doing nothing — re-adding a class that is already present
     does not restart a CSS animation. Reading offsetWidth is what flushes it, and it is
     used rather than requestAnimationFrame because rAF does not run in a background or
     throttled tab, which would leave the button stuck mid-state. */
  function flashAdded(btn) {
    if (!btn) return;
    btn.classList.remove('is-added');
    void btn.offsetWidth;
    btn.classList.add('is-added');
    if (btn._addedTimer) clearTimeout(btn._addedTimer);
    btn._addedTimer = setTimeout(function () { btn.classList.remove('is-added'); }, 1200);
  }

  /* ---------- Add-to-cart errors ----------
     Shown in the form's [data-form-error] element when it has one (the product page
     does); always announced to other scripts as a cart:add-error event. */
  function clearFormError(form) {
    var box = form.querySelector('[data-form-error]');
    if (box) { box.hidden = true; box.textContent = ''; }
  }
  function showFormError(form, body) {
    body = body || {};
    var box = form.querySelector('[data-form-error]');
    if (box) {
      /* errors as an object means field errors the gift card form shows on each field;
         the box then carries the summary line. Otherwise the description is the message. */
      /* description is plain text for most errors, but for gift card field errors
         Shopify sends it as an object like errors (checked live), so only text is used. */
      var text = (typeof body.description === 'string' && body.description) ||
                 (typeof body.message === 'string' && body.message) ||
                 'This could not be added to your cart.';
      if (body.errors && typeof body.errors === 'object') text = 'Please check the highlighted fields above.';
      else if (typeof body.errors === 'string') text = body.errors;
      box.textContent = text;
      box.hidden = false;
    }
    /* Announced AFTER the box is written, so a listener (gift-card-form.js) can refine
       the message — e.g. replace the summary with an error that has no field of its own. */
    form.dispatchEvent(new CustomEvent('cart:add-error', { bubbles: true, detail: body }));
  }

  /* ---------- Purchase requirements ----------
     snippets/purchase-requirements.liquid draws the certifications and fields inside a
     product form. Their HTML required / type / pattern attributes stop a plain form post
     on their own (no JavaScript needed). With JavaScript the browser's own bubbles are
     switched off for that form and every problem is shown under its field at once, in a
     role="alert" element so it is announced; focus moves to the first one. The AJAX add
     below calls preqValid() before anything else, so nothing reaches /cart/add.js until
     every required answer is in. */
  var PREQ_MSG = {
    checkbox: 'Please tick this box to continue.',
    email: 'Please enter a valid email address.',
    tel: 'Please enter a valid phone number.',
    date: 'Please enter a date.',
    text: 'Please fill in this field.'
  };
  function preqCheck(item) {
    var kind = item.getAttribute('data-preq-kind');
    var input = item.querySelector('input');
    if (!input || input.disabled) return '';
    var required = input.required;
    if (kind === 'checkbox') return (required && !input.checked) ? PREQ_MSG.checkbox : '';
    var v = input.value.trim();
    if (!v) return required ? (kind === 'text' ? 'Please fill in ' + (item.getAttribute('data-preq-label') || 'this field') + '.' : PREQ_MSG[kind]) : '';
    if (kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return PREQ_MSG.email;
    if (kind === 'tel') {
      var digits = v.replace(/\D/g, '');
      if (!/^[0-9()+.\-\s]+$/.test(v) || digits.length < 7 || digits.length > 15) return PREQ_MSG.tel;
    }
    if (kind === 'date' && input.validity && input.validity.badInput) return PREQ_MSG.date;
    return '';
  }
  function preqShow(item, msg) {
    var input = item.querySelector('input');
    var err = item.querySelector('.preq__error');
    if (msg) {
      item.setAttribute('data-invalid', '');
      if (input) input.setAttribute('aria-invalid', 'true');
      if (err) { err.textContent = msg; err.hidden = false; }
    } else {
      item.removeAttribute('data-invalid');
      if (input) input.removeAttribute('aria-invalid');
      if (err) { err.hidden = true; err.textContent = ''; }
    }
  }
  function preqValid(form) {
    var items = form.querySelectorAll('[data-preq-item]');
    if (!items.length) return true;
    var first = null;
    items.forEach(function (item) {
      var msg = preqCheck(item);
      preqShow(item, msg);
      if (msg && !first) first = item.querySelector('input');
    });
    var box = form.querySelector('[data-form-error]');
    if (first) {
      if (box) { box.textContent = 'Please complete the highlighted fields above.'; box.hidden = false; }
      first.focus();
      if (first.scrollIntoView) first.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }
    return true;
  }
  /* Shown only while a box is ticked (v250, show_when). The Liquid draws such a
     requirement visible and optional, so a browser without JavaScript never blocks on
     it. Here, while its box [data-preq-controls] is unticked, it is hidden (the hidden
     attribute), its input disabled — so it is not validated, not required and not
     submitted: no empty properties[...] reaches the cart — and its value cleared, so an
     email typed before unticking is never sent. Ticked, it appears straight after the
     box and follows its own data-preq-required, like any requirement. The box's
     aria-expanded follows. */
  function preqSyncWhen(box) {
    var wrap = box.closest('[data-preq]');
    if (!wrap) return;
    var on = box.checked && !box.disabled;
    box.setAttribute('aria-expanded', on ? 'true' : 'false');
    wrap.querySelectorAll('[data-preq-when="' + box.id + '"]').forEach(function (item) {
      var req = on && item.getAttribute('data-preq-required') === 'true';
      item.hidden = !on;
      item.querySelectorAll('input').forEach(function (inp) {
        inp.disabled = !on;
        inp.required = req;
        if (req) inp.setAttribute('aria-required', 'true'); else inp.removeAttribute('aria-required');
        if (!on) { if (inp.type === 'checkbox') inp.checked = false; else inp.value = ''; }
      });
      item.querySelectorAll('[data-preq-mark="req"]').forEach(function (m) { m.hidden = !req; });
      item.querySelectorAll('[data-preq-mark="opt"]').forEach(function (m) { m.hidden = req; });
      if (!on) preqShow(item, '');
    });
    var form = wrap.closest('form');
    if (form && !form.querySelector('[data-preq-item][data-invalid]')) clearFormError(form);
  }
  if (window.Shopify && Shopify.designMode) {
    document.querySelectorAll('[data-preq-warn]').forEach(function (w) { console.warn('[Purchase requirements] ' + w.textContent.replace(/\s+/g, ' ').trim()); });
  }
  document.querySelectorAll('[data-preq]').forEach(function (wrap) {
    var form = wrap.closest('form');
    if (!form) return;
    form.noValidate = true;   /* our messages replace the browser bubbles */
    wrap.querySelectorAll('[data-preq-controls]').forEach(function (box) {
      box.addEventListener('change', function () { preqSyncWhen(box); });
      preqSyncWhen(box);
    });
    wrap.querySelectorAll('[data-preq-item]').forEach(function (item) {
      var input = item.querySelector('input');
      if (!input) return;
      /* Once a field has been flagged, re-check it as the shopper fixes it, so the
         message goes away the moment it is no longer true. */
      var recheck = function () {
        if (!item.hasAttribute('data-invalid')) return;
        preqShow(item, preqCheck(item));
        if (!form.querySelector('[data-preq-item][data-invalid]')) clearFormError(form);
      };
      input.addEventListener('change', recheck);
      input.addEventListener('input', recheck);
    });
  });

  /* Back/forward cache: the browser may bring the page back with a box ticked or
     unticked differently from when the scripts last ran. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) document.querySelectorAll('[data-preq] [data-preq-controls]').forEach(preqSyncWhen);
  });

  /* ---------- AJAX add to cart ---------- */
  document.querySelectorAll('form[action="/cart/add"], form[action^="/cart/add"]').forEach(function (form) {
    form.addEventListener('submit', function (e) {
      /* Request access (v228): a form showing the access button is not for sale to this
         customer. Its button is type="button", so this only catches a stray submit. */
      if (form.querySelector('[data-ra-btn], [data-ltb]')) { e.preventDefault(); return; }
      if (!preqValid(form)) { e.preventDefault(); return; }
      if (form.dataset.noajax) return;
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      /* Swap the label element's text, not the button's — product cards keep an
         icon inside the button, and replacing textContent would delete it. */
      var labelEl = btn ? (btn.querySelector('.card__btn-label') || btn) : null;
      /* v253: the product page button says "✓ Added" for a moment after a good add.
         Its real label is remembered, so a second click during that moment does not
         take "✓ Added" for the label. */
      var isPdp = !!(btn && btn.classList.contains('pdp__atc'));
      var label = labelEl ? labelEl.textContent : '';
      if (isPdp) { if (btn._added) label = btn._label; else btn._label = label; btn._added = false; clearTimeout(btn._labelTimer); }
      var addedOk = false;
      if (btn) {
        btn.disabled = true; btn.classList.add('is-adding');
        if (isPdp) labelEl.innerHTML = '<span class="pdp__atc-spin" aria-hidden="true"></span>Adding…';
        else labelEl.textContent = 'Adding…';
      }
      clearFormError(form);
      fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          /* A rejected add (422) used to fall through to here as if it had worked: the
             toast opened with an empty item and the shopper was never told why. Now the
             error is shown and nothing claims success. Gift card recipient errors arrive
             as res.body.errors, keyed by field (email, name, message, send_on);
             gift-card-form.js listens for cart:add-error and puts each one on its field. */
          if (!res.ok) { showFormError(form, res.body); return; }
          var item = res.body;
          addedOk = true;
          noteAdded(item); afterAdd(item, form); flashAdded(btn);
          form.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: item }));
        })
        .catch(function () { showFormError(form, { description: 'Something went wrong. Please try again.' }); })
        .finally(function () {
          if (!btn) return;
          btn.disabled = false; btn.classList.remove('is-adding');
          if (isPdp && addedOk) {
            labelEl.textContent = '\u2713 Added'; btn._added = true;
            btn._labelTimer = setTimeout(function () {
              /* Only if nothing else (a variant change to Sold Out) rewrote it meanwhile. */
              if (btn._added && labelEl.textContent === '\u2713 Added') labelEl.textContent = label;
              btn._added = false;
            }, 1500);
          } else {
            labelEl.textContent = label;
          }
        });
    });
  });

  /* ---------- Request access (v228) ----------
     Restricted products (Theme settings → Request access) show a button instead of Add to
     Cart for anyone not approved: data-ra-btn on the product page, data-ra-card on cards
     (a link to the product without JavaScript; with it, the same pop-ups and request). data-ra-state says which:
       guest       pop-up "Please log in…" + Log in, returning to this page
       request     send the request, then pop-up "Access requested…"; every Request
                   Access button and card label on the page becomes Awaiting Approval
       awaiting    pop-up "You have already requested access…"
       restricted  pop-up "You were restricted…" (store email and phone)
     Texts and labels come from snippets/request-access.liquid ([data-ra-config]); with
     the feature off that element is absent and none of this runs.

     SENDING. A theme cannot tag a customer, so the request goes through the store's
     Shopify Forms form on the "Request form page". Storefront pages refuse to be framed
     (X-Frame-Options: DENY), so it cannot be filled out of sight: Request Access goes to
     that page with the product and the way back in the address, the theme there fills
     the form (open shadow root) with the logged-in customer's email and the
     product and submits it, and on its confirmation returns to the product with
     ?ra_sent=1, which opens the success pop-up. Forms tags the customer
     access-requested; a Flow workflow emails staff. Without a form page, or with a page
     that has no form, the request is posted through the contact form instead (an email
     to the store, no tag). Only if that fails too does the shopper see the error.

     REMEMBERED. Right after a request the tag may not have reached the page yet, so the
     request is also remembered in this browser for 7 days, per customer
     (localStorage "dp-ra-requested:<customer id>"); any Request Access button for that
     customer then reads Awaiting Approval. The tag, once there, says the same thing from
     the server, and an approval or decline (access-granted / access-restricted) always
     wins because those products then render without a Request Access button. */
  var raCfg = null;
  (function () {
    var el = document.querySelector('[data-ra-config]');
    if (!el) return;
    try { raCfg = JSON.parse(el.textContent); } catch (err) { raCfg = null; }
  })();
  var RA_REMEMBER_MS = 7 * 24 * 60 * 60 * 1000;

  function raKey() { return raCfg && raCfg.customer ? 'dp-ra-requested:' + raCfg.customer.id : null; }
  function raRemembered() {
    var k = raKey(); if (!k) return false;
    try { var t = parseInt(localStorage.getItem(k), 10); return !!t && (Date.now() - t) < RA_REMEMBER_MS; }
    catch (err) { return false; }
  }
  function raRemember() {
    var k = raKey(); if (!k) return;
    try { localStorage.setItem(k, String(Date.now())); } catch (err) { /* private mode: the tag still does it */ }
  }

  /* Every "request" control on the page → "awaiting". */
  function raMarkAwaiting() {
    if (!raCfg) return;
    var label = raCfg.labels.awaiting;
    document.querySelectorAll('[data-ra-btn][data-ra-state="request"]').forEach(function (b) {
      b.setAttribute('data-ra-state', 'awaiting');
      b.setAttribute('aria-disabled', 'true');
      b.textContent = label;
    });
    document.querySelectorAll('[data-ra-card][data-ra-state="request"]').forEach(function (a) {
      a.setAttribute('data-ra-state', 'awaiting');
      a.setAttribute('aria-disabled', 'true');
      a.removeAttribute('aria-busy');
      var l = a.querySelector('.card__btn-label');
      if (l) l.textContent = label;
      var t = a.getAttribute('aria-label') || '';
      var i = t.indexOf(': ');
      if (i > -1) a.setAttribute('aria-label', label + t.slice(i));
    });
  }
  if (raCfg && raRemembered()) raMarkAwaiting();

  /* The pop-up */
  /* Log in, then come back to this product. Customer accounts (the current kind) sign in
     through /customer_authentication/login, whose return_to brings the shopper back to the
     storefront page; the plain account login link (routes.account_login_url) ends on the
     account pages instead — checked on a live store. Classic accounts have no such route;
     there the login page's checkout_url does the same job. The route is probed once, on
     the first guest pop-up, and the link swapped if it is missing. */
  var raLoginRoute = 'new';
  var raLoginChecked = false;
  function raLoginHref() {
    var back = location.pathname + location.search;
    if (raLoginRoute === 'classic') return (raCfg.loginUrl || '/account/login') + '?checkout_url=' + encodeURIComponent(back);
    return '/customer_authentication/login?return_to=' + encodeURIComponent(back);
  }
  function raCheckLoginRoute() {
    if (raLoginChecked) return;
    raLoginChecked = true;
    fetch('/customer_authentication/login', { redirect: 'manual', credentials: 'same-origin' })
      .then(function (r) {
        if (r.type === 'opaqueredirect' || r.ok) return;
        if (r.status === 404) {
          raLoginRoute = 'classic';
          var a = raDlg && raDlg.querySelector('[data-ra-login]');
          if (a) a.setAttribute('href', raLoginHref());
        }
      })
      .catch(function () { /* keep the default */ });
  }

  var raDlg = document.querySelector('[data-ra-dialog]');
  var raReturnFocus = null;
  function raOpen(kind) {
    if (!raDlg || !raCfg) return;
    var titleKey = { guest: 'guest', success: 'success', already: 'success', restricted: 'restricted', error: 'error' }[kind] || 'success';
    raDlg.querySelector('[data-ra-title]').textContent = raCfg.titles[titleKey] || '';
    raDlg.querySelector('[data-ra-msg]').innerHTML = '<p>' + (raCfg.html[kind] || '') + '</p>';
    var actions = '';
    if (kind === 'guest') {
      actions = '<a class="btn" href="' + escapeHtml(raLoginHref()) + '" data-ra-login>' + escapeHtml(raCfg.labels.login) + '</a>';
      raCheckLoginRoute();
    }
    actions += '<button type="button" class="btn' + (kind === 'guest' ? ' btn--outline' : '') + '" data-ra-close>' + escapeHtml(raCfg.labels.close) + '</button>';
    raDlg.querySelector('[data-ra-actions]').innerHTML = actions;
    raReturnFocus = document.activeElement;
    raDlg.hidden = false;
    lockScroll('ra');
    var first = raDlg.querySelector('[data-ra-actions] a, [data-ra-actions] button');
    (first || raDlg.querySelector('.ra-dialog__panel')).focus();
  }
  function raClose() {
    if (!raDlg || raDlg.hidden) return;
    raDlg.hidden = true;
    unlockScroll('ra');
    if (raReturnFocus && raReturnFocus.focus) raReturnFocus.focus();
  }
  if (raDlg) {
    raDlg.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-ra-close]')) raClose();
    });
    raDlg.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.stopPropagation(); raClose(); return; }
      if (e.key !== 'Tab') return;
      /* Keep Tab inside the pop-up while it is open. */
      var f = Array.prototype.slice.call(raDlg.querySelectorAll('a[href], button:not([disabled])'));
      if (!f.length) return;
      var a = f[0], z = f[f.length - 1];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    });
  }

  /* Fill one field inside the form's shadow root the way a person would, so the form's
     own script sees the value (it listens for input/change, not for .value). */
  function raFill(el, value) {
    if (!el || value == null || value === '') return;
    var win = el.ownerDocument.defaultView || window;
    var setter = Object.getOwnPropertyDescriptor(win.HTMLInputElement.prototype, 'value').set;
    setter.call(el, value);
    ['input', 'change', 'blur'].forEach(function (t) { el.dispatchEvent(new Event(t, { bubbles: true, composed: true })); });
  }

  /* A path on this store only — never an outside address — so ra_return cannot be used
     to bounce a shopper to another site. */
  function raSafePath(p) {
    return (typeof p === 'string' && p.charAt(0) === '/' && p.charAt(1) !== '/' && p.charAt(1) !== '\\') ? p : '/';
  }
  function raWithParam(path, key, val) {
    return path + (path.indexOf('?') > -1 ? '&' : '?') + key + '=' + encodeURIComponent(val);
  }

  function raViaContact(c, product) {
    var body = new URLSearchParams();
    body.append('form_type', 'contact');
    body.append('utf8', '✓');
    body.append('contact[name]', ((c.first || '') + ' ' + (c.last || '')).trim() || c.email);
    body.append('contact[email]', c.email);
    body.append('contact[body]', 'Request access to purchase restricted products.\nProduct: ' + product);
    return fetch('/contact', { method: 'POST', body: body, credentials: 'same-origin' })
      .then(function (r) { return r.ok && /contact_posted=true/.test(r.url); })
      .catch(function () { return false; });
  }

  /* Step 1, on the product page or a product card: a logged-in customer clicks Request
     Access. With a request form page set, go there carrying the product and the way back
     (this page — a collection stays a collection); without one, post the contact form
     from here. Cards (v230) behave exactly like the product page button, as on Magento;
     their href to the product is only the no-JavaScript fallback. A card keeps its icon,
     so only its label text changes while sending. */
  function raSetLabel(el, text) {
    var l = el.querySelector('.card__btn-label');
    if (l) l.textContent = text; else el.textContent = text;
  }
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-ra-btn], [data-ra-card]') : null;
    if (!b || !raCfg) return;
    e.preventDefault();
    var st = b.getAttribute('data-ra-state');
    if (st === 'guest') { raOpen('guest'); return; }
    if (st === 'awaiting') { raOpen('already'); return; }
    if (st === 'restricted') { raOpen('restricted'); return; }
    if (st !== 'request' || b.getAttribute('aria-busy') === 'true' || !raCfg.customer) return;
    var title = b.getAttribute('data-ra-product') || document.title;
    var url = b.getAttribute('data-ra-product-url') || location.pathname;
    b.setAttribute('aria-busy', 'true');
    raSetLabel(b, raCfg.labels.sending);
    if (raCfg.formPage) {
      var to = raWithParam(raCfg.formPage, 'ra_product', title);
      to = raWithParam(to, 'ra_url', url);
      location.href = raWithParam(to, 'ra_return', location.pathname + location.search);
      return;
    }
    var label = raCfg.labels.request;
    raViaContact(raCfg.customer, title + ' (' + location.origin + url + ')').then(function (ok) {
      b.removeAttribute('aria-busy');
      raSetLabel(b, label);
      if (ok) { raRemember(); raMarkAwaiting(); raOpen('success'); }
      else { raOpen('error'); }
    });
  });

  /* Step 2, on the request form page (?ra_product=…): fill the Shopify Forms form with
     the logged-in customer's email and the product, and submit it. A notice at
     the top says what is happening; the form stays visible underneath, so if Shopify
     asks the shopper to prove they are human, or a field the theme could not fill is
     required, they can finish it themselves — success is detected either way. When the
     form confirms, return to the product with ?ra_sent=1. If the page has no form at all
     (Forms app missing or the form deleted), the contact form is used instead. */
  (function () {
    if (!raCfg || !raCfg.customer) return;
    var params;
    try { params = new URLSearchParams(location.search); } catch (err) { return; }
    var title = (params.get('ra_product') || '').slice(0, 200);
    if (!title) return;
    var back = raSafePath(params.get('ra_return') || '/');
    var productUrl = raSafePath(params.get('ra_url') || back);
    var product = title + ' (' + location.origin + productUrl + ')';
    var c = raCfg.customer;

    var note = document.createElement('div');
    note.className = 'ra-sending';
    note.setAttribute('role', 'status');
    note.textContent = (raCfg.labels.sendingFor || raCfg.labels.sending) + ' ' + title + '\u2026';
    var main = document.getElementById('MainContent') || document.body;
    main.insertBefore(note, main.firstChild);

    /* Once per product per tab for two minutes: a reload or Back must not send it again. */
    var onceKey = 'dp-ra-sent:' + c.id + ':' + title;
    try {
      var prev = parseInt(sessionStorage.getItem(onceKey), 10);
      if (prev && Date.now() - prev < 120000) { location.replace(raWithParam(back, 'ra_sent', '1')); return; }
    } catch (err) { /* no sessionStorage: carry on */ }

    function sent() {
      try { sessionStorage.setItem(onceKey, String(Date.now())); } catch (err) {}
      raRemember();
      location.replace(raWithParam(back, 'ra_sent', '1'));
    }
    function failed() { location.replace(raWithParam(back, 'ra_sent', '0')); }

    /* WHEN TO SUBMIT. Not as soon as the fields exist. Shopify Forms draws the form
       first and attaches its captcha (hCaptcha) a moment later; a submit sent before the
       captcha is ready is refused (401) and the form is replaced by "Sorry, there's a
       glitch". Found on a live store (v228): that message has no form in it, which the
       old success check read as confirmation, so the shopper was told "Access requested"
       while nothing had been sent. So: wait for the captcha widget plus 0.8 s (or, on a
       form with no captcha, 5 s of the form standing still) and the page to finish
       loading.

       WHAT COUNTS AS SENT. The browser's own record of the form_submission request
       (Resource Timing) after our click, with no error status, AND the form gone. A
       refusal or the "glitch" message reloads the page and tries once more; a second
       failure posts the contact form instead. A form still showing (a captcha
       challenge, a field to fix) is left to the shopper, and finishing it still counts. */
    var started = Date.now(), formSeenAt = 0, captchaSeenAt = 0, sentAt = 0, sentPerf = 0, timer = null;
    var tryKey = 'dp-ra-try:' + c.id + ':' + title;
    var canSeeRequests = !!(window.performance && performance.getEntriesByType);
    function attempts() { try { return parseInt(sessionStorage.getItem(tryKey), 10) || 0; } catch (err) { return 9; } }
    function clearAttempts() { try { sessionStorage.removeItem(tryKey); } catch (err) {} }
    function retryOrFallback() {
      clearInterval(timer);
      var n = attempts();
      if (n < 1) {
        try { sessionStorage.setItem(tryKey, String(n + 1)); } catch (err) {}
        location.reload();
        return;
      }
      clearAttempts();
      raViaContact(c, product).then(function (ok) { if (ok) sent(); else failed(); });
    }
    function lastSubmission() {
      if (!canSeeRequests) return null;
      var list = performance.getEntriesByType('resource').filter(function (r) {
        return r.startTime >= sentPerf && /\/form_submission/.test(r.name);
      });
      return list.length ? list[list.length - 1] : null;
    }
    timer = setInterval(function () {
      var host = document.querySelector('shopify-forms-embed, form-embed');
      var sr = host && host.shadowRoot;
      if (!sentAt && !sr && Date.now() - started > 12000) {
        /* No form on the page: send it through the contact form. */
        clearInterval(timer);
        raViaContact(c, product).then(function (ok) { if (ok) sent(); else failed(); });
        return;
      }
      if (!sr) return;
      var email = sr.getElementById('email');
      var formShown = !!sr.querySelector('form');
      if (!sentAt) {
        var submit = sr.querySelector('button[type="submit"]');
        if (!email || !submit) return;
        var now = Date.now();
        if (!formSeenAt) formSeenAt = now;
        if (!captchaSeenAt && sr.querySelector('iframe[src*="hcaptcha"], [data-hcaptcha-widget-id]')) captchaSeenAt = now;
        var ready = captchaSeenAt ? now - captchaSeenAt >= 800 : now - formSeenAt >= 5000;
        if (!ready || document.readyState !== 'complete') return;
        /* Email only (and the product below): the email is all Shopify Forms needs to
           find the customer and tag them, and leaving the name fields alone means a
           request can never change the name on their account. */
        raFill(email, c.email);
        /* The product question: a custom field whose id mentions "product", else the
           first custom field. */
        var custom = Array.prototype.slice.call(sr.querySelectorAll('input[id^="custom"]'));
        var pf = custom.filter(function (i) { return /product/i.test(i.id); })[0] || custom[0];
        if (pf) raFill(pf, product);
        sentAt = now;
        sentPerf = canSeeRequests ? performance.now() : 0;
        submit.click();
        return;
      }
      var sub = lastSubmission();
      var status = sub && typeof sub.responseStatus === 'number' ? sub.responseStatus : 0;
      var glitch = !formShown && /glitch|went wrong/i.test(sr.textContent || '');
      if (glitch || status >= 400) { retryOrFallback(); return; }
      if ((sub || !canSeeRequests) && !formShown && !email) {
        clearInterval(timer);
        clearAttempts();
        sent();
      }
    }, 250);
  })();

  /* Step 3, back on the product page (?ra_sent=1 or 0): say how it went, and take the
     parameter off the address so a reload or a shared link does not repeat the pop-up. */
  (function () {
    if (!raCfg) return;
    var params;
    try { params = new URLSearchParams(location.search); } catch (err) { return; }
    var flag = params.get('ra_sent');
    if (flag !== '1' && flag !== '0') return;
    params.delete('ra_sent');
    var qs = params.toString();
    try { history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash); } catch (err) {}
    if (flag === '1') { raRemember(); raMarkAwaiting(); raOpen('success'); }
    else { raOpen('error'); }
  })();

  /* Cart: html.ra-cart-blocked (set by Liquid on load) hides the checkout controls and
     shows the cart message. After each cart change, ask Liquid again — only it can read
     the customer's tags and the products' metafields. A failed check leaves the current
     state alone. */
  function raCartCheck() {
    if (!raCfg) return;
    fetch('/cart?section_id=cart-access-data', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('cart access ' + r.status); return r.text(); })
      .then(function (html) {
        var m = /data-ra-cart-state="([01])"/.exec(html);
        if (m) document.documentElement.classList.toggle('ra-cart-blocked', m[1] === '1');
      })
      .catch(function () {});
  }
  /* Hidden is not enough for a keyboard or a stale page: refuse checkout while blocked.
     html.ltb-guest (Log in to buy, v243) blocks it the same way. */
  function checkoutBlocked() {
    var c = document.documentElement.classList;
    return c.contains('ra-cart-blocked') || c.contains('ltb-guest');
  }
  document.addEventListener('click', function (e) {
    if (!checkoutBlocked()) return;
    var c = e.target.closest ? e.target.closest('a[href^="/checkout"], [data-checkout-gate], button[name="checkout"]') : null;
    if (!c) return;
    e.preventDefault();
    e.stopPropagation();
  }, true);
  document.querySelectorAll('.cart-page form').forEach(function (f) {
    f.addEventListener('submit', function (e) {
      if (!checkoutBlocked()) return;
      var via = e.submitter && e.submitter.getAttribute('name');
      if (via === 'checkout') e.preventDefault();
    });
  });

  /* ---------- Log in to buy (v243) ----------
     Theme settings → Log in to buy. For a guest, layout/theme.liquid adds html.ltb-guest
     and every Add to Cart is a [data-ltb] link (product page, cards, the drawer's
     suggestions, the cart message). Clicked, it goes to the login page that brings the
     shopper back to this page — the same rule as Request access's Log in button:
     /customer_authentication/login?return_to= on customer accounts (the current kind);
     classic accounts have no such route (probed once per visit), so there the login
     page's checkout_url does the job. The href is set at click time, so a link added
     later (the drawer, filtered results) works too, and Cmd-click still opens a tab. */
  (function () {
    var root = document.documentElement;
    if (!root.classList.contains('ltb-guest')) return;
    var KEY = 'dp-ltb-route', route = null;
    try { route = sessionStorage.getItem(KEY); } catch (err) {}
    if (route !== 'new' && route !== 'classic') {
      route = 'new';
      fetch('/customer_authentication/login', { redirect: 'manual', credentials: 'same-origin' })
        .then(function (r) {
          if (r.status === 404) route = 'classic';
          else if (r.type !== 'opaqueredirect' && !r.ok) return;
          try { sessionStorage.setItem(KEY, route); } catch (err) {}
        })
        .catch(function () {});
    }
    function ltbHref() {
      var back = location.pathname + location.search;
      if (route === 'classic') return (root.getAttribute('data-ltb-login') || '/account/login') + '?checkout_url=' + encodeURIComponent(back);
      return '/customer_authentication/login?return_to=' + encodeURIComponent(back);
    }
    function point(e) {
      var a = e.target.closest ? e.target.closest('a[data-ltb]') : null;
      if (a) a.setAttribute('href', ltbHref());
    }
    document.addEventListener('click', point, true);
    document.addEventListener('auxclick', point, true);
    document.addEventListener('contextmenu', point, true);
  })();

  /* ---------- Quantity steppers ---------- */
  /* .pdp__qty is the product page's stepper. It shares the cart's .cart-qty
     classes for styling, but keeps its own behaviour hook: the cart steppers post
     to /cart/change.js on click, whereas this one only edits the number in the
     add-to-cart form. Matching on .pdp__qty rather than .cart-qty is what keeps
     those two behaviours from being wired to the same elements. */
  /* v267: the same motion as the cart's steppers (Theme settings → Cart drawer → Changing
     quantity): "Rolling number" rolls the digits up or down, "Floating +1" floats a +1 / −1
     from the stepper, "Picture hop" pops the number (there is no line picture here, and
     hopping the big product photo would be too much); every style gives the pressed button a
     little pop. Pressing − at the minimum shakes the stepper instead of doing nothing. No
     animation / reduced motion: the number just changes, as before. */
  document.querySelectorAll('.pdp__qty').forEach(function (q) {
    var input = q.querySelector('input');
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 1;
    q.querySelectorAll('button').forEach(function (b) {
      b.addEventListener('click', function () {
        var cur = parseInt(input.value, 10) || min;
        var up = b.dataset.dir === 'up';
        var v = cur + (up ? 1 : -1);
        if (v < min) v = min;
        var m = cartMotion();
        if (v === cur) { input.value = v; shake(q); }
        else {
          rollInput(input, v, up, true);
          if (m.qty === 'float') floatChip(q, b, up);
          else if (m.qty !== 'none') b.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.15)' }, { transform: 'scale(1)' }], { duration: 260, easing: 'ease-out' });
          if (m.qty === 'hop') input.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.3)' }, { transform: 'scale(1)' }], { duration: 320, easing: 'cubic-bezier(.3,1.6,.5,1)' });
        }
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  /* ---------- Hero slider ----------
     The transition itself is CSS (base.css, "Hero transitions"); this only moves the
     classes. Incoming slide gets .is-in, outgoing .is-out, the starting state is
     committed, then both get .is-go and the effect runs. After DUR ms the classes
     come off and the incoming slide is .is-active. DUR must match the CSS.

     Replaces a track that slid sideways and wrapped with a modulo, which made the last
     slide rewind across every other slide to reach the first — with two slides,
     autoplay ping-ponged left, right, left. The timer was a bare setInterval: it
     ignored clicks (so it could fire a moment after one) and never paused.

     Autoplay now counts only while the slider can be read: not while a MOUSE is over it
     (pointerType, so a tap on a phone does not freeze it), not while a keyboard user
     has focus inside it, not while the tab is in the background, and never during a
     transition. Any click restarts the count. The active dot fills as it runs. */
  document.querySelectorAll('[data-hero]').forEach(function (hero) {
    var slides = hero.querySelectorAll('[data-hero-slide]');
    var n = slides.length;
    if (n <= 1) return;
    var dots = hero.querySelectorAll('[data-hero-dot]');
    var edge = hero.querySelector('[data-hero-edge]');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var fx = hero.getAttribute('data-fx') || 'parallax';
    var DUR = { wipe: 950, fade: 900, slide: 850, parallax: 900 };
    var dur = reduce ? 300 : (DUR[fx] || 900);
    var hold = reduce ? 0 : (parseInt(hero.getAttribute('data-autoplay'), 10) || 0);
    if (!hold) hero.removeAttribute('data-autoplay');   /* dots then show full, see base.css */
    var i = 0, busy = false, elapsed = 0, hovering = false, focused = false, last = null;

    function setDots() {
      dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
        if (k === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
        var f = d.querySelector('[data-hero-fill]');
        if (f) f.style.width = '';
      });
    }
    function go(to, dir) {
      if (busy) return;
      var target = ((to % n) + n) % n;
      if (target === i) return;
      var a = slides[i], b = slides[target];
      busy = true; elapsed = 0;
      hero.setAttribute('data-dir', dir < 0 ? 'prev' : 'next');
      i = target; setDots();
      b.classList.add('is-in');
      a.classList.add('is-out');
      void b.offsetWidth;                     /* commit the starting state before moving */
      b.classList.add('is-go');
      a.classList.add('is-go');
      if (edge && !reduce) { edge.classList.remove('is-sweeping'); void edge.offsetWidth; edge.classList.add('is-sweeping'); }
      setTimeout(function () {
        a.classList.remove('is-active', 'is-out', 'is-go');
        b.classList.remove('is-in', 'is-go');
        b.classList.add('is-active');
        if (edge) edge.classList.remove('is-sweeping');
        busy = false;
      }, dur + 40);
    }

    var prev = hero.querySelector('[data-hero-prev]');
    var next = hero.querySelector('[data-hero-next]');
    if (prev) prev.addEventListener('click', function () { go(i - 1, -1); });
    if (next) next.addEventListener('click', function () { go(i + 1, 1); });
    dots.forEach(function (d, k) { d.addEventListener('click', function () { go(k, k > i ? 1 : -1); }); });
    setDots();

    if (!hold) return;
    hero.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hovering = true; });
    hero.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') hovering = false; });
    hero.addEventListener('focusin', function (e) {
      var t = e.target; try { focused = !!(t && t.matches && t.matches(':focus-visible')); } catch (err) { focused = false; }
    });
    hero.addEventListener('focusout', function (e) { if (!hero.contains(e.relatedTarget)) focused = false; });
    (function tick(t) {
      if (last !== null && !hovering && !focused && !busy && !document.hidden) {
        elapsed += Math.min(100, t - last);
        var f = dots[i] && dots[i].querySelector('[data-hero-fill]');
        if (f) f.style.width = Math.min(100, elapsed / hold * 100) + '%';
        if (elapsed >= hold) go(i + 1, 1);
      }
      last = t;
      requestAnimationFrame(tick);
    })(null);
  });

  /* ---------- Living icons (v268) ----------
     The header's icon buttons and the product page's Share / Pin it / Email icons redraw
     their lines on hover (assets/interactions.css, .ix-draw), like the footer's feature
     icons draw themselves. That needs every line measured as length 1: pathLength="1".
     Only outline icons (a stroke); a filled mark (the Facebook f) just moves. */
  (function () {
    var icons = document.querySelectorAll('.header-icons > a > svg, .header-icons > button > svg, .menu-toggle > svg, .pdp__share svg');
    Array.prototype.forEach.call(icons, function (svg) {
      var stroke = svg.getAttribute('stroke');
      if (!stroke || stroke === 'none') return;
      Array.prototype.forEach.call(svg.querySelectorAll('path, circle, rect, line, polyline, polygon, ellipse'), function (p) { p.setAttribute('pathLength', '1'); });
      svg.classList.add('ix-draw');
    });
    /* v274: the hover circle behind each header icon (interactions.css, as on Share / Pin it /
       Email) is centred on the icon, not on the button: the "back to website" link has the
       divider's padding on one side, and phones pad the buttons unevenly. Measured at rest
       (before any icon moves) and again when the window changes size. */
    var chipEls = document.querySelectorAll('.header-icons:not(.header-icons--labels) > a, .header-icons:not(.header-icons--labels) > button, .menu-toggle');
    var placeChips = function () {
      Array.prototype.forEach.call(chipEls, function (el) {
        var ic = el.querySelector(':scope > svg, :scope > .header-account__initials');
        if (!ic || !el.offsetWidth) return;
        var r = el.getBoundingClientRect(), i = ic.getBoundingClientRect();
        if (!i.width) return;
        el.style.setProperty('--ic-x', (i.left - r.left + i.width / 2).toFixed(1) + 'px');
        el.style.setProperty('--ic-y', (i.top - r.top + i.height / 2).toFixed(1) + 'px');
        el.style.setProperty('--ic-d', Math.round(Math.max(i.width, i.height) + 16) + 'px');
      });
    };
    if (chipEls.length) {
      placeChips();
      var chipT;
      window.addEventListener('resize', function () { clearTimeout(chipT); chipT = setTimeout(placeChips, 150); });
      window.addEventListener('load', placeChips);
    }
    /* v269: without a mouse (phones, tablets) there is no hover, so the motion plays by itself:
       the header icons once the page has loaded, Share / Pin it / Email when they come into
       view, one after another; and any of them again when tapped. Not with reduced motion. */
    if (!window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.matchMedia('(hover: none)').matches) return;
    function play(els, gap) {
      Array.prototype.forEach.call(els, function (el, i) {
        el.style.setProperty('--ix-d', (i * gap).toFixed(2) + 's');
        el.classList.remove('ix-play');
        void el.offsetWidth;
        el.classList.add('ix-play');
        clearTimeout(el._ixT);
        el._ixT = setTimeout(function () { el.classList.remove('ix-play'); el.style.removeProperty('--ix-d'); }, i * gap * 1000 + 1500);
      });
    }
    var heads = document.querySelectorAll('.header-icons > a, .header-icons > button, .menu-toggle');
    var shares = document.querySelectorAll('.pdp__share a');
    var go = function () { play(Array.prototype.filter.call(heads, function (el) { return el.offsetWidth; }), 0.15); };
    if (document.readyState === 'complete') setTimeout(go, 300); else window.addEventListener('load', function () { setTimeout(go, 300); });
    if (shares.length && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        io.disconnect();
        play(shares, 0.18);
      }, { threshold: 0.6 });
      io.observe(shares[0].parentNode);
    }
    Array.prototype.forEach.call(heads, function (el) { el.addEventListener('pointerdown', function () { play([el], 0); }, { passive: true }); });
    Array.prototype.forEach.call(shares, function (el) { el.addEventListener('pointerdown', function () { play([el], 0); }, { passive: true }); });
  })();

  /* ---------- Introduction styles (v267) ----------
     snippets/intro-styled (Introduction → Style). The section arrives armed (its start state:
     words lowered and blurred, text faded; v268: armed by the snippet's own script before the
     first paint, so one already on screen when the page opens plays too, with no flash) and
     plays once a quarter of it is in view. is-armed comes off once
     the entrance has played, so the entrance delays never hold back hover motion.
     Highlight: the text is split into words that light up as they pass 72% of the screen
     height on the way up (scroll-linked, so scrolling back down dims them again).
     Reduced motion or no IntersectionObserver: everything shows, all words lit. */
  (function () {
    document.documentElement.classList.add('ix-js');
    var intros = document.querySelectorAll('[data-intro]');
    if (!intros.length) return;
    var still = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) || !('IntersectionObserver' in window);
    var io = still ? null : new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var sec = en.target;
        io.unobserve(sec);
        sec.classList.add('is-in');
        setTimeout(function () { sec.classList.remove('is-armed'); }, 2600);
      });
    }, { threshold: 0.25 });
    var readers = [];
    function splitWords(root) {
      var words = [];
      var walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null), nodes = [], n;
      while ((n = walk.nextNode())) if (n.nodeValue.trim()) nodes.push(n);
      nodes.forEach(function (t) {
        var frag = document.createDocumentFragment();
        t.nodeValue.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          var sp = document.createElement('span');
          sp.className = 'ix-hw';
          sp.textContent = part;
          frag.appendChild(sp);
          words.push(sp);
        });
        t.parentNode.replaceChild(frag, t);
      });
      return words;
    }
    /* v268: a soft light follows the mouse (--gx / --gy, as on Testimonials); Spotlight
       measures it on its card, and Ink also tells the heading where the mouse is (--hx / --hy)
       for its colour. is-pointing fades the light in and out. */
    var mouse = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    function follow(sec) {
      if (still || !mouse) return;
      var box = sec.querySelector('.intro__card') || sec, head = sec.querySelector('.intro__heading');
      var ink = sec.getAttribute('data-intro') === 'ink', frame = 0, last = null;
      sec.addEventListener('pointermove', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        last = e;
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = 0;
          if (!last) return;
          var r = box.getBoundingClientRect();
          if (r.width && r.height) {
            box.style.setProperty('--gx', ((last.clientX - r.left) / r.width * 100).toFixed(1) + '%');
            box.style.setProperty('--gy', ((last.clientY - r.top) / r.height * 100).toFixed(1) + '%');
          }
          if (ink && head) {
            var h = head.getBoundingClientRect();
            head.style.setProperty('--hx', (last.clientX - h.left).toFixed(0) + 'px');
            head.style.setProperty('--hy', (last.clientY - h.top).toFixed(0) + 'px');
          }
          sec.classList.add('is-pointing');
        });
      }, { passive: true });
      sec.addEventListener('pointerleave', function () {
        last = null;
        sec.classList.remove('is-pointing');
        if (head) { head.style.removeProperty('--hx'); head.style.removeProperty('--hy'); }
      }, { passive: true });
    }
    intros.forEach(function (sec) {
      follow(sec);
      if (sec.getAttribute('data-intro') === 'highlight' && !still) {
        var body = sec.querySelector('.intro__body');
        if (body) { readers.push({ sec: sec, words: splitWords(body) }); sec.classList.add('is-reading'); }
      }
      if (still) { sec.classList.remove('is-armed'); return; }
      /* Armed already by the snippet's own script (v268); added here too in case it did not run. */
      sec.classList.add('is-armed');
      io.observe(sec);
    });
    if (!readers.length) return;
    var ticking = false;
    function read() {
      ticking = false;
      var line = window.innerHeight * 0.72;
      /* At the very bottom of the page a word may never reach the line: light them all. */
      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2) line = Infinity;
      readers.forEach(function (rd) {
        rd.words.forEach(function (w) { w.classList.toggle('is-lit', w.getBoundingClientRect().top < line); });
      });
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(read); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    read();
  })();

  /* ---------- Product strip arrows (scroll) ---------- */
  document.querySelectorAll('[data-strip]').forEach(function (strip) {
    var sc = strip.querySelector('[data-strip-scroller]');
    if (!sc) return;
    var prev = strip.querySelector('[data-strip-prev]');
    var next = strip.querySelector('[data-strip-next]');
    function step() { return sc.clientWidth / (strip.dataset.perView || 4) * 2; }
    if (prev) prev.addEventListener('click', function () { sc.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { sc.scrollBy({ left: step(), behavior: 'smooth' }); });
  });

  /* ---------- PDP gallery thumbnails ---------- */
  var mainImg = document.querySelector('[data-gallery-main]');
  if (mainImg) {
    document.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
      t.addEventListener('click', function () {
        mainImg.src = t.dataset.full || t.querySelector('img').src;
        document.querySelectorAll('[data-gallery-thumb]').forEach(function (x) { x.classList.remove('is-active'); });
        t.classList.add('is-active');
      });
    });
  }

  /* ---------- Product card tilt (v237) ----------
     The card image leans toward the mouse and a soft light follows it, as on the DermPRO
     headless storefront (lib/motion.ts useTilt, copied value for value): rotateY up to
     +/-6deg across the width, rotateX up to +/-4.5deg down the height, --px/--py from -1
     to 1 for the photo's drift, --gx/--gy for the light. The look is in base.css
     ("B2. Tilt and light").
     One listener on the document, not one per card, so cards drawn later (product rows,
     cart recommendations, filtering) tilt without being wired. The pointer is followed
     anywhere over the card's image block, Add to cart bar included, so the image does not
     snap back when the pointer crosses onto the bar. At most one write per frame. Mouse
     only, and nothing at all with reduced motion. */
  (function () {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var cur = null, frame = 0, last = null;
    function mediaOf(el) {
      var zone = el && el.closest ? el.closest('.card__top, .card__media') : null;
      if (!zone) return null;
      return zone.classList.contains('card__media') ? zone : zone.querySelector('.card__media');
    }
    function reset(m) {
      m.classList.remove('is-tilting');
      ['--rx', '--ry', '--px', '--py'].forEach(function (k) { m.style.removeProperty(k); });
    }
    function apply() {
      frame = 0;
      if (!cur || !last) return;
      var r = cur.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var x = Math.min(Math.max((last.clientX - r.left) / r.width, 0), 1);
      var y = Math.min(Math.max((last.clientY - r.top) / r.height, 0), 1);
      cur.classList.add('is-tilting');
      cur.style.setProperty('--ry', ((x - 0.5) * 12).toFixed(2) + 'deg');
      cur.style.setProperty('--rx', ((0.5 - y) * 9).toFixed(2) + 'deg');
      cur.style.setProperty('--px', ((x - 0.5) * 2).toFixed(3));
      cur.style.setProperty('--py', ((y - 0.5) * 2).toFixed(3));
      cur.style.setProperty('--gx', (x * 100).toFixed(1) + '%');
      cur.style.setProperty('--gy', (y * 100).toFixed(1) + '%');
    }
    document.addEventListener('pointermove', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      var m = mediaOf(e.target);
      if (m !== cur) { if (cur) reset(cur); cur = m; }
      if (!m) return;
      last = e;
      if (!frame) frame = requestAnimationFrame(apply);
    }, { passive: true });
    document.addEventListener('pointerout', function (e) {
      if (!cur) return;
      var to = mediaOf(e.relatedTarget);
      if (to !== cur) { reset(cur); cur = null; }
    }, { passive: true });
  })();

  /* ---------- Product cards: fade in on page open and on scroll ----------
     The layout's <head> has already put `cards-pending` on <html>, so every card has
     been invisible since the first paint (base.css, "C. Fade in"). This replaces that
     with a per-card state: --waiting until the card comes into view, then --enter,
     which plays the entrance. The observer's first report covers the cards already on
     screen, which is what makes them fade in as the page opens; the rest follow as
     they are scrolled to — including cards to the right in a horizontal strip, which
     wait until the strip is scrolled sideways.

     Cards arriving together ripple left to right WITHIN their row: the delay comes
     from the card's position in its row, not in the batch, so a fast scroll does not
     land several rows as one block. Capped at the fifth card. Once a card has arrived
     its classes come off, leaving it exactly as it would be without this code.

     Skipped entirely — every card simply shown — when motion is reduced, when the
     browser has no IntersectionObserver, and when this runs so late that the CSS
     failsafe (2.5s) has already revealed the cards, so they are not hidden again. */
  (function () {
    var root = document.documentElement;
    function release() { root.classList.remove('cards-pending'); }
    var cards = document.querySelectorAll('.card');
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var late = window.performance && performance.now() > 2300;
    if (!cards.length || reduce || late || !('IntersectionObserver' in window)) { release(); return; }

    var waiting = [];
    Array.prototype.forEach.call(cards, function (c) {
      var r = c.getBoundingClientRect();
      /* No size = inside something hidden; above the viewport = already scrolled past
         (a restored scroll position). Neither should be parked at opacity 0. */
      if (r.width === 0 || r.height === 0 || r.bottom <= 0) return;
      c.classList.add('card--waiting');
      waiting.push(c);
    });
    release();
    if (!waiting.length) return;

    var io = new IntersectionObserver(function (entries) {
      var arriving = entries.filter(function (e) { return e.isIntersecting; }).map(function (e) {
        return { el: e.target, top: Math.round(e.boundingClientRect.top), left: e.boundingClientRect.left };
      });
      arriving.sort(function (a, b) { return a.top - b.top || a.left - b.left; });
      var rowTop = null, col = 0;
      arriving.forEach(function (a) {
        io.unobserve(a.el);
        /* Same row = tops within 10px; titles of different lengths do not move a card's top. */
        if (rowTop === null || Math.abs(a.top - rowTop) > 10) { rowTop = a.top; col = 0; } else { col++; }
        var delay = Math.min(col, 4) * 80;
        a.el.style.animationDelay = delay + 'ms';
        a.el.classList.add('card--enter');
        a.el.classList.remove('card--waiting');
        setTimeout(function () {
          a.el.classList.remove('card--enter');
          a.el.style.animationDelay = '';
        }, delay + 700);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    waiting.forEach(function (c) { io.observe(c); });
  })();

  /* Collection filters now use a native <details> dropdown (see main-collection.liquid) */

  /* ---------- Sticky header: scrolls away in flow, then re-pops fixed & stays ---------- */
  (function () {
    var hdr = document.querySelector('.site-header');
    if (!hdr) return;
    var root = document.documentElement;

    /* Floating layout (v264): one bar that changes shape. Its section is sticky (CSS), so
       nothing here moves it; this only says WHEN it is pinned: .is-stuck a few pixels after
       the header reaches the top of the window (12px on the way down, back at 2px on the
       way up, so a slow scroll at the edge does not flicker). The CSS eases the bar between
       the two shapes. No fixed position, no slide-down, no body padding.
       Other pages keep the header at its rest height (--float-box, measured while unpinned)
       so the page below does not move up as the pinned bar gets shorter. A hidden marker
       before the header's section gives the header's place in the page (the promotion bar,
       when on, sits above it). Frozen while a drawer or search has the page locked, as below. */
    if (hdr.classList.contains('site-header--floating')) {
      var sec = hdr.parentElement;
      var mark = document.createElement('div');
      mark.setAttribute('data-float-mark', '');
      mark.setAttribute('aria-hidden', 'true');
      mark.style.cssText = 'height:0;margin:0;padding:0';
      sec.parentNode.insertBefore(mark, sec);
      var fStuck = false, fTick = false, restH = 0;
      var inFlow = !hdr.classList.contains('site-header--float-home');
      var bar = hdr.querySelector('.header-main') || hdr;
      var measure = function () {
        if (fStuck) return;
        if (inFlow) hdr.style.removeProperty('--float-box');
        restH = hdr.offsetHeight;
        root.style.setProperty('--header-h', restH + 'px');
        /* v273: --header-rest is the header's height at rest and does not change while the
           page scrolls. --header-h follows the pinned bar (shorter, higher), which moved the
           phone hero's words (placed just under the header) the moment the bar pinned: the
           "glitch" Raymond saw scrolling down on a phone. The hero uses --header-rest. */
        root.style.setProperty('--header-rest', restH + 'px');
        if (inFlow) hdr.style.setProperty('--float-box', restH + 'px');
      };
      /* --header-h / --header-bottom are what sticky things under the header (the product
         gallery, the mega panel's height) clear. Pinned, the bar is shorter and higher than
         at rest, so once its shape change has finished they follow the bar's real bottom;
         unpinned, they go back to the rest height. */
      var settle = function () {
        var b = Math.max(0, Math.round(bar.getBoundingClientRect().bottom));
        root.style.setProperty('--header-bottom', b + 'px');
        root.style.setProperty('--header-h', (fStuck ? b : restH) + 'px');
      };
      bar.addEventListener('transitionend', function (e) { if (e.target === bar && e.propertyName === 'transform') settle(); });
      var fUpdate = function () {
        fTick = false;
        if (root.classList.contains('scroll-locked')) return;
        var y = window.scrollY < 0 ? 0 : window.scrollY;
        var top = mark.getBoundingClientRect().top + y;
        var want = fStuck ? y > top + 2 : y > top + 12;
        if (want !== fStuck) {
          fStuck = want;
          hdr.classList.toggle('is-stuck', want);
          root.classList.toggle('header-stuck', want);
          /* No transition to wait for (reduced motion, or a jump): settle on the next frame. */
          window.requestAnimationFrame(function () { window.requestAnimationFrame(settle); });
        }
        root.style.setProperty('--header-bottom', Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) + 'px');
      };
      measure();
      fUpdate();
      window.addEventListener('scroll', function () {
        if (!fTick) { fTick = true; window.requestAnimationFrame(fUpdate); }
      }, { passive: true });
      var fResize;
      window.addEventListener('resize', function () {
        clearTimeout(fResize);
        fResize = setTimeout(function () { measure(); fUpdate(); }, 150);
      });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
      window.addEventListener('load', measure);
      return;
    }

    var ticking = false, stuck = false, h = 0, top = 0;
    function update() {
      /* Frozen while a drawer, the mobile nav or the search overlay is open.
         lockScroll pins the body with position:fixed at a negative top offset, which
         collapses the document scroll to 0 and fires a scroll event. Without this
         guard update() reads y≈0, concludes the page is back at the top, un-pins the
         header, and the header drops into normal flow at the top of a body that is
         now offset off-screen — so the menu vanishes for as long as the drawer is
         open and only returns on close, when the restored scroll fires another event
         and re-pins it.

         Skipping leaves the header in whatever state it already had, which is safe:
         position:fixed is viewport-relative and unaffected by the body also being
         fixed, because position:fixed on an ancestor does not create a containing
         block for fixed descendants.

         ticking must be cleared before returning, or it stays true forever and no
         further rAF is ever scheduled — the header would freeze permanently after
         the first drawer open. */
      if (root.classList.contains('scroll-locked')) { ticking = false; return; }
      var y = window.scrollY < 0 ? 0 : window.scrollY;
      if (!stuck) {
        h = hdr.offsetHeight || 120; // measure while in normal flow
        /* How far down the document the header actually starts. This used to be
           assumed to be zero, which was true only because nothing was ever rendered
           above the header. The promotion bar is, and with it in place the old
           threshold (y > h) pinned the header while it was still partly on screen —
           a visible jump of exactly the bar's height.

           Measured here rather than stored once, because the bar can be switched off
           in the theme editor without a reload, and because a stuck header reports a
           different offset — which is why this only runs while it is unstuck. */
        top = hdr.getBoundingClientRect().top + y;
        /* Published so CSS can budget the space a dropdown has between the header and
           the bottom of the window. The mega menu used to subtract a hardcoded 215px
           for "header plus panel chrome"; the real figure here is 93 + 166 = 259, so
           the panel over-allocated by 44px and its last row fell off the screen. The
           header's height varies by layout and by logo, so it has to be measured
           rather than assumed. */
        root.style.setProperty('--header-h', h + 'px');
        root.style.setProperty('--header-rest', h + 'px');   /* v273: see the floating branch */
      }
      if (!stuck && y > top + h) {
        /* scrolled past the header — pin it (slides back down) and reserve its space.
           `top + h` is the document position of the header's bottom edge. With nothing
           above the header top is 0 and this is identical to the old `y > h`, so a
           store with the promotion bar switched off behaves exactly as before. */
        stuck = true;
        /* A see-through header (centred layout, homepage) never took up space in the
           page, so pinning it must not reserve any: that would push the page down by
           the header's height the moment it pins. */
        if (!hdr.classList.contains('site-header--overlay')) document.body.style.paddingTop = h + 'px';
        hdr.classList.add('is-stuck');
        root.classList.add('header-stuck');
      } else if (stuck && y <= top + 30) {
        // back near the top — return the header to normal flow
        stuck = false;
        document.body.style.paddingTop = '';
        hdr.classList.remove('is-stuck');
        root.classList.remove('header-stuck');
      }

      /* Where the header's bottom edge actually is in the viewport — which is what a
         dropdown opening beneath it has to clear. --header-h is only its HEIGHT; the
         two were the same number while nothing was rendered above the header, and
         differ by the promotion bar's height when it is switched on.

         Read AFTER the pin decision above, deliberately. Measured before it, the
         header is still in normal flow during the frame it gets pinned, so the edge
         reads far up the page and clamps to 0 — which published a budget 100px too
         generous for as long as the header stayed stuck. Exactly the over-allocation
         this calculation was rewritten to fix once before, reintroduced by reading one
         statement too early.

         With no promotion bar this equals h at rest, so a store without one gets the
         identical number it got before. */
      root.style.setProperty('--header-bottom', Math.max(0, hdr.getBoundingClientRect().bottom) + 'px');

      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  })();

  /* ---------- Sort + filter auto-submit ---------- */
  document.querySelectorAll('[data-auto-submit]').forEach(function (sel) {
    sel.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set(sel.name, sel.value);
      window.location.href = url.toString();
    });
  });
})();
